import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import React, { useEffect, useState } from "react"
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap"
import config from "config/config"

const cbseLogo = "/images/assets/cbse_logo.png"
const schoolLogo = "/images/assets/sps_logo.png"

const getPhotoUrl = (pic) => {
  if (!pic) return null
  if (pic.startsWith("http")) return pic
  return pic.startsWith("/") ? `${config.API_URL}${pic}` : `${config.API_URL}/media/${pic}`
}

const loadImage = (url) => {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = url
  })
}


/*
  Gradecardpdf.js  (HTML-design-matched version)
  ───────────────────────────────────────────────
  Exports TWO things:
    1. GradeCardPreviewModal  — React modal whose HTML layout mirrors the PDF exactly
    2. generateGradeCardPdf   — jsPDF generator (same layout, print-ready)

  Both consume the same report shape:
  {
    student_info  : { st_name, st_code, st_class, st_sec, dob, address,
                      father_name, mother_name }
    session_name  : string
    exams         : [{ exam_id, exam_name, assessments: [{ assessment_id, assessment_name,
                       max_mark, subjects: [{ subject_code, subject_name, normalized_score,
                       positional_grade, sub_assessments: [{ sub_assessment_id,
                       sub_assessment_name, max_mark, mark_value }] }] }] }]
    grand_total   : [{ subject_code, subject_name, grand_total, final_grade }]
    co_scholastic : [{ subject_code, subject_name, grade }]
    subject_avg   : [{ subject_name, term1_avg, term2_avg }]   // class averages
    attendance    : { total_days, days_present, percentage }
    promotion_status: string
    remark        : string
  }
*/

// ─────────────────────────────────────────────────────────────────────────────
//  Shared constants
// ─────────────────────────────────────────────────────────────────────────────
const CSS = {
  maroon    : "#7d0a0a",
  darkBrown : "#4a2e18",
  gold      : "#d4b06a",
  tan       : "#b8956a",
  navyBlue  : "#020255",
  tableHead : "#555555",
  sectionBg : "#efefe9",
  rowAlt    : "#f5f5f5",
  white     : "#ffffff",
  black     : "#000000",
  lightGrey : "#cccccc",
  midGrey   : "#888888",
  darkGrey  : "#505050",
  infoBg    : "#f0f0f0",
  goldCell  : "#e8d060",
  notesBg   : "#f9f9f9",
  pleaseBg  : "#eef4ee",
  pleaseBdr : "#aac8aa",
  orange    : "#e07020",
  yellowAtt : "#f5e642",
  // grade strip colours
  gsA1      : "#c0392b",
  gsA2      : "#e67e22",
  gsB1      : "#f39c12",
  gsB2      : "#27ae60",
  gsC1      : "#16a085",
  gsC2      : "#2980b9",
  gsD1      : "#1a5276",
  gsD2      : "#4a235a",
  gsE       : "#1a1a1a",
}

const GRADE_STRIP = [
  { label: "A1", bg: CSS.gsA1,  sub: "Top 1/8th",  color: "#fff" },
  { label: "A2", bg: CSS.gsA2,  sub: "Next 1/8th", color: "#fff" },
  { label: "B1", bg: CSS.gsB1,  sub: "Next 1/8th", color: "#000" },
  { label: "B2", bg: CSS.gsB2,  sub: "Next 1/8th", color: "#fff" },
  { label: "C1", bg: CSS.gsC1,  sub: "Next 1/8th", color: "#fff" },
  { label: "C2", bg: CSS.gsC2,  sub: "Next 1/8th", color: "#fff" },
  { label: "D1", bg: CSS.gsD1,  sub: "Next 1/8th", color: "#fff" },
  { label: "D2", bg: CSS.gsD2,  sub: "Next 1/8th", color: "#fff" },
  { label: "E",  bg: CSS.gsE,   sub: "Failed",     color: "#fff" },
]

// ─────────────────────────────────────────────────────────────────────────────
//  Shared helpers
// ─────────────────────────────────────────────────────────────────────────────
const fmt  = v  => (v === null || v === undefined || v === "" ? "—" : String(v))
const fmtAvg = v => (v === null || v === undefined || v === "" ? "" : String(v))
const fmtD = ds => {
  if (!ds) return "—"
  const [y, m, d] = ds.split("-")
  return `${d}/${m}/${y}`
}

const today     = new Date()
const printDate = `${today.getDate()}/${today.getMonth() + 1}/${today.getFullYear()}`

const SCHOOL = {
  name       : "South Point School, Guwahati",
  established: "Established Year 1960",
  affiliation: "Affiliated to CBSE New Delhi, Affiliation No : 230044",
  address    : "21, Barsapara Road, Guwahati - 781018",
  phone      : "+91 97060 12121",
  email      : "helpdesk@spsghy.co.in",
  website    : "www.spsghy.co.in",
}

/** Returns the real sub-assessments (non-null id) for an assessment */
const getRealSubs = assessment => {
  const sample = assessment.subjects?.[0]
  return (sample?.sub_assessments || []).filter(sa => sa.sub_assessment_id !== null)
}

// ═════════════════════════════════════════════════════════════════════════════
//  ①  REACT PREVIEW MODAL
// ═════════════════════════════════════════════════════════════════════════════

/* ── Inline style objects mirroring the HTML design ── */
const S = {
  page: {
    fontFamily : "Arial, sans-serif",
    fontSize   : 11,
    color      : CSS.black,
    background : CSS.white,
    boxSizing  : "border-box",
  },
  outerBorder: {
    border   : `4px solid #3a7d44`,
    padding  : "8px",
    minHeight: 800,
    background: CSS.white,
  },
  topBar: {
    display        : "flex",
    justifyContent : "space-between",
    fontSize       : 11,
    marginBottom   : 4,
  },
  schoolHeader: {
    background : CSS.maroon,
    color      : CSS.white,
    textAlign  : "center",
    padding    : "4px 2px",
  },
  schoolEst: { fontSize: 10, letterSpacing: 0.5 },
  schoolName: {
    fontSize   : 26,
    fontWeight : "bold",
    fontFamily : "Georgia, serif",
    margin     : "2px 0 0 0",
  },
  infoStrip: {
    background : CSS.white,
    display    : "flex",
    alignItems : "center",
    padding    : "6px 10px",
    borderBottom: "1px solid #ddd",
    marginBottom: 6,
  },
  logoBox: {
    width          : 65,
    height         : 65,
    display        : "flex",
    alignItems     : "center",
    justifyContent : "center",
    flexShrink     : 0,
  },
  logoImg: {
    maxHeight: "100%",
    maxWidth: "100%",
    objectFit: "contain",
  },
  centerInfo: {
    flex      : 1,
    textAlign : "center",
    fontSize  : 11,
    padding   : "0 15px",
    lineHeight: "1.4",
  },
  affiliation: { fontWeight: "bold", fontSize: 11, marginBottom: 2 },
  redHeader: { color: "#7d0a0a", fontWeight: "600" },
  studentSection: {
    background : CSS.sectionBg,
    padding    : "6px 8px",
    marginBottom: 8,
  },
  studentLabel: {
    background   : CSS.maroon,
    color        : CSS.white,
    display      : "inline-block",
    padding      : "2px 8px",
    fontWeight   : "bold",
    fontSize     : 11,
    marginBottom : 6,
  },
  studentGrid: {
    display    : "flex",
    gap        : 15,
    alignItems : "stretch",
  },
  studentLeft : { flex: 1.2, fontSize: 11 },
  studentRight: { flex: 1, fontSize: 11 },
  studentFieldRow: { 
    marginBottom: 4,
    display: "flex",
  },
  studentFieldLabel: {
    color: "#505050",
    minWidth: 100,
  },
  studentFieldValue: {
    fontWeight: "bold",
    color: "#000",
    flex: 1,
    wordBreak: "break-word",
  },
  studentPhoto: {
    width          : 90,
    height         : 110,
    border         : `4px solid white`,
    boxShadow      : "0 2px 6px rgba(0,0,0,0.15)",
    background     : "#e5e7eb",
    flexShrink     : 0,
    display        : "flex",
    alignItems     : "center",
    justifyContent : "center",
    fontSize       : 10,
    color          : "#9ca3af",
    overflow       : "hidden",
  },
  titleBand: { 
    display: "flex", 
    marginTop: 6,
    marginBottom: 8,
  },
  titleLeft  : { width: 50, background: "#dfc794" },
  titleCenter: {
    flex          : 1,
    background    : CSS.darkBrown,
    color         : CSS.white,
    textAlign     : "center",
    fontSize      : 18,
    fontWeight    : "bold",
    letterSpacing : 1.5,
    padding       : "6px 0",
    fontFamily    : "Arial, sans-serif",
  },
  titleRight : { width: 50, background: "#dfc794" },
  scholHeader: {
    background   : "#f7f7f7",
    padding      : "4px 6px",
    border       : `1px solid #ddd`,
    borderBottom : `none`,
  },
  scholPart : { fontWeight: "bold", fontSize: 11, color: "#000" },
  scholInst : { fontSize: 10, marginTop: 1, color: "#444", lineHeight: 1.3 },
  gradeStripRow: {
    display             : "grid",
    gridTemplateColumns : "repeat(9, 1fr)",
    fontSize            : 10,
    fontWeight          : "bold",
    borderLeft          : "1px solid #ccc",
    borderRight         : "1px solid #ccc",
  },
  gradeSubRow: {
    display             : "grid",
    gridTemplateColumns : "repeat(9, 1fr)",
    fontSize            : 8.5,
    background          : "#e0e0e0",
    border              : "1px solid #ccc",
    borderTop           : "none",
    marginBottom        : 8,
  },
  marksTable: {
    width          : "100%",
    borderCollapse : "collapse",
    fontSize       : 10,
    marginTop      : 4,
    marginBottom   : 4,
  },
  th: (extra = {}) => ({
    border     : `1px solid #555`,
    padding    : "3px 4px",
    textAlign  : "center",
    background : "#d8d8d0",
    fontWeight : "bold",
    verticalAlign: "middle",
    ...extra,
  }),
  td: (extra = {}) => ({
    border       : `1px solid #555`,
    padding      : "3px 4px",
    textAlign    : "center",
    verticalAlign: "middle",
    background   : "#ffffff",
    ...extra,
  }),
  goldCell: {
    background : "#fef08a",
    fontWeight : "bold",
  },
  thTerm: { background: "#e5e7eb", fontWeight: "bold", fontSize: 10.5 },
  thAvg : { background: "#d1d5db", fontWeight: "bold", fontSize: 10.5 },
  totalRow: {
    background : "#020255",
    color      : CSS.white,
    fontWeight : "bold",
  },
  addlRow  : { background: "#e0e0e0", fontSize: 9.5, fontWeight: "bold" },
  addlRow2 : { background: "#f0f0f0", fontSize: 9.5 },
  notesRow: {
    fontSize   : 9.5,
    padding    : "4px 6px",
    background : CSS.notesBg,
    border     : `1px solid #ccc`,
    marginBottom: 8,
    color      : "#444",
  },
  pleaseNoteContainer: {
    border: "1px solid #7d0a0a",
    marginTop: 8,
    marginBottom: 8,
    fontSize: 9.5,
  },
  pleaseNoteHeader: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    background: "#7d0a0a",
    color: "#fff",
    fontWeight: "bold",
    padding: "3px 8px",
  },
  pleaseNoteBody: {
    background: "#efefe9",
    padding: "6px 8px",
  },
  pleaseNoteRow: {
    display: "grid",
    gridTemplateColumns: "100px 1fr 1fr",
    gap: "10px",
    marginBottom: "6px",
    borderBottom: "1px solid #dcdcd5",
    paddingBottom: "4px",
  },
  pleaseNoteSubject: {
    fontWeight: "bold",
  },
  lowerGrid: {
    display             : "grid",
    gridTemplateColumns : "1.1fr 1.2fr 180px",
    gap                 : 6,
    marginTop           : 8,
    marginBottom        : 8,
  },
  coSchol: { border: `1px solid #555` },
  coScholTitle: {
    background : "#020255",
    color      : CSS.white,
    padding    : "3px 6px",
    fontSize   : 10.5,
    fontWeight : "bold",
  },
  coTable: { width: "100%", borderCollapse: "collapse", fontSize: 9.5 },
  coTh: { background: "#555", color: CSS.white, padding: "3px 4px", textAlign: "left", fontWeight: "bold" },
  coTd: { border: `1px solid #888`, padding: "3px 4px" },
  subjAvg: { border: `1px solid #555` },
  subjAvgTitle: {
    background : "#020255",
    color      : CSS.white,
    padding    : "3px 6px",
    fontSize   : 10.5,
    fontWeight : "bold",
  },
  avgTable: { width: "100%", borderCollapse: "collapse", fontSize: 9.5 },
  avgTh: { background: "#ddd", padding: "3px 4px", border: `1px solid #aaa`, fontWeight: "bold" },
  avgTd: { border: `1px solid #aaa`, padding: "3px 4px" },
  promoAttend: { border: `1px solid #555`, display: "flex", flexDirection: "column" },
  promoTitle: {
    background : CSS.maroon,
    color      : CSS.white,
    textAlign  : "center",
    padding    : "3px 4px",
    fontSize   : 10.5,
    fontWeight : "bold",
  },
  attendTitle: {
    background : CSS.maroon,
    color      : CSS.white,
    textAlign  : "center",
    padding    : "3px 4px",
    fontSize   : 10.5,
    fontWeight : "bold",
    marginTop  : "auto",
  },
  attendBody: {
    background : "#f5e642",
    padding    : "6px 8px",
    fontSize   : 10,
  },
  attendGrid: {
    display             : "flex",
    justifyContent      : "space-between",
    alignItems          : "center",
  },
  attendPct: {
    background : "#e07020",
    color      : CSS.white,
    fontWeight : "bold",
    fontSize   : 12,
    padding    : "4px 8px",
    borderRadius: "2px",
  },
  remarks: {
    border     : `1px solid #aaa`,
    marginTop  : 8,
    padding    : "6px 8px",
  },
  remarksTitle: { fontWeight: "bold", fontSize: 12, marginBottom: 4 },
  signatures: {
    display             : "grid",
    gridTemplateColumns : "1fr 1fr 1fr",
    marginTop           : 25,
    textAlign          : "center",
    fontSize           : 10.5,
  },
  sigLine: {
    borderTop : `1px solid #333`,
    width     : 160,
    margin    : "0 auto 4px",
  },
  principalSig: {
    fontFamily: "'Dancing Script', 'Brush Script MT', cursive",
    fontSize: 18,
    fontWeight: "bold",
    color: "#000",
    height: 25,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: -4,
  },
}

/* ── Single report card HTML ── */
const ReportCard = ({ report, school = SCHOOL }) => {
  switch (report.group) {
    case "MA-pre":
      return <ReportCardMAPre report={report} school={school} />
    case "11-12-practical":
      return <ReportCard11_12Practical report={report} school={school} />
    case "11-12":
      return <ReportCard11_12 report={report} school={school} />
    case "Retest":
    case "retest":
      return <ReportCardRetest report={report} school={school} />
    case "9":
      return <ReportCardGroup9 report={report} school={school} />
    default:
      break
  }

  const {
    student_info     = {},
    exams            = [],
    grand_total      = [],
    co_scholastic    = [],
    subject_avg      = [],
    attendance       = {},
    remark           = "",
    session_name     = "",
    promotion_status = "",
  } = report

  const si = student_info
  const photoUrl = getPhotoUrl(si.profile_pic)

  // Find Term 1 and Term 2 exams
  const term1Exam = exams.find(e => e.exam_name === "Term 1") || exams[0]
  const term2Exam = exams.find(e => e.exam_name === "Term 2") || exams[1]

  const t1Subjects = term1Exam?.subjects || []
  const t2Subjects = term2Exam?.subjects || []

  // Extract unique subject codes across both terms
  const subjectMap = {}
  t1Subjects.forEach(s => { subjectMap[s.subject_code] = s.subject_name })
  t2Subjects.forEach(s => { subjectMap[s.subject_code] = s.subject_name })
  const subjectCodes = Object.keys(subjectMap)

  const getMark = (exam, subCode, assessName) => {
    if (!exam) return "—"
    const subjectsList = exam.subjects || []
    const sub = subjectsList.find(s => s.subject_code === subCode)
    if (!sub) return "—"

    let targetNames = [assessName]
    if (assessName === "P.T") targetNames.push("PT", "Periodic Test")
    if (assessName === "N.B") targetNames.push("NB", "Note Book")
    if (assessName === "S.E") targetNames.push("SE", "Subject Enrichment")
    if (assessName === "H.Y") targetNames.push("HY", "Half Yearly", "Half-Yearly")
    if (assessName === "A.E") targetNames.push("AE", "Annual Exam", "Annual")

    const assess = (sub.assessments || []).find(a =>
      targetNames.some(name => name.replace(/\./g, "").toLowerCase() === a.assessment_name?.replace(/\./g, "").toLowerCase())
    )
    if (!assess) return "—"
    const score = assess.normalized_score !== null && assess.normalized_score !== undefined ? assess.normalized_score : assess.sum_obtained
    return score !== null && score !== undefined && score !== "" ? String(score) : "—"
  }

  const getTermTotal = (exam, subCode) => {
    if (!exam) return "—"
    const subjectsList = exam.subjects || []
    const sub = subjectsList.find(s => s.subject_code === subCode)
    if (!sub) return "—"
    const total = sub.term_total
    return total !== null && total !== undefined && total !== "" ? total : "—"
  }

  const getTermGrade = (exam, subCode) => {
    if (!exam) return "—"
    const subjectsList = exam.subjects || []
    const sub = subjectsList.find(s => s.subject_code === subCode)
    if (!sub) return "—"
    const grade = sub.positional_grade
    return grade !== null && grade !== undefined && grade !== "" ? grade : "—"
  }

  // Column sums
  const getColumnSum = (exam, assessName) => {
    if (!exam) return ""
    let sum = 0
    let hasVal = false
    subjectCodes.forEach(code => {
      const val = parseFloat(getMark(exam, code, assessName))
      if (!isNaN(val)) {
        sum += val
        hasVal = true
      }
    })
    return hasVal ? sum : ""
  }

  const getTermTotalSum = (exam) => {
    if (!exam) return ""
    let sum = 0
    let hasVal = false
    subjectCodes.forEach(code => {
      const val = parseFloat(getTermTotal(exam, code))
      if (!isNaN(val)) {
        sum += val
        hasVal = true
      }
    })
    return hasVal ? sum : ""
  }

  const getGrandTotalSum = () => {
    let sum = 0
    let hasVal = false
    subjectCodes.forEach(code => {
      const gt = grand_total.find(g => g.subject_code === code)
      const val = parseFloat(gt?.average)
      if (!isNaN(val)) {
        sum += val
        hasVal = true
      }
    })
    return hasVal ? sum : ""
  }

  const isClass4 = String(si.st_class).toLowerCase() === "iv" || String(si.st_class).toLowerCase() === "4"

  return (
    <div style={S.page}>
      <div style={S.outerBorder}>

        {/* ── Top bar ── */}
        <div style={S.topBar}>
          <span>Session : <b>{session_name || "—"}</b></span>
          <span>Print Date : {printDate}</span>
        </div>

        {/* ── School header ── */}
        <div style={S.schoolHeader}>
          <div style={S.schoolEst}>{school.established}</div>
          <div style={S.schoolName}>{school.name}</div>
        </div>

        {/* ── Info strip ── */}
        <div style={S.infoStrip}>
          <div style={S.logoBox}>
            <img id="preview-cbse-logo" src={cbseLogo} alt="CBSE" style={S.logoImg} />
          </div>
          <div style={S.centerInfo}>
            <div style={S.affiliation}>{school.affiliation}</div>
            <div><span style={S.redHeader}>Address : </span>{school.address}</div>
            <div>
              <span style={S.redHeader}>Phone : </span>{school.phone},
            </div>
            <div>
              <span style={S.redHeader}>Email : </span>{school.email} &nbsp; 
              <span style={S.redHeader}>Website : </span>{school.website}
            </div>
          </div>
          <div style={S.logoBox}>
            <img id="preview-school-logo" src={schoolLogo} alt="School Logo" style={S.logoImg} />
          </div>
        </div>

        {/* ── Student details ── */}
        <div style={S.studentSection}>
          <div style={S.studentLabel}>Student Details</div>
          <div style={S.studentGrid}>
            <div style={S.studentLeft}>
              <div style={S.studentFieldRow}>
                <span style={S.studentFieldLabel}>Student's Name :</span>
                <span style={S.studentFieldValue}>{fmt(si.st_name)}</span>
              </div>
              <div style={S.studentFieldRow}>
                <span style={S.studentFieldLabel}>Father's Name :</span>
                <span style={S.studentFieldValue}>{si.father_name ? `MR. ${si.father_name.toUpperCase()}` : "—"}</span>
              </div>
              <div style={S.studentFieldRow}>
                <span style={S.studentFieldLabel}>Mother's Name :</span>
                <span style={S.studentFieldValue}>{si.mother_name ? `MS. ${si.mother_name.toUpperCase()}` : "—"}</span>
              </div>
              <div style={S.studentFieldRow}>
                <span style={S.studentFieldLabel}>Address :</span>
                <span style={S.studentFieldValue}>{fmt(si.address)}</span>
              </div>
            </div>
            <div style={S.studentRight}>
              <div style={S.studentFieldRow}>
                <span style={S.studentFieldLabel}>Code :</span>
                <span style={S.studentFieldValue}>{fmt(si.st_code)}</span>
              </div>
              <div style={S.studentFieldRow}>
                <span style={S.studentFieldLabel}>Class :</span>
                <span style={S.studentFieldValue}>{fmt(si.st_class)}</span>
              </div>
              <div style={S.studentFieldRow}>
                <span style={S.studentFieldLabel}>Section :</span>
                <span style={S.studentFieldValue}>{fmt(si.st_sec)}</span>
              </div>
              <div style={S.studentFieldRow}>
                <span style={S.studentFieldLabel}>Date of Birth :</span>
                <span style={S.studentFieldValue}>{fmtD(si.dob)}</span>
              </div>
            </div>
            <div style={S.studentPhoto}>
              {photoUrl ? (
                <img src={photoUrl} alt="Student" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                "Photo"
              )}
            </div>
          </div>
        </div>

        {/* ── Title band ── */}
        <div style={S.titleBand}>
          <div style={S.titleLeft} />
          <div style={S.titleCenter}>ANNUAL EXAMINATION REPORT CARD</div>
          <div style={S.titleRight} />
        </div>

        {/* ── Scholastic header ── */}
        <div style={S.scholHeader}>
          <div style={S.scholPart}>PART 1 : SCHOLASTIC AREA</div>
          <div style={S.scholInst}>
            Instructions : Positional Grade is position of the student according to grading scale.
            The grades have been awarded on a 9-point scale, for scholastic areas.
            Following is the grading scale:
          </div>
        </div>

        {/* ── Grade strip ── */}
        <div style={S.gradeStripRow}>
          {GRADE_STRIP.map(g => (
            <div key={g.label} style={{ background: g.bg, color: g.color, padding: "3px 1px", textAlign: "center" }}>
              {g.label}
            </div>
          ))}
        </div>
        <div style={S.gradeSubRow}>
          {GRADE_STRIP.map(g => (
            <div key={g.label} style={{ padding: 2, textAlign: "center", borderRight: `1px solid #bbb`, color: "#222" }}>
              {g.sub}
            </div>
          ))}
        </div>

        {/* ── Unified side-by-side Marks Table ── */}
        <div style={{ overflowX: "auto" }}>
          <table style={S.marksTable}>
            <thead>
              {/* Row 1: Term headers */}
              <tr>
                <th rowSpan={3} style={{ ...S.th({ textAlign: "left" }), width: 150 }}>SUBJECTS</th>
                <th colSpan={6} style={S.th({ ...S.thTerm })}>TERM 1 (APRIL - SEPTEMBER)</th>
                <th colSpan={6} style={S.th({ ...S.thTerm })}>TERM 2 (OCTOBER - MARCH)</th>
                <th colSpan={2} style={S.th({ ...S.thAvg })}>AVERAGE<br />(Term-1 + Term-2)</th>
              </tr>
              {/* Row 2: Assessment names */}
              <tr>
                {/* Term 1 */}
                <th style={S.th()}>P.T.</th>
                <th style={S.th()}>N.B.</th>
                <th style={S.th()}>S.E.</th>
                <th style={S.th()}>H.Y.</th>
                <th style={S.th()}>Term-1 Total</th>
                <th style={S.th()}>Positional Grade</th>

                {/* Term 2 */}
                <th style={S.th()}>P.T.</th>
                <th style={S.th()}>N.B.</th>
                <th style={S.th()}>S.E.</th>
                <th style={S.th()}>A.E.</th>
                <th style={S.th()}>Term-2 Total</th>
                <th style={S.th()}>Positional Grade</th>

                {/* Average */}
                <th style={S.th()}>Grand Total</th>
                <th style={S.th()}>Positional Grade</th>
              </tr>
              {/* Row 3: Max marks */}
              <tr>
                {/* Term 1 max */}
                <th style={S.th({ fontSize: 8.5 })}>A=10</th>
                <th style={S.th({ fontSize: 8.5 })}>B=5</th>
                <th style={S.th({ fontSize: 8.5 })}>C=5</th>
                <th style={S.th({ fontSize: 8.5 })}>D=80</th>
                <th style={S.th({ fontSize: 8.5 })}>A+B+C+D=100</th>
                <th style={S.th()} />

                {/* Term 2 max */}
                <th style={S.th({ fontSize: 8.5 })}>A=10</th>
                <th style={S.th({ fontSize: 8.5 })}>B=5</th>
                <th style={S.th({ fontSize: 8.5 })}>C=5</th>
                <th style={S.th({ fontSize: 8.5 })}>D=80</th>
                <th style={S.th({ fontSize: 8.5 })}>A+B+C+D=100</th>
                <th style={S.th()} />

                {/* Average max */}
                <th style={S.th({ fontSize: 8.5 })}>Out of 100</th>
                <th style={S.th({ fontSize: 8.5 })}>Rank</th>
              </tr>
            </thead>
            <tbody>
              {subjectCodes.map((code, idx) => {
                const term1Total = getTermTotal(term1Exam, code)
                const term2Total = getTermTotal(term2Exam, code)
                const gt = grand_total.find(g => g.subject_code === code)

                return (
                  <tr key={code}>
                    <td style={S.td({ textAlign: "left", fontWeight: "600" })}>
                      {idx + 1}) {subjectMap[code]}
                    </td>

                    {/* Term 1 scores */}
                    <td style={S.td()}>{getMark(term1Exam, code, "P.T")}</td>
                    <td style={S.td()}>{getMark(term1Exam, code, "N.B")}</td>
                    <td style={S.td()}>{getMark(term1Exam, code, "S.E")}</td>
                    <td style={S.td()}>{getMark(term1Exam, code, "H.Y")}</td>
                    <td style={S.td({ ...S.goldCell })}>{fmt(term1Total)}</td>
                    <td style={S.td()}>{getTermGrade(term1Exam, code)}</td>

                    {/* Term 2 scores */}
                    <td style={S.td()}>{getMark(term2Exam, code, "P.T")}</td>
                    <td style={S.td()}>{getMark(term2Exam, code, "N.B")}</td>
                    <td style={S.td()}>{getMark(term2Exam, code, "S.E")}</td>
                    <td style={S.td()}>{getMark(term2Exam, code, "A.E")}</td>
                    <td style={S.td({ ...S.goldCell })}>{fmt(term2Total)}</td>
                    <td style={S.td()}>{getTermGrade(term2Exam, code)}</td>

                    {/* Average */}
                    <td style={S.td({ ...S.goldCell })}>{fmt(gt?.average ?? null)}</td>
                    <td style={S.td({ ...S.goldCell })}>{fmt(gt?.final_grade || null)}</td>
                  </tr>
                )
              })}

              {/* TOTAL MARKS Row */}
              <tr style={S.totalRow}>
                <td style={S.td({ textAlign: "left", ...S.totalRow })}>TOTAL MARKS</td>

                {/* Term 1 sums */}
                <td style={S.td({ ...S.totalRow })}>{getColumnSum(term1Exam, "P.T")}</td>
                <td style={S.td({ ...S.totalRow })}>{getColumnSum(term1Exam, "N.B")}</td>
                <td style={S.td({ ...S.totalRow })}>{getColumnSum(term1Exam, "S.E")}</td>
                <td style={S.td({ ...S.totalRow })}>{getColumnSum(term1Exam, "H.Y")}</td>
                <td style={S.td({ ...S.totalRow, background: "#020255" })}>{getTermTotalSum(term1Exam)}</td>
                <td style={S.td({ ...S.totalRow })} />

                {/* Term 2 sums */}
                <td style={S.td({ ...S.totalRow })}>{getColumnSum(term2Exam, "P.T")}</td>
                <td style={S.td({ ...S.totalRow })}>{getColumnSum(term2Exam, "N.B")}</td>
                <td style={S.td({ ...S.totalRow })}>{getColumnSum(term2Exam, "S.E")}</td>
                <td style={S.td({ ...S.totalRow })}>{getColumnSum(term2Exam, "A.E")}</td>
                <td style={S.td({ ...S.totalRow, background: "#020255" })}>{getTermTotalSum(term2Exam)}</td>
                <td style={S.td({ ...S.totalRow })} />

                {/* Average sums */}
                <td style={S.td({ ...S.totalRow, background: "#020255" })}>{getGrandTotalSum()}</td>
                <td style={S.td({ ...S.totalRow })} />
              </tr>
            </tbody>
          </table>
        </div>

        {report.is_retest && report.previous_attempt_marks && (
          <div style={{
            background: "#ebdcb9",
            border: "1px solid #7d0a0a",
            padding: "8px 12px",
            marginBottom: "12px",
            fontWeight: "bold",
            fontSize: "11px",
            color: "#000",
            textAlign: "left"
          }}>
            {report.previous_attempt_marks}
          </div>
        )}

        {/* ── Notes ── */}
        <div style={S.notesRow}>
          <b>P.T.</b> = Periodic Test, <b>N.B.</b> : Note Book, <b>S.E.</b> = Subject Enrichment, <b>H.Y.</b> = Half Yearly Examination, <b>A.E.</b> = Annual Examination
        </div>

        {/* ── Please Note block for Class 4 ── */}
        {isClass4 && (
          <div style={S.pleaseNoteContainer}>
            <div style={S.pleaseNoteHeader}>
              <div>Please Note (Term - 1)</div>
              <div>Term - 2</div>
            </div>
            <div style={S.pleaseNoteBody}>
              <div style={S.pleaseNoteRow}>
                <div style={S.pleaseNoteSubject}>English 1</div>
                <div>
                  Periodic Test mark is replaced by their Theatre test mark.<br />
                  Subject Enrichment mark is awarded based on their Reading Skills.
                </div>
                <div>
                  Periodic Test mark is replaced by their Theatre test mark.<br />
                  Subject Enrichment mark is awarded based on their Speaking Skills.<br />
                  Note Book mark is replaced by their writing test mark.
                </div>
              </div>
              <div style={S.pleaseNoteRow}>
                <div style={S.pleaseNoteSubject}>English 2</div>
                <div>
                  Note Book mark is replaced by their dictation test mark.<br />
                  Periodic Test mark is the English unit test mark
                </div>
                <div>
                  Note Book mark is replaced by their writing test mark.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── PART 2 label ── */}
        <div style={{ fontWeight: "bold", fontSize: 11, marginTop: 4, color: "#000" }}>
          PART 2 : CO-SCHOLASTIC AREA
        </div>

        {/* ── Lower 3-column section ── */}
        <div style={S.lowerGrid}>

          {/* Co-scholastic */}
          <div style={S.coSchol}>
            <div style={S.coScholTitle}>CO-SCHOLASTIC AREA</div>
            <div style={{ padding: 6 }}>
              <div style={{ fontSize: 10, marginBottom: 4, fontWeight: "bold" }}>Grading Scale</div>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 6, justifyContent: "center" }}>
                {["A", "B", "C"].map((g, i) => (
                  <div key={g} style={{ textAlign: "center" }}>
                    <div style={{
                      width: 26, height: 26, borderRadius: "50%", border: "1.5px solid #333",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: "bold", fontSize: 13, margin: "0 auto",
                    }}>{g}</div>
                    <div style={{ fontSize: 8.5, marginTop: 1, color: "#444" }}>
                      {["Outstanding", "Very Good", "Fair"][i]}
                    </div>
                  </div>
                ))}
              </div>
              <table style={S.coTable}>
                <thead>
                  <tr>
                    <th style={S.coTh}>SUBJECTS</th>
                    <th style={S.coTh}>GRADE</th>
                  </tr>
                </thead>
                <tbody>
                  {co_scholastic.length > 0 ? co_scholastic.map((c, i) => (
                    <tr key={i}>
                      <td style={S.coTd}>{fmt(c.subject_name)}</td>
                      <td style={{ ...S.coTd, width: 45, fontWeight: "bold", textAlign: "center" }}>{fmt(c.grade ?? null)}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={2} style={{ ...S.coTd, color: CSS.midGrey, fontStyle: "italic" }}>
                        No records
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Subject-wise average */}
          <div style={S.subjAvg}>
            <div style={S.subjAvgTitle}>
              Subject wise average score in Class {student_info.st_class}, Section {student_info.st_sec}
            </div>
            <table style={S.avgTable}>
              <thead>
                <tr>
                  <th style={S.avgTh}>SUBJECTS</th>
                  <th style={S.avgTh}>TERM1</th>
                  <th style={S.avgTh}>TERM2</th>
                </tr>
              </thead>
              <tbody>
                {subject_avg.length > 0 ? subject_avg.map((s, i) => (
                  <tr key={i}>
                    <td style={S.avgTd}>{i + 1}) {s.subject_name}</td>
                    <td style={{ ...S.avgTd, textAlign: "center", fontWeight: "600" }}>{fmtAvg(s.term1_avg)}</td>
                    <td style={{ ...S.avgTd, textAlign: "center", fontWeight: "600" }}>{fmtAvg(s.term2_avg)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={3} style={{ ...S.avgTd, color: CSS.midGrey, fontStyle: "italic" }}>
                      No data
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Promotion + Attendance */}
          <div style={S.promoAttend}>
            <div style={S.promoTitle}>PROMOTION STATUS</div>
            <div style={{ minHeight: 45, padding: "6px 8px", fontSize: 11, fontWeight: "bold", textAlign: "center", color: "#15803d" }}>
              {promotion_status || "—"}
            </div>
            <div style={S.attendTitle}>ATTENDANCE</div>
            <div style={S.attendBody}>
              <div style={S.attendGrid}>
                <div>
                  <div><b>Total Days:</b> &nbsp; {fmt(attendance?.total_days)}</div>
                  <div style={{ marginTop: 2 }}><b>Present:</b> &nbsp; {fmt(attendance?.days_present)}</div>
                </div>
                <div style={S.attendPct}>
                  {attendance?.percentage ?? "—"}%
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* ── Class Teacher's Remarks ── */}
        <div style={S.remarks}>
          <div style={S.remarksTitle}>CLASS TEACHER'S REMARKS</div>
          <div style={{ minHeight: 40, fontSize: 11, color: CSS.darkGrey, fontStyle: remark ? "normal" : "italic" }}>
            {remark || "No remarks entered."}
          </div>
        </div>

        {/* ── Signatures ── */}
        <div style={S.signatures}>
          <div>
            <div style={{ height: 25 }} />
            <div style={S.sigLine} />
            <div><b>Signature of Guardian</b></div>
          </div>
          <div>
            <div style={{ height: 25 }} />
            <div style={S.sigLine} />
            <div><b>Signature of Class Teacher</b></div>
          </div>
          <div>
            <div style={{ height: 25 }} />
            <div style={S.sigLine} />
            <div><b>Signature of Principal</b></div>
          </div>
        </div>

      </div>
    </div>
  )
}

/* ── Modal wrapper ── */
export const GradeCardPreviewModal = ({
  isOpen,
  toggle,
  reports,
  isLoadingReports,
  onDownloadPdf,
}) => {
  const [currentPage, setCurrentPage] = useState(0)

  useEffect(() => { if (isOpen) setCurrentPage(0) }, [isOpen])

  if (!isOpen) return null

  if (isLoadingReports) {
    return (
      <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
        <ModalBody className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-3 text-muted">Generating grade cards…</p>
        </ModalBody>
      </Modal>
    )
  }

  if (!reports || reports.length === 0) return null

  const total  = reports.length
  const report = reports[currentPage]

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" centered scrollable>
      <ModalHeader toggle={toggle} className="bg-light border-bottom">
        <span className="fw-semibold">
          <i className="bx bx-id-card me-2 text-primary" />
          Grade Card Preview
        </span>
      </ModalHeader>

      <ModalBody style={{ padding: "16px 20px", background: "#cccccc" }}>
        <ReportCard report={report} />
      </ModalBody>

      <ModalFooter className="d-flex flex-wrap gap-2 justify-content-between align-items-center border-top bg-light">
        <div className="d-flex align-items-center gap-2">
          <Button color="secondary" outline size="sm"
            disabled={currentPage === 0}
            onClick={() => setCurrentPage(p => p - 1)}>
            <i className="bx bx-chevron-left" /> Previous
          </Button>
          <span style={{ fontSize: 13, color: "#6b7280", minWidth: 80, textAlign: "center" }}>
            {currentPage + 1} of {total}
          </span>
          <Button color="secondary" outline size="sm"
            disabled={currentPage === total - 1}
            onClick={() => setCurrentPage(p => p + 1)}>
            Next <i className="bx bx-chevron-right" />
          </Button>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button color="danger" size="sm" onClick={onDownloadPdf}>
            <i className="bx bxs-file-pdf me-1" />Download PDF
          </Button>
          <Button color="secondary" outline size="sm" onClick={toggle}>Close</Button>
        </div>
      </ModalFooter>
    </Modal>
  )
}

// ═════════════════════════════════════════════════════════════════════════════
//  ②  PDF GENERATOR  (jsPDF — mirrors the HTML layout above)
// ═════════════════════════════════════════════════════════════════════════════

// ── jsPDF colour palette (RGB arrays matching CSS hex above) ─────────────────
const C = {
  maroon    : [125, 10,  10],
  darkBrown : [74,  46,  24],
  gold      : [212, 176, 106],
  tan       : [184, 149, 106],
  navyBlue  : [2,   2,   85],
  tableHead : [85,  85,  85],
  sectionBg : [239, 239, 233],
  rowAlt    : [245, 245, 245],
  white     : [255, 255, 255],
  black     : [0,   0,   0],
  lightGrey : [204, 204, 204],
  midGrey   : [136, 136, 136],
  darkGrey  : [80,  80,  80],
  infoBg    : [240, 240, 240],
  goldCell  : [232, 208, 96],
  orange    : [224, 112, 32],
  yellowAtt : [245, 230, 66],
  green     : [58,  125, 68],
  gsA1      : [192, 57,  43],
  gsA2      : [230, 126, 34],
  gsB1      : [243, 156, 18],
  gsB2      : [39,  174, 96],
  gsC1      : [22,  160, 133],
  gsC2      : [41,  128, 185],
  gsD1      : [26,  82,  118],
  gsD2      : [74,  35,  90],
  gsE       : [26,  26,  26],
}

const C_GRADE_STRIP = [
  { label: "A1", bg: C.gsA1, textColor: C.white },
  { label: "A2", bg: C.gsA2, textColor: C.white },
  { label: "B1", bg: C.gsB1, textColor: C.black },
  { label: "B2", bg: C.gsB2, textColor: C.white },
  { label: "C1", bg: C.gsC1, textColor: C.white },
  { label: "C2", bg: C.gsC2, textColor: C.white },
  { label: "D1", bg: C.gsD1, textColor: C.white },
  { label: "D2", bg: C.gsD2, textColor: C.white },
  { label: "E",  bg: C.gsE,  textColor: C.white },
]

const C_GRADE_SUBS = [
  "Top 1/8th", "Next 1/8th", "Next 1/8th", "Next 1/8th",
  "Next 1/8th", "Next 1/8th", "Next 1/8th", "Next 1/8th", "Failed",
]

const mmW = 210, mmH = 297, M = 6

const pSetFill = (doc, a) => doc.setFillColor(a[0], a[1], a[2])
const pSetDraw = (doc, a) => doc.setDrawColor(a[0], a[1], a[2])
const pSetFont = (doc, a) => doc.setTextColor(a[0], a[1], a[2])

const getBase64Image = (imgElement) => {
  if (!imgElement) return null
  try {
    const canvas = document.createElement("canvas")
    canvas.width = imgElement.naturalWidth || imgElement.width
    canvas.height = imgElement.naturalHeight || imgElement.height
    const ctx = canvas.getContext("2d")
    ctx.drawImage(imgElement, 0, 0)
    return canvas.toDataURL("image/png")
  } catch (e) {
    console.error("Failed to get base64 from element:", e)
    return null
  }
}

export const generateGradeCardPdf = async (reports, schoolConfig = {}) => {
  if (!reports || reports.length === 0) return

  const school = {
    name       : schoolConfig.name        || SCHOOL.name,
    established: schoolConfig.established || SCHOOL.established,
    affiliation: schoolConfig.affiliation || SCHOOL.affiliation,
    address    : schoolConfig.address     || SCHOOL.address,
    phone      : schoolConfig.phone       || SCHOOL.phone,
    email      : schoolConfig.email       || SCHOOL.email,
    website    : schoolConfig.website     || SCHOOL.website,
  }

  // Get preloaded image data from DOM to avoid CORS issues
  const cbseImgEl = document.getElementById("preview-cbse-logo")
  const schoolImgEl = document.getElementById("preview-school-logo")

  const cbseBase64 = getBase64Image(cbseImgEl)
  const schoolBase64 = getBase64Image(schoolImgEl)

  // Preload student photos
  const studentImages = {}
  for (const report of reports) {
    const code = report.student_info?.st_code
    const photoUrl = getPhotoUrl(report.student_info?.profile_pic)
    if (photoUrl && code) {
      studentImages[code] = await loadImage(photoUrl)
    }
  }

  const isMAPre = reports[0]?.group === "MA-pre"
  const doc = new jsPDF({ orientation: isMAPre ? "landscape" : "portrait", unit: "mm", format: "a4" })
  for (let idx = 0; idx < reports.length; idx++) {
    const report = reports[idx]
    if (idx > 0) doc.addPage("a4", isMAPre ? "landscape" : "portrait")
    await pDrawPage(doc, report, school, cbseBase64, schoolBase64, studentImages[report.student_info?.st_code])
  }

  if (reports.length > 1) {
    doc.save("GradeCards.pdf")
  } else {
    const firstName = reports[0]?.student_info?.st_name?.replace(/\s+/g, "_") || "student"
    doc.save(`GradeCard_${firstName}.pdf`)
  }
}

async function pDrawPage(doc, report, school, cbseImg, schoolImg, studentImg) {
  switch (report.group) {
    case "MA-pre":
      return pDrawPageMAPre(doc, report, school, cbseImg, schoolImg, studentImg)
    case "11-12-practical":
      return pDrawPage11_12Practical(doc, report, school, cbseImg, schoolImg, studentImg)
    case "11-12":
      return pDrawPage11_12(doc, report, school, cbseImg, schoolImg, studentImg)
    case "Retest":
    case "retest":
      return pDrawPageRetest(doc, report, school, cbseImg, schoolImg, studentImg)
    case "9":
      return pDrawPageGroup9(doc, report, school, cbseImg, schoolImg, studentImg)
    default:
      break
  }

  const {
    student_info     = {},
    exams            = [],
    grand_total      = [],
    co_scholastic    = [],
    subject_avg      = [],
    attendance       = {},
    remark           = "",
    session_name     = "",
    promotion_status = "",
  } = report

  let y = M

  y = pTopBar(doc, session_name, y)
  y = pSchoolHeader(doc, school, y)
  y = pInfoStrip(doc, school, cbseImg, schoolImg, y)
  y = pStudentDetails(doc, student_info, studentImg, y)
  y = pTitleBand(doc, y)
  y = pScholasticHeader(doc, y)
  y = pGradeStrip(doc, y)

  const term1Exam = exams.find(e => e.exam_name === "Term 1") || exams[0]
  const term2Exam = exams.find(e => e.exam_name === "Term 2") || exams[1]

  y = pMarksTable(doc, term1Exam, term2Exam, grand_total, y)
  if (report.is_retest && report.previous_attempt_marks) {
    y = pPreviousAttemptMarks(doc, report.previous_attempt_marks, y)
  }
  y = pNotesRow(doc, y)

  const isClass4 = String(student_info.st_class).toLowerCase() === "iv" || String(student_info.st_class).toLowerCase() === "4"
  y = pPleaseNoteBox(doc, isClass4, y)

  y = pPart2Label(doc, y)
  y = pLowerSection(doc, co_scholastic, subject_avg, student_info, attendance, promotion_status, y)
  y = pRemarks(doc, remark, y)
  pSignatures(doc)

  // Outer green border drawn on top
  pSetDraw(doc, [58, 125, 68]); doc.setLineWidth(1.2)
  doc.rect(M, M, mmW - M * 2, mmH - M * 2)
  doc.setLineWidth(0.3)
}

function pTopBar(doc, sessionName, y) {
  pSetFont(doc, C.black); doc.setFontSize(7); doc.setFont("helvetica", "normal")
  doc.text(`Session : ${sessionName || "—"}`, M + 2, y + 4)
  doc.text(`Print Date : ${printDate}`, mmW - M - 2, y + 4, { align: "right" })
  return y + 6
}

function pSchoolHeader(doc, school, y) {
  const w = mmW - M * 2
  pSetFill(doc, C.maroon)
  doc.rect(M, y, w, 22, "F")
  pSetFont(doc, C.white)
  doc.setFontSize(7); doc.setFont("helvetica", "normal")
  doc.text(school.established, mmW / 2, y + 4.5, { align: "center" })
  doc.setFontSize(17); doc.setFont("helvetica", "bold")
  doc.text(school.name, mmW / 2, y + 17, { align: "center" })
  return y + 23
}

function pInfoStrip(doc, school, cbseImg, schoolImg, y) {
  const w = mmW - M * 2
  pSetFill(doc, C.white)
  doc.rect(M, y, w, 18, "F")
  pSetDraw(doc, [221, 221, 221]); doc.setLineWidth(0.2)
  doc.line(M, y + 18, M + w, y + 18)

  // Logo boxes
  if (cbseImg) {
    try {
      doc.addImage(cbseImg, "PNG", M + 1, y + 1, 16, 16)
    } catch (e) {
      console.warn("Failed to add CBSE logo to PDF:", e)
      pSetDraw(doc, C.lightGrey); doc.setLineWidth(0.3)
      doc.rect(M + 1, y + 1, 16, 16)
      pSetFont(doc, C.midGrey); doc.setFontSize(5)
      doc.text("CBSE Logo", M + 9, y + 10, { align: "center" })
    }
  } else {
    pSetDraw(doc, C.lightGrey); doc.setLineWidth(0.3)
    doc.rect(M + 1, y + 1, 16, 16)
    pSetFont(doc, C.midGrey); doc.setFontSize(5)
    doc.text("CBSE Logo", M + 9, y + 10, { align: "center" })
  }

  if (schoolImg) {
    try {
      doc.addImage(schoolImg, "PNG", mmW - M - 17, y + 1, 16, 16)
    } catch (e) {
      console.warn("Failed to add School logo to PDF:", e)
      pSetDraw(doc, C.lightGrey); doc.setLineWidth(0.3)
      doc.rect(mmW - M - 17, y + 1, 16, 16)
      pSetFont(doc, C.midGrey); doc.setFontSize(5)
      doc.text("School Logo", mmW - M - 9, y + 10, { align: "center" })
    }
  } else {
    pSetDraw(doc, C.lightGrey); doc.setLineWidth(0.3)
    doc.rect(mmW - M - 17, y + 1, 16, 16)
    pSetFont(doc, C.midGrey); doc.setFontSize(5)
    doc.text("School Logo", mmW - M - 9, y + 10, { align: "center" })
  }

  // Center info
  const cx = mmW / 2
  pSetFont(doc, C.black); doc.setFontSize(7.5); doc.setFont("helvetica", "bold")
  doc.text(school.affiliation, cx, y + 4.5, { align: "center" })
  doc.setFont("helvetica", "normal"); doc.setFontSize(6.5)
  doc.text(`Address : ${school.address}`, cx, y + 8, { align: "center" })
  doc.text(`Phone : ${school.phone}`, cx, y + 11.5, { align: "center" })
  doc.text(`Email : ${school.email}   Website : ${school.website}`, cx, y + 15, { align: "center" })
  return y + 19
}

function pStudentDetails(doc, si, studentImg, y) {
  const w = mmW - M * 2
  const startY = y
  
  // Details background box
  pSetFill(doc, C.sectionBg)
  doc.rect(M, startY, w, 30, "F")

  // "Student Details" red label
  pSetFill(doc, C.maroon)
  doc.rect(M + 1.5, startY + 1.5, 30, 5, "F")
  pSetFont(doc, C.white); doc.setFontSize(7); doc.setFont("helvetica", "bold")
  doc.text("Student Details", M + 3, startY + 5)

  // Draw fields
  const lx = M + 2
  const col2x = mmW / 2 - 10
  const lineH = 4.5
  let curY = startY + 11

  const field = (label, value, x, yy, maxWidth) => {
    pSetFont(doc, C.darkGrey); doc.setFont("helvetica", "normal"); doc.setFontSize(7)
    doc.text(label, x, yy)
    pSetFont(doc, C.black); doc.setFont("helvetica", "bold"); doc.setFontSize(7)
    if (maxWidth) {
      doc.text(fmt(value), x + 24, yy, { maxWidth })
    } else {
      doc.text(fmt(value), x + 24, yy)
    }
  }

  field("Student's Name :", si.st_name,                    lx,    curY)
  field("Code :",           si.st_code,                    col2x, curY); curY += lineH
  field("Father's Name :",  si.father_name ? `MR. ${si.father_name.toUpperCase()}` : "—", lx,    curY)
  field("Class :",          (si.st_class || "").toString().toUpperCase(),col2x, curY); curY += lineH
  field("Mother's Name :",  si.mother_name ? `MS. ${si.mother_name.toUpperCase()}` : "—", lx,    curY)
  field("Section :",        (si.st_sec   || "").toString(),col2x, curY); curY += lineH
  field("Address :",        si.address,                    lx,    curY, 61)
  field("Date of Birth :",  fmtD(si.dob),                 col2x, curY)

  // Photo box
  const photoW = 18, photoH = 22
  const photoX = mmW - M - photoW - 2
  const photoY = startY + 4

  // Draw photo background and border
  pSetFill(doc, C.white)
  doc.rect(photoX, photoY, photoW, photoH, "F")
  pSetDraw(doc, [200, 200, 200]); doc.setLineWidth(0.2)
  doc.rect(photoX, photoY, photoW, photoH)

  if (studentImg) {
    try {
      doc.addImage(studentImg, "JPEG", photoX + 0.5, photoY + 0.5, photoW - 1, photoH - 1)
    } catch (e) {
      console.warn("Failed to add student photo to PDF:", e)
      pSetFont(doc, C.midGrey); doc.setFontSize(6)
      doc.text("Photo", photoX + photoW/2, photoY + photoH/2 + 2, { align: "center" })
    }
  } else {
    pSetFont(doc, C.midGrey); doc.setFontSize(6)
    doc.text("Photo", photoX + photoW/2, photoY + photoH/2 + 2, { align: "center" })
  }

  return startY + 30
}


function pTitleBand(doc, y) {
  const w    = mmW - M * 2
  const leftW = 18

  pSetFill(doc, [223, 199, 148])
  doc.rect(M, y, leftW, 9, "F")

  pSetFill(doc, C.darkBrown)
  doc.rect(M + leftW, y, w - leftW * 2, 9, "F")
  pSetFont(doc, C.white); doc.setFontSize(10); doc.setFont("helvetica", "bold")
  doc.text("ANNUAL EXAMINATION REPORT CARD", mmW / 2, y + 6, { align: "center" })

  pSetFill(doc, [223, 199, 148])
  doc.rect(mmW - M - leftW, y, leftW, 9, "F")

  return y + 11
}

function pScholasticHeader(doc, y) {
  const w = mmW - M * 2
  pSetFill(doc, [247, 247, 247])
  doc.rect(M, y, w, 10, "F")
  pSetDraw(doc, [221, 221, 221]); doc.setLineWidth(0.3)
  doc.line(M, y + 10, M + w, y + 10)

  pSetFont(doc, C.black); doc.setFontSize(7.5); doc.setFont("helvetica", "bold")
  doc.text("PART 1 : SCHOLASTIC AREA", M + 2, y + 4.5)
  doc.setFont("helvetica", "normal"); doc.setFontSize(6)
  pSetFont(doc, C.darkGrey)
  doc.text(
    "Instructions : Positional Grade is position of the student according to grading scale. The grades have been awarded on a 9-point scale, for scholastic areas. Following is the grading scale:",
    M + 2, y + 8.5, { maxWidth: mmW - M * 2 - 4 }
  )
  return y + 12
}

function pGradeStrip(doc, y) {
  const w    = mmW - M * 2
  const colW = w / 9

  C_GRADE_STRIP.forEach((g, i) => {
    pSetFill(doc, g.bg)
    doc.rect(M + i * colW, y, colW, 5, "F")
    pSetFont(doc, g.textColor); doc.setFontSize(7); doc.setFont("helvetica", "bold")
    doc.text(g.label, M + i * colW + colW / 2, y + 3.5, { align: "center" })
  })
  y += 5

  pSetFill(doc, [224, 224, 224])
  doc.rect(M, y, w, 4, "F")
  pSetFont(doc, C.black); doc.setFontSize(5.5); doc.setFont("helvetica", "normal")
  C_GRADE_SUBS.forEach((sub, i) => {
    pSetDraw(doc, C.lightGrey); doc.setLineWidth(0.1)
    if (i > 0) doc.line(M + i * colW, y, M + i * colW, y + 4)
    doc.text(sub, M + i * colW + colW / 2, y + 2.8, { align: "center" })
  })
  return y + 6
}

function pMarksTable(doc, term1Exam, term2Exam, grandTotal, y) {
  const t1Subjects = term1Exam?.subjects || []
  const t2Subjects = term2Exam?.subjects || []

  // Extract unique subject codes across both terms
  const subjectMap = {}
  t1Subjects.forEach(s => { subjectMap[s.subject_code] = s.subject_name })
  t2Subjects.forEach(s => { subjectMap[s.subject_code] = s.subject_name })
  const subjectCodes = Object.keys(subjectMap)

  const getMark = (exam, subCode, assessName) => {
    if (!exam) return "—"
    const subjectsList = exam.subjects || []
    const sub = subjectsList.find(s => s.subject_code === subCode)
    if (!sub) return "—"

    let targetNames = [assessName]
    if (assessName === "P.T") targetNames.push("PT", "Periodic Test")
    if (assessName === "N.B") targetNames.push("NB", "Note Book")
    if (assessName === "S.E") targetNames.push("SE", "Subject Enrichment")
    if (assessName === "H.Y") targetNames.push("HY", "Half Yearly", "Half-Yearly")
    if (assessName === "A.E") targetNames.push("AE", "Annual Exam", "Annual")

    const assess = (sub.assessments || []).find(a =>
      targetNames.some(name => name.replace(/\./g, "").toLowerCase() === a.assessment_name?.replace(/\./g, "").toLowerCase())
    )
    if (!assess) return "—"
    const score = assess.normalized_score !== null && assess.normalized_score !== undefined ? assess.normalized_score : assess.sum_obtained
    return score !== null && score !== undefined && score !== "" ? String(score) : "—"
  }

  const getTermTotal = (exam, subCode) => {
    if (!exam) return "—"
    const subjectsList = exam.subjects || []
    const sub = subjectsList.find(s => s.subject_code === subCode)
    if (!sub) return "—"
    const total = sub.term_total
    return total !== null && total !== undefined && total !== "" ? total : "—"
  }

  const getTermGrade = (exam, subCode) => {
    if (!exam) return "—"
    const subjectsList = exam.subjects || []
    const sub = subjectsList.find(s => s.subject_code === subCode)
    if (!sub) return "—"
    const grade = sub.positional_grade
    return grade !== null && grade !== undefined && grade !== "" ? grade : "—"
  }

  // Column sums
  const getColumnSum = (exam, assessName) => {
    if (!exam) return ""
    let sum = 0
    let hasVal = false
    subjectCodes.forEach(code => {
      const val = parseFloat(getMark(exam, code, assessName))
      if (!isNaN(val)) {
        sum += val
        hasVal = true
      }
    })
    return hasVal ? sum : ""
  }

  const getTermTotalSum = (exam) => {
    if (!exam) return ""
    let sum = 0
    let hasVal = false
    subjectCodes.forEach(code => {
      const val = parseFloat(getTermTotal(exam, code))
      if (!isNaN(val)) {
        sum += val
        hasVal = true
      }
    })
    return hasVal ? sum : ""
  }

  const getGrandTotalSum = () => {
    let sum = 0
    let hasVal = false
    subjectCodes.forEach(code => {
      const gt = grandTotal.find(g => g.subject_code === code)
      const val = parseFloat(gt?.average)
      if (!isNaN(val)) {
        sum += val
        hasVal = true
      }
    })
    return hasVal ? sum : ""
  }

  // Build header rows
  const headRow1 = [
    { content: "SUBJECTS", rowSpan: 3, styles: { valign: "middle", halign: "left", fontStyle: "bold" } },
    { content: "TERM 1 (APRIL - SEPTEMBER)", colSpan: 6, styles: { halign: "center", fillColor: [229, 231, 235], textColor: C.black, fontStyle: "bold", fontSize: 7 } },
    { content: "TERM 2 (OCTOBER - MARCH)", colSpan: 6, styles: { halign: "center", fillColor: [229, 231, 235], textColor: C.black, fontStyle: "bold", fontSize: 7 } },
    { content: "AVERAGE\n(Term-1 + Term-2)", colSpan: 2, styles: { halign: "center", valign: "middle", fillColor: [209, 213, 219], textColor: C.black, fontStyle: "bold", fontSize: 7 } },
  ]

  const headRow2 = [
    // Term 1
    { content: "P.T.", styles: { halign: "center", fontSize: 6.5 } },
    { content: "N.B.", styles: { halign: "center", fontSize: 6.5 } },
    { content: "S.E.", styles: { halign: "center", fontSize: 6.5 } },
    { content: "H.Y.", styles: { halign: "center", fontSize: 6.5 } },
    { content: "Term-1\nTotal", styles: { halign: "center", fontStyle: "bold", fontSize: 6.5 } },
    { content: "Positional\nGrade", styles: { halign: "center", fontSize: 6.5 } },

    // Term 2
    { content: "P.T.", styles: { halign: "center", fontSize: 6.5 } },
    { content: "N.B.", styles: { halign: "center", fontSize: 6.5 } },
    { content: "S.E.", styles: { halign: "center", fontSize: 6.5 } },
    { content: "A.E.", styles: { halign: "center", fontSize: 6.5 } },
    { content: "Term-2\nTotal", styles: { halign: "center", fontStyle: "bold", fontSize: 6.5 } },
    { content: "Positional\nGrade", styles: { halign: "center", fontSize: 6.5 } },

    // Average
    { content: "Grand\nTotal", styles: { halign: "center", fontStyle: "bold", fontSize: 6.5 } },
    { content: "Positional\nGrade", styles: { halign: "center", fontStyle: "bold", fontSize: 6.5 } },
  ]

  const headRow3 = [
    // Term 1 max
    { content: "A=10", styles: { halign: "center", fontSize: 5.5 } },
    { content: "B=5", styles: { halign: "center", fontSize: 5.5 } },
    { content: "C=5", styles: { halign: "center", fontSize: 5.5 } },
    { content: "D=80", styles: { halign: "center", fontSize: 5.5 } },
    { content: "Total=100", styles: { halign: "center", fontStyle: "bold", fontSize: 5.5 } },
    { content: "", styles: { halign: "center", fontSize: 5.5 } },

    // Term 2 max
    { content: "A=10", styles: { halign: "center", fontSize: 5.5 } },
    { content: "B=5", styles: { halign: "center", fontSize: 5.5 } },
    { content: "C=5", styles: { halign: "center", fontSize: 5.5 } },
    { content: "D=80", styles: { halign: "center", fontSize: 5.5 } },
    { content: "Total=100", styles: { halign: "center", fontStyle: "bold", fontSize: 5.5 } },
    { content: "", styles: { halign: "center", fontSize: 5.5 } },

    // Average max
    { content: "Out of 100", styles: { halign: "center", fontSize: 5.5 } },
    { content: "Rank", styles: { halign: "center", fontSize: 5.5 } },
  ]

  // Build body rows
  const body = subjectCodes.map((code, idx) => {
    const gt = grandTotal.find(g => g.subject_code === code)
    const term1Total = getTermTotal(term1Exam, code)
    const term2Total = getTermTotal(term2Exam, code)

    return [
      { content: `${idx + 1}) ${subjectMap[code]}`, styles: { halign: "left", fontStyle: "bold" } },

      // Term 1 scores
      { content: getMark(term1Exam, code, "P.T") },
      { content: getMark(term1Exam, code, "N.B") },
      { content: getMark(term1Exam, code, "S.E") },
      { content: getMark(term1Exam, code, "H.Y") },
      { content: fmt(term1Total), styles: { fontStyle: "bold", fillColor: [254, 240, 138], textColor: C.black } },
      { content: getTermGrade(term1Exam, code) },

      // Term 2 scores
      { content: getMark(term2Exam, code, "P.T") },
      { content: getMark(term2Exam, code, "N.B") },
      { content: getMark(term2Exam, code, "S.E") },
      { content: getMark(term2Exam, code, "A.E") },
      { content: fmt(term2Total), styles: { fontStyle: "bold", fillColor: [254, 240, 138], textColor: C.black } },
      { content: getTermGrade(term2Exam, code) },

      // Average
      { content: fmt(gt?.average ?? null), styles: { fontStyle: "bold", fillColor: [254, 240, 138], textColor: C.black } },
      { content: fmt(gt?.final_grade || null), styles: { fontStyle: "bold", fillColor: [254, 240, 138], textColor: C.black } },
    ]
  })

  // TOTAL MARKS Row
  const totalRowCells = [
    { content: "TOTAL MARKS", styles: { halign: "left", fontStyle: "bold", fillColor: [2, 2, 85], textColor: C.white } },

    // Term 1 sums
    { content: String(getColumnSum(term1Exam, "P.T")), styles: { fillColor: [2, 2, 85], textColor: C.white, fontStyle: "bold" } },
    { content: String(getColumnSum(term1Exam, "N.B")), styles: { fillColor: [2, 2, 85], textColor: C.white, fontStyle: "bold" } },
    { content: String(getColumnSum(term1Exam, "S.E")), styles: { fillColor: [2, 2, 85], textColor: C.white, fontStyle: "bold" } },
    { content: String(getColumnSum(term1Exam, "H.Y")), styles: { fillColor: [2, 2, 85], textColor: C.white, fontStyle: "bold" } },
    { content: String(getTermTotalSum(term1Exam)), styles: { fillColor: [2, 2, 85], textColor: C.white, fontStyle: "bold" } },
    { content: "", styles: { fillColor: [2, 2, 85] } },

    // Term 2 sums
    { content: String(getColumnSum(term2Exam, "P.T")), styles: { fillColor: [2, 2, 85], textColor: C.white, fontStyle: "bold" } },
    { content: String(getColumnSum(term2Exam, "N.B")), styles: { fillColor: [2, 2, 85], textColor: C.white, fontStyle: "bold" } },
    { content: String(getColumnSum(term2Exam, "S.E")), styles: { fillColor: [2, 2, 85], textColor: C.white, fontStyle: "bold" } },
    { content: String(getColumnSum(term2Exam, "A.E")), styles: { fillColor: [2, 2, 85], textColor: C.white, fontStyle: "bold" } },
    { content: String(getTermTotalSum(term2Exam)), styles: { fillColor: [2, 2, 85], textColor: C.white, fontStyle: "bold" } },
    { content: "", styles: { fillColor: [2, 2, 85] } },

    // Average sums
    { content: String(getGrandTotalSum()), styles: { fillColor: [2, 2, 85], textColor: C.white, fontStyle: "bold" } },
    { content: "", styles: { fillColor: [2, 2, 85] } },
  ]
  body.push(totalRowCells)

  autoTable(doc, {
    head: [headRow1, headRow2, headRow3],
    body,
    startY: y,
    margin: { left: M, right: M },
    styles: {
      fontSize: 6.5,
      cellPadding: { top: 1, bottom: 1, left: 1, right: 1 },
      lineColor: [85, 85, 85],
      lineWidth: 0.15,
      textColor: C.black,
      valign: "middle",
      halign: "center",
    },
    headStyles: { fillColor: C.tableHead, textColor: C.white, fontStyle: "bold", fontSize: 6.5, halign: "center" },
    columnStyles: { 0: { halign: "left", cellWidth: 28 } },
    theme: "grid",
  })

  return doc.lastAutoTable.finalY + 2
}

function pPreviousAttemptMarks(doc, text, y) {
  if (!text) return y
  pSetFill(doc, [235, 220, 185])
  pSetDraw(doc, [125, 10, 10])
  doc.setLineWidth(0.3)
  
  const splitText = doc.splitTextToSize(text, mmW - M*2 - 8)
  const lineH = 3.5
  const boxH = splitText.length * lineH + 4.5
  
  doc.rect(M + 2, y, mmW - M*2 - 4, boxH, "FD")
  pSetFont(doc, C.black); doc.setFontSize(7.5); doc.setFont("helvetica", "bold")
  
  let textY = y + 4.5
  splitText.forEach(line => {
    doc.text(line, M + 5, textY)
    textY += lineH
  })
  
  return y + boxH + 3
}

function pNotesRow(doc, y) {
  const w = mmW - M * 2
  pSetFill(doc, [249, 249, 249])
  doc.rect(M, y, w, 5.5, "F")
  pSetDraw(doc, [204, 204, 204]); doc.setLineWidth(0.2)
  doc.rect(M, y, w, 5.5)
  pSetFont(doc, C.darkGrey); doc.setFontSize(6); doc.setFont("helvetica", "normal")
  doc.text(
    "P.T. = Periodic Test,  N.B. : Note Book,  S.E. = Subject Enrichment,  H.Y. = Half Yearly Examination,  A.E. = Annual Examination",
    M + 2, y + 3.5
  )
  return y + 7
}

function pPleaseNoteBox(doc, isClass4, y) {
  if (!isClass4) return y

  autoTable(doc, {
    head: [[
      { content: "Please Note (Term - 1)", styles: { fillColor: [125, 10, 10], textColor: C.white, fontStyle: "bold", fontSize: 7, halign: "left" } },
      { content: "Term - 2", styles: { fillColor: [125, 10, 10], textColor: C.white, fontStyle: "bold", fontSize: 7, halign: "left" } }
    ]],
    body: [
      [
        "English 1:\nPeriodic Test mark is replaced by their Theatre test mark.\nSubject Enrichment mark is awarded based on their Reading Skills.",
        "English 1:\nPeriodic Test mark is replaced by their Theatre test mark.\nSubject Enrichment mark is awarded based on their Speaking Skills.\nNote Book mark is replaced by their writing test mark."
      ],
      [
        "English 2:\nNote Book mark is replaced by their dictation test mark.\nPeriodic Test mark is the English unit test mark",
        "English 2:\nNote Book mark is replaced by their writing test mark."
      ]
    ],
    startY: y,
    margin: { left: M, right: M },
    styles: {
      fontSize: 6.5,
      cellPadding: 1.5,
      lineColor: [125, 10, 10],
      lineWidth: 0.15,
      textColor: C.black,
      valign: "top",
    },
    theme: "grid"
  })

  return doc.lastAutoTable.finalY + 2
}

function pPart2Label(doc, y) {
  pSetFont(doc, C.black); doc.setFontSize(7.5); doc.setFont("helvetica", "bold")
  doc.text("PART 2 : CO-SCHOLASTIC AREA", M + 2, y + 4)
  return y + 6
}

function pLowerSection(doc, coScholastic, subjectAvg, si, attendance, promotionStatus, y) {
  const w         = mmW - M * 2
  const col1W     = (w - 46 - 2) / 2   // co-schol
  const col2W     = (w - 46 - 2) / 2   // subj avg
  const col3W     = 46              // promo + attend
  const col1X     = M
  const col2X     = M + col1W + 1
  const col3X     = M + col1W + col2W + 2
  const sectionH  = 70
  const startY    = y

  // ── Co-scholastic ──
  pSetFill(doc, C.navyBlue)
  doc.rect(col1X, y, col1W, 6, "F")
  pSetFont(doc, C.white); doc.setFontSize(7); doc.setFont("helvetica", "bold")
  doc.text("CO-SCHOLASTIC AREA", col1X + 2, y + 4.2)

  const coY = y + 7
  // Grading scale circles
  pSetFont(doc, C.black); doc.setFontSize(6); doc.setFont("helvetica", "bold")
  doc.text("Grading Scale", col1X + 2, coY + 3)
  const circles = [["A", "Outstanding"], ["B", "Very Good"], ["C", "Fair"]]
  circles.forEach(([label, desc], i) => {
    const cx = col1X + 16 + i * 20, cy = coY + 8
    pSetDraw(doc, C.black); doc.setLineWidth(0.4)
    doc.circle(cx, cy, 4)
    pSetFont(doc, C.black); doc.setFontSize(6.5); doc.setFont("helvetica", "bold")
    doc.text(label, cx, cy + 2, { align: "center" })
    pSetFont(doc, C.darkGrey); doc.setFontSize(5); doc.setFont("helvetica", "normal")
    doc.text(desc, cx, cy + 7, { align: "center" })
  })

  autoTable(doc, {
    head : [["SUBJECTS", "GRADE"]],
    body : coScholastic.length > 0
      ? coScholastic.map(c => [fmt(c.subject_name), { content: fmt(c.grade ?? null), styles: { halign: "center", fontStyle: "bold" } }])
      : [["No co-scholastic records", ""]],
    startY  : coY + 16,
    margin  : { left: col1X, right: mmW - col1X - col1W },
    styles  : { fontSize: 6.5, cellPadding: { top: 1, bottom: 1, left: 2, right: 2 }, lineColor: [136, 136, 136], lineWidth: 0.2, textColor: C.black },
    headStyles    : { fillColor: C.tableHead, textColor: C.white, fontStyle: "bold", fontSize: 6.5 },
    columnStyles  : { 0: { halign: "left" }, 1: { halign: "center", cellWidth: 15 } },
    theme         : "grid",
  })

  // ── Subject avg ──
  pSetFill(doc, C.navyBlue)
  doc.rect(col2X, y, col2W, 6, "F")
  pSetFont(doc, C.white); doc.setFontSize(7); doc.setFont("helvetica", "bold")
  doc.text(`Subject wise average score in Class ${si.st_class || ""}, Section ${si.st_sec || ""}`, col2X + 2, y + 4.2)

  autoTable(doc, {
    head : [["SUBJECTS", "TERM1", "TERM2"]],
    body : subjectAvg.length > 0
      ? subjectAvg.map((s, i) => [
          `${i + 1}) ${s.subject_name}`,
          { content: fmtAvg(s.term1_avg), styles: { halign: "center", fontStyle: "bold" } },
          { content: fmtAvg(s.term2_avg), styles: { halign: "center", fontStyle: "bold" } },
        ])
      : [["No data available", "", ""]],
    startY  : y + 7,
    margin  : { left: col2X, right: mmW - col2X - col2W },
    styles  : { fontSize: 6.5, cellPadding: { top: 1, bottom: 1, left: 2, right: 2 }, lineColor: [170, 170, 170], lineWidth: 0.2, textColor: C.black },
    headStyles   : { fillColor: [221, 221, 221], textColor: C.black, fontStyle: "bold", fontSize: 6.5, halign: "center" },
    columnStyles : { 0: { halign: "left" }, 1: { halign: "center", cellWidth: 14 }, 2: { halign: "center", cellWidth: 14 } },
    theme        : "grid",
  })

  // ── Promotion + Attendance ──
  pSetFill(doc, C.maroon)
  doc.rect(col3X, y, col3W, 5.5, "F")
  pSetFont(doc, C.white); doc.setFontSize(7); doc.setFont("helvetica", "bold")
  doc.text("PROMOTION STATUS", col3X + col3W / 2, y + 3.8, { align: "center" })

  pSetFill(doc, C.white)
  doc.rect(col3X, y + 5.5, col3W, 20, "F")
  pSetDraw(doc, C.lightGrey); doc.setLineWidth(0.2)
  doc.rect(col3X, y + 5.5, col3W, 20)
  if (promotionStatus) {
    pSetFont(doc, [21, 128, 61]); doc.setFontSize(7.5); doc.setFont("helvetica", "bold")
    doc.text(doc.splitTextToSize(promotionStatus, col3W - 4), col3X + 2, y + 10, { align: "left" })
  }

  pSetFill(doc, C.maroon)
  doc.rect(col3X, y + 26.5, col3W, 5.5, "F")
  pSetFont(doc, C.white); doc.setFontSize(7); doc.setFont("helvetica", "bold")
  doc.text("ATTENDANCE", col3X + col3W / 2, y + 30.3, { align: "center" })

  const attY = y + 32
  pSetFill(doc, [245, 230, 66]) // C.yellowAtt
  doc.rect(col3X, attY, col3W, 16, "F")
  pSetDraw(doc, C.lightGrey); doc.setLineWidth(0.2)
  doc.rect(col3X, attY, col3W, 16)

  pSetFont(doc, C.black); doc.setFontSize(6.5); doc.setFont("helvetica", "bold")
  doc.text(`Total Days`, col3X + 2, attY + 5)
  doc.setFont("helvetica", "normal")
  doc.text(`${fmt(attendance?.total_days)} days`, col3X + 22, attY + 5)
  doc.setFont("helvetica", "bold")
  doc.text(`Days Present`, col3X + 2, attY + 10)
  doc.setFont("helvetica", "normal")
  doc.text(`${fmt(attendance?.days_present)} days`, col3X + 22, attY + 10)

  // Percentage orange box
  pSetFill(doc, C.orange)
  doc.rect(col3X + col3W - 14, attY, 14, 16, "F")
  pSetFont(doc, C.white); doc.setFontSize(8.5); doc.setFont("helvetica", "bold")
  doc.text(`${attendance?.percentage ?? "—"}%`, col3X + col3W - 7, attY + 9.5, { align: "center" })

  // Return y after the taller of the two autoTables and the right column
  const afterLeft  = doc.lastAutoTable ? doc.lastAutoTable.finalY : startY + sectionH
  const afterRight = attY + 17
  return Math.max(afterLeft, afterRight) + 3
}

function pRemarks(doc, remark, y) {
  const w = mmW - M * 2
  pSetDraw(doc, [170, 170, 170]); doc.setLineWidth(0.3)
  doc.rect(M, y, w, 20, "D")
  pSetFont(doc, C.black); doc.setFontSize(9); doc.setFont("helvetica", "bold")
  doc.text("CLASS TEACHER'S REMARKS", M + 2, y + 5)
  if (remark) {
    pSetFont(doc, C.darkGrey); doc.setFontSize(7); doc.setFont("helvetica", "italic")
    doc.text(doc.splitTextToSize(remark, w - 6), M + 2, y + 11)
  }
  return y + 22
}

function pSignatures(doc) {
  const y      = mmH - M - 14
  const labels = ["Signature of Guardian", "Signature of Class Teacher", "Signature of Principal"]
  const xs     = [M + 8, mmW / 2 - 22, mmW - M - 52]
  pSetDraw(doc, C.black); doc.setLineWidth(0.3)
  labels.forEach((label, i) => {
    doc.line(xs[i], y, xs[i] + 44, y)
    pSetFont(doc, C.darkGrey); doc.setFontSize(7); doc.setFont("helvetica", "bold")
    doc.text(label, xs[i] + 22, y + 4.5, { align: "center" })
  })
}

// ── Group-Specific Report Card Components (Placeholders) ──

const ReportCardGroup9 = ({ report, school = SCHOOL }) => {
  const {
    student_info     = {},
    exams            = [],
    grand_total      = [],
    co_scholastic    = [],
    subject_avg      = [],
    attendance       = {},
    remark           = "",
    session_name     = "",
    promotion_status = "",
  } = report

  const si = student_info
  const photoUrl = getPhotoUrl(si.profile_pic)

  const scholasticExams = exams.filter(e => 
    !e.exam_name.toLowerCase().includes("co-sco") && 
    !e.exam_name.toLowerCase().includes("co-scholastic")
  )
  const coScholasticExams = exams.filter(e => 
    e.exam_name.toLowerCase().includes("co-sco") || 
    e.exam_name.toLowerCase().includes("co-scholastic")
  )

  const exam1 = scholasticExams.find(e => e.exam_name.includes("Term 1")) || scholasticExams[0]
  const exam2 = scholasticExams.find(e => e.exam_name.includes("Term 2")) || scholasticExams[1] || scholasticExams[0]

  const t1Subjects = exam1?.subjects || []
  const t2Subjects = exam2?.subjects || []

  const subjectMap = {}
  t1Subjects.forEach(s => { subjectMap[s.subject_code] = s.subject_name })
  t2Subjects.forEach(s => { subjectMap[s.subject_code] = s.subject_name })

  const subjectCodes = Object.keys(subjectMap)

  const getMarkForClass9 = (exam, subCode, assessName) => {
    if (!exam) return "—"
    const subjectsList = exam.subjects || []
    const sub = subjectsList.find(s => s.subject_code === subCode)
    if (!sub) return "—"
    
    let targetNames = [assessName]
    if (assessName === "P.T") targetNames.push("PT", "Periodic Test")
    if (assessName === "N.B") targetNames.push("NB", "Note Book")
    if (assessName === "S.E") targetNames.push("SE", "Subject Enrichment")
    if (assessName === "A.E") targetNames.push("AE", "Annual Exam", "Annual")

    const assess = (sub.assessments || []).find(a => 
      targetNames.some(name => name.toLowerCase() === a.assessment_name.toLowerCase())
    )
    if (!assess) return "—"
    const score = assess.normalized_score !== null && assess.normalized_score !== undefined ? assess.normalized_score : assess.sum_obtained
    return score !== null && score !== undefined && score !== "" ? String(score) : "—"
  }

  const getTermTotalForClass9 = (subCode) => {
    const gt = grand_total.find(g => g.subject_code === subCode)
    if (gt && gt.average !== null && gt.average !== undefined) {
      return gt.average
    }
    const term2 = exams.find(e => e.exam_name === "Term 2") || exams[1]
    if (term2) {
      const sub = (term2.subjects || []).find(s => s.subject_code === subCode)
      if (sub && sub.term_total !== null && sub.term_total !== undefined && sub.term_total !== "") return sub.term_total
    }
    return "—"
  }

  const getTermGradeForClass9 = (subCode) => {
    const gt = grand_total.find(g => g.subject_code === subCode)
    if (gt && gt.final_grade) return gt.final_grade
    const term2 = exams.find(e => e.exam_name === "Term 2") || exams[1]
    if (term2) {
      const sub = (term2.subjects || []).find(s => s.subject_code === subCode)
      if (sub && sub.positional_grade !== null && sub.positional_grade !== undefined && sub.positional_grade !== "") return sub.positional_grade
    }
    return "—"
  }

  const subjectsData = subjectCodes.map((code, idx) => {
    const periodic = getMarkForClass9(exam2, code, "P.T") === "—" ? getMarkForClass9(exam1, code, "P.T") : getMarkForClass9(exam2, code, "P.T")
    const notebook = getMarkForClass9(exam2, code, "N.B")
    const enrich = getMarkForClass9(exam2, code, "S.E")
    const annual = getMarkForClass9(exam2, code, "A.E")
    const total = getTermTotalForClass9(code)
    const grade = getTermGradeForClass9(code)

    return {
      slno: idx + 1,
      name: subjectMap[code],
      code,
      periodic,
      notebook,
      enrich,
      annual,
      total,
      grade
    }
  })

  const getSum = (key) => {
    let sum = 0
    let hasVal = false
    subjectsData.forEach(s => {
      const val = parseFloat(s[key])
      if (!isNaN(val)) {
        sum += val
        hasVal = true
      }
    })
    return hasVal ? sum : "—"
  }

  const total_periodic = getSum("periodic")
  const total_notebook = getSum("notebook")
  const total_enrich = getSum("enrich")
  const total_annual = getSum("annual")
  const total_total = getSum("total")

  const coScholasticList = []
  coScholasticExams.forEach(e => {
    (e.subjects || []).forEach(s => {
      coScholasticList.push({
        subject_name: s.subject_name,
        grade: s.grade || s.positional_grade || s.term_total || null
      })
    })
  })
  const finalCoScholastic = co_scholastic.length > 0 ? co_scholastic : coScholasticList

  return (
    <div style={S.page}>
      <div style={S.outerBorder}>
        {/* Top bar */}
        <div style={S.topBar}>
          <span>Session : <b>{session_name || "—"}</b></span>
          <span>Print Date : {new Date().toLocaleDateString("en-GB")}</span>
        </div>

        {/* School header */}
        <div style={S.schoolHeader}>
          <div style={S.schoolEst}>{school.established}</div>
          <div style={S.schoolName}>{school.name}</div>
        </div>

        {/* Info strip */}
        <div style={S.infoStrip}>
          <div style={S.logoBox}>
            <img id="preview-cbse-logo" src={cbseLogo} alt="CBSE" style={S.logoImg} />
          </div>
          <div style={S.centerInfo}>
            <div style={S.affiliation}>{school.affiliation}</div>
            <div><span style={S.redHeader}>Address : </span>{school.address}</div>
            <div>
              <span style={S.redHeader}>Phone : </span>{school.phone},
            </div>
            <div>
              <span style={S.redHeader}>Email : </span>{school.email} &nbsp; 
              <span style={S.redHeader}>Website : </span>{school.website}
            </div>
          </div>
          <div style={S.logoBox}>
            <img id="preview-school-logo" src={schoolLogo} alt="School Logo" style={S.logoImg} />
          </div>
        </div>

        {/* Student details */}
        <div style={S.studentSection}>
          <div style={S.studentLabel}>Student Details</div>
          <div style={S.studentGrid}>
            <div style={S.studentLeft}>
              <div style={S.studentFieldRow}>
                <span style={S.studentFieldLabel}>Student's Name :</span>
                <span style={S.studentFieldValue}>{fmt(si.st_name)}</span>
              </div>
              <div style={S.studentFieldRow}>
                <span style={S.studentFieldLabel}>Father's Name :</span>
                <span style={S.studentFieldValue}>{si.father_name ? `MR. ${si.father_name.toUpperCase()}` : "—"}</span>
              </div>
              <div style={S.studentFieldRow}>
                <span style={S.studentFieldLabel}>Mother's Name :</span>
                <span style={S.studentFieldValue}>{si.mother_name ? `MS. ${si.mother_name.toUpperCase()}` : "—"}</span>
              </div>
              <div style={S.studentFieldRow}>
                <span style={S.studentFieldLabel}>Address :</span>
                <span style={S.studentFieldValue}>{fmt(si.address)}</span>
              </div>
            </div>
            <div style={S.studentRight}>
              <div style={S.studentFieldRow}>
                <span style={S.studentFieldLabel}>Code :</span>
                <span style={S.studentFieldValue}>{fmt(si.st_code)}</span>
              </div>
              <div style={S.studentFieldRow}>
                <span style={S.studentFieldLabel}>Class :</span>
                <span style={S.studentFieldValue}>{fmt(si.st_class).toUpperCase()}</span>
              </div>
              <div style={S.studentFieldRow}>
                <span style={S.studentFieldLabel}>Section :</span>
                <span style={S.studentFieldValue}>{fmt(si.st_sec).toUpperCase()}</span>
              </div>
              <div style={S.studentFieldRow}>
                <span style={S.studentFieldLabel}>Date of Birth :</span>
                <span style={S.studentFieldValue}>{fmtD(si.dob)}</span>
              </div>
            </div>
            <div style={S.studentPhoto}>
              {photoUrl ? (
                <img src={photoUrl} alt="Student" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                "Photo"
              )}
            </div>
          </div>
        </div>

        {/* Title band */}
        <div style={S.titleBand}>
          <div style={S.titleLeft} />
          <div style={S.titleCenter}>ANNUAL EXAMINATION REPORT CARD</div>
          <div style={S.titleRight} />
        </div>

        {/* Scholastic header */}
        <div style={S.scholHeader}>
          <div style={S.scholPart}>PART 1 : SCHOLASTIC AREA</div>
          <div style={S.scholInst}>
            Instructions : Positional Grade is position of the student according to grading scale.
            The grades have been awarded on a 9-point scale, for scholastic areas.
            Following is the grading scale:
          </div>
        </div>

        {/* Grade strip */}
        <div style={S.gradeStripRow}>
          {GRADE_STRIP.map(g => (
            <div key={g.label} style={{ background: g.bg, color: g.color, padding: "3px 1px", textAlign: "center" }}>
              {g.label}
            </div>
          ))}
        </div>
        <div style={S.gradeSubRow}>
          {GRADE_STRIP.map(g => (
            <div key={g.label} style={{ padding: 2, textAlign: "center", borderRight: `1px solid #bbb`, color: "#222" }}>
              {g.sub}
            </div>
          ))}
        </div>

        {/* Scholastic Table */}
        <div style={{ overflowX: "auto" }}>
          <table style={S.marksTable}>
            <thead style={{ background: "#7d0a0a", color: "#fff" }}>
              <tr>
                <th style={{ padding: "4px 6px", border: "1px solid #7d0a0a", color: "#fff" }}>SLNO</th>
                <th style={{ padding: "4px 6px", border: "1px solid #7d0a0a", color: "#fff", textAlign: "left" }}>SUBJECTS</th>
                <th style={{ padding: "4px 6px", border: "1px solid #7d0a0a", color: "#fff" }}>SUBJECT CODE</th>
                <th style={{ padding: "4px 6px", border: "1px solid #7d0a0a", color: "#fff" }}>PERIODIC TEST<br /><small>A=10</small></th>
                <th style={{ padding: "4px 6px", border: "1px solid #7d0a0a", color: "#fff" }}>NOTE BOOK<br /><small>B=5</small></th>
                <th style={{ padding: "4px 6px", border: "1px solid #7d0a0a", color: "#fff" }}>SUBJECT ENRICHMENT<br /><small>C=5</small></th>
                <th style={{ padding: "4px 6px", border: "1px solid #7d0a0a", color: "#fff" }}>ANNUAL EXAM<br /><small>D=80</small></th>
                <th style={{ padding: "4px 6px", border: "1px solid #7d0a0a", color: "#fff" }}>GRAND TOTAL<br /><small>A+B+C+D=100</small></th>
                <th style={{ padding: "4px 6px", border: "1px solid #7d0a0a", color: "#fff" }}>POSITIONAL GRADE</th>
              </tr>
            </thead>
            <tbody>
              {subjectsData.map(s => (
                <tr key={s.slno}>
                  <td style={{ padding: "4px 6px", border: "1px solid #ccc", textAlign: "center" }}>{s.slno}</td>
                  <td style={{ padding: "4px 6px", border: "1px solid #ccc", textAlign: "left", fontWeight: "bold" }}>{s.name}</td>
                  <td style={{ padding: "4px 6px", border: "1px solid #ccc", textAlign: "center" }}>{s.code}</td>
                  <td style={{ padding: "4px 6px", border: "1px solid #ccc", textAlign: "center" }}>{s.periodic}</td>
                  <td style={{ padding: "4px 6px", border: "1px solid #ccc", textAlign: "center" }}>{s.notebook}</td>
                  <td style={{ padding: "4px 6px", border: "1px solid #ccc", textAlign: "center" }}>{s.enrich}</td>
                  <td style={{ padding: "4px 6px", border: "1px solid #ccc", textAlign: "center" }}>{s.annual}</td>
                  <td style={{ padding: "4px 6px", border: "1px solid #ccc", textAlign: "center", fontWeight: "bold", backgroundColor: "#fef9c3" }}>{s.total}</td>
                  <td style={{ padding: "4px 6px", border: "1px solid #ccc", textAlign: "center", fontWeight: "bold" }}>{s.grade}</td>
                </tr>
              ))}
              <tr style={{ background: "#020255", color: "#fff", fontWeight: "bold" }}>
                <td colSpan={3} style={{ padding: "4px 6px", border: "1px solid #020255", color: "#fff", textAlign: "center" }}>TOTAL MARKS</td>
                <td style={{ padding: "4px 6px", border: "1px solid #020255", color: "#fff", textAlign: "center" }}>{total_periodic}</td>
                <td style={{ padding: "4px 6px", border: "1px solid #020255", color: "#fff", textAlign: "center" }}>{total_notebook}</td>
                <td style={{ padding: "4px 6px", border: "1px solid #020255", color: "#fff", textAlign: "center" }}>{total_enrich}</td>
                <td style={{ padding: "4px 6px", border: "1px solid #020255", color: "#fff", textAlign: "center" }}>{total_annual}</td>
                <td style={{ padding: "4px 6px", border: "1px solid #020255", color: "#fff", textAlign: "center", backgroundColor: "#1e3a8a" }}>{total_total}</td>
                <td style={{ padding: "4px 6px", border: "1px solid #020255", color: "#fff", textAlign: "center" }}>-</td>
              </tr>
            </tbody>
          </table>
        </div>

        {report.is_retest && report.previous_attempt_marks && (
          <div style={{
            background: "#ebdcb9",
            border: "1px solid #7d0a0a",
            padding: "8px 12px",
            marginBottom: "12px",
            fontWeight: "bold",
            fontSize: "11px",
            color: "#000",
            textAlign: "left"
          }}>
            {report.previous_attempt_marks}
          </div>
        )}

        {/* Notes */}
        <div style={S.notesRow}>
          <b>P.T.</b> = Periodic Test, <b>N.B.</b> : Note Book, <b>S.E.</b> = Subject Enrichment, <b>H.Y.</b> = Half Yearly Examination, <b>A.E.</b> = Annual Examination
        </div>

        {/* Lower 3-column section */}
        <div style={S.lowerGrid}>
          {/* Co-scholastic */}
          <div style={S.coSchol}>
            <div style={S.coScholTitle}>CO-SCHOLASTIC AREA</div>
            <div style={{ padding: 6 }}>
              <div style={{ fontSize: 10, marginBottom: 4, fontWeight: "bold" }}>Grading Scale</div>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 6, justifyContent: "center" }}>
                {["A", "B", "C"].map((g, i) => (
                  <div key={g} style={{ textAlign: "center" }}>
                    <div style={{
                      width: 26, height: 26, borderRadius: "50%", border: "1.5px solid #333",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: "bold", fontSize: 13, margin: "0 auto",
                    }}>{g}</div>
                    <div style={{ fontSize: 8.5, marginTop: 1, color: "#444" }}>
                      {["Outstanding", "Very Good", "Fair"][i]}
                    </div>
                  </div>
                ))}
              </div>
              <table style={S.coTable}>
                <thead>
                  <tr>
                    <th style={S.coTh}>SUBJECTS</th>
                    <th style={S.coTh}>GRADE</th>
                  </tr>
                </thead>
                <tbody>
                  {finalCoScholastic.length > 0 ? finalCoScholastic.map((c, i) => (
                    <tr key={i}>
                      <td style={S.coTd}>{fmt(c.subject_name)}</td>
                      <td style={{ ...S.coTd, width: 45, fontWeight: "bold", textAlign: "center" }}>{fmt(c.grade ?? null)}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={2} style={{ ...S.coTd, color: CSS.midGrey, fontStyle: "italic" }}>
                        No records
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Subject-wise average */}
          <div style={S.subjAvg}>
            <div style={S.subjAvgTitle}>
              Subject wise average score in Class {fmt(student_info.st_class).toUpperCase()}, Section {fmt(student_info.st_sec).toUpperCase()}
            </div>
            <table style={S.avgTable}>
              <thead>
                <tr>
                  <th style={S.avgTh}>SUBJECTS</th>
                  <th style={{ ...S.avgTh, textAlign: "right" }}>Average</th>
                </tr>
              </thead>
              <tbody>
                {subject_avg.length > 0 ? subject_avg.map((s, i) => (
                  <tr key={i}>
                    <td style={S.avgTd}>{s.subject_name}</td>
                    <td style={{ ...S.avgTd, textAlign: "right", fontWeight: "bold" }}>{fmt(s.average ?? s.term2_avg ?? s.term1_avg)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={2} style={{ ...S.avgTd, color: CSS.midGrey, fontStyle: "italic" }}>
                      No data
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Promotion + Attendance */}
          <div style={S.promoAttend}>
            <div style={S.promoTitle}>PROMOTION STATUS</div>
            <div style={{ minHeight: 45, padding: "6px 8px", fontSize: 11, fontWeight: "bold", textAlign: "center", color: "#15803d" }}>
              {promotion_status || "—"}
            </div>
            <div style={S.attendTitle}>ATTENDANCE</div>
            <div style={S.attendBody}>
              <div style={S.attendGrid}>
                <div>
                  <div><b>Total Days:</b> &nbsp; {fmt(attendance?.total_days)}</div>
                  <div style={{ marginTop: 2 }}><b>Present:</b> &nbsp; {fmt(attendance?.days_present)}</div>
                </div>
                <div style={S.attendPct}>
                  {attendance?.percentage ?? "—"}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Class Teacher's Remarks */}
        <div style={S.remarks}>
          <div style={S.remarksTitle}>CLASS TEACHER'S REMARKS</div>
          <div style={{ minHeight: 40, fontSize: 11, color: CSS.darkGrey, fontStyle: remark ? "normal" : "italic" }}>
            {remark || "No remarks entered."}
          </div>
        </div>

        {/* Signatures */}
        <div style={S.signatures}>
          <div>
            <div style={{ height: 25 }} />
            <div style={S.sigLine} />
            <div><b>Signature of Guardian</b></div>
          </div>
          <div>
            <div style={{ height: 25 }} />
            <div style={S.sigLine} />
            <div><b>Signature of Class Teacher</b></div>
          </div>
          <div>
            <div style={{ height: 25 }} />
            <div style={S.sigLine} />
            <div><b>Signature of Principal</b></div>
          </div>
        </div>
      </div>
    </div>
  )
}

const getMAPreGrade = (exams, examName, assessmentName, subjectName) => {
  if (!exams || !Array.isArray(exams)) return "—"
  
  const cleanAssessName = assessmentName?.replace(/[^a-zA-Z0-9]/g, "").toUpperCase()
  const exam = exams.find(e => {
    const cleanExamName = e.exam_name?.replace(/[^a-zA-Z0-9]/g, "").toUpperCase()
    return cleanExamName?.includes(cleanAssessName)
  })
  if (!exam || !exam.subjects) return "—"
  
  const subject = exam.subjects.find(s => s.subject_name?.toUpperCase() === subjectName?.toUpperCase())
  if (!subject || !subject.assessments) return "—"
  
  const cleanExamNameParam = examName?.replace(/[^a-zA-Z0-9]/g, "").toUpperCase()
  const assessment = subject.assessments.find(a => {
    const cleanAssessNameObj = a.assessment_name?.replace(/[^a-zA-Z0-9]/g, "").toUpperCase()
    return cleanAssessNameObj?.includes(cleanExamNameParam) || cleanExamNameParam?.includes(cleanAssessNameObj)
  })
  if (!assessment) return "—"
  return assessment.grade || assessment.grade_value || "—"
}

const ReportCardMAPre = ({ report, school = SCHOOL }) => {
  const {
    student_info     = {},
    exams            = [],
    attendance       = {},
    remark           = "",
    session_name     = "",
    promotion_status = "",
  } = report

  const si = student_info

  const obsSubjects = [
    "Activities taken part",
    "General Appearance and Grooming",
    "Behaviour and Conduct",
    "Personality",
    "Discipline",
    "Co-Operation"
  ]

  const evalSubjects = [
    "English (Oral)",
    "English (Written)",
    "Number (Oral)",
    "Number (Written)",
    "2nd Language (Oral)",
    "2nd Language (Written)",
    "General Knowledge (Oral)",
    "Recitation",
    "Observation of surroundings",
    "Recognition of sound",
    "Sensorial Presentation",
    "Drawing and Colouring",
    "Apparatus Presentation",
    "Exercises of Practical Life",
    "Picture Conversation"
  ]

  // Styles for pre-primary booklet
  const BS = {
    booklet: {
      width: "100%",
      maxWidth: "1020px",
      margin: "0 auto",
      display: "flex",
      flexDirection: "column",
      gap: "24px",
      fontFamily: "'Inter', 'Arial', sans-serif",
      color: "#333",
    },
    page: {
      background: "#fffde6", // Warm cream/yellowish page background
      border: "3px double #b8956a",
      padding: "20px",
      boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
      borderRadius: "4px",
      position: "relative",
    },
    spread: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "30px",
      minHeight: "700px",
      position: "relative",
    },
    foldLine: {
      position: "absolute",
      left: "50%",
      top: 0,
      bottom: 0,
      width: "1px",
      borderLeft: "1px dashed #b8956a",
      pointerEvents: "none",
    },
    header: {
      textAlign: "center",
      marginBottom: "15px",
    },
    schoolName: {
      fontFamily: "'Georgia', serif",
      fontSize: "20px",
      fontWeight: "bold",
      color: "#7d0a0a",
      marginBottom: "2px",
    },
    schoolDetails: {
      fontSize: "9px",
      color: "#555",
      lineHeight: "1.3",
    },
    band: {
      background: "#b8956a",
      color: "#white",
      fontWeight: "bold",
      fontSize: "12px",
      padding: "5px 10px",
      textAlign: "center",
      borderRadius: "2px",
      textTransform: "uppercase",
      letterSpacing: "1px",
      color: "#fff",
      marginBottom: "10px",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginBottom: "10px",
      fontSize: "11px",
    },
    th: {
      textAlign: "left",
      padding: "6px 8px",
      background: "#b8956a",
      color: "#fff",
      fontWeight: "bold",
    },
    td: {
      padding: "6px 8px",
      borderBottom: "1px solid #e2d2bd",
    },
    gradeBox: {
      width: "25px",
      height: "20px",
      border: "1px solid #b8956a",
      background: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "bold",
      color: "#7d0a0a",
      margin: "0 auto",
    },
    footerBand: {
      background: "#7d0a0a",
      color: "#fff",
      fontSize: "8.5px",
      textAlign: "center",
      padding: "5px",
      borderRadius: "2px",
      marginTop: "10px",
      fontWeight: "bold",
    },
    studentBox: {
      border: "2px solid #7d0a0a",
      padding: "15px",
      background: "#fffde6",
      borderRadius: "4px",
      marginTop: "15px",
    },
    studentGridRow: {
      display: "flex",
      alignItems: "center",
      marginBottom: "8px",
      fontSize: "11px",
    },
    studentLabel: {
      width: "230px",
      fontWeight: "bold",
      color: "#7d0a0a",
    },
    studentValBox: {
      flex: 1,
      border: "1px solid #7d0a0a",
      background: "#fff",
      padding: "4px 8px",
      minHeight: "22px",
      fontWeight: "bold",
    },
    codeBox: {
      width: "18px",
      height: "20px",
      border: "1px solid #7d0a0a",
      background: "#fff",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "bold",
      marginRight: "3px",
    },
    gradeChart: {
      background: "#fffde6",
      border: "1px solid #b8956a",
      borderRadius: "4px",
      padding: "8px",
      display: "flex",
      justifyContent: "space-between",
      fontSize: "9.5px",
      fontWeight: "bold",
      color: "#7d0a0a",
      marginBottom: "15px",
    },
    sigGroup: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: "9px",
      marginTop: "18px",
      fontWeight: "bold",
    }
  }

  // Attendance helpers
  const formatAttVal = val => (val === 0 || val === null || val === undefined || val === "" ? "       " : String(val))
  const getAtt = (termKey, field) => {
    if (report[termKey]) {
      const tAtt = report[termKey]
      if (field === "days_present" && tAtt.present !== undefined) return formatAttVal(tAtt.present)
      if (field === "total_days" && tAtt.total !== undefined) return formatAttVal(tAtt.total)
      if (tAtt[field] !== undefined) return formatAttVal(tAtt[field])
    }
    return formatAttVal(attendance[field])
  }

  const attT1 = { present: getAtt("attendance_t1", "days_present"), total: getAtt("attendance_t1", "total_days") }
  const attT2 = { present: getAtt("attendance_t2", "days_present"), total: getAtt("attendance_t2", "total_days") }
  const remT1 = report.remarks_t1 || ""
  const remT2 = report.remarks_t2 || remark || ""

  return (
    <div style={BS.booklet}>
      {/* ════ PAGE 1 SPREAD: Left (General Observations), Right (Cover Page) ════ */}
      <div style={BS.page}>
        <div style={BS.spread}>
          {/* Left Side: General Observations */}
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            {/* Term 1 Observations */}
            <div>
              <div style={BS.band}>General Observations Term - 1</div>
              <table style={BS.table}>
                <tbody>
                  {obsSubjects.map(sub => (
                    <tr key={sub}>
                      <td style={BS.td}>{sub}</td>
                      <td style={{ ...BS.td, width: 40, textAlign: "center" }}>
                        <div style={BS.gradeBox}>{getMAPreGrade(exams, "GENERAL OBSERVATIONS", "TERM - 1", sub)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: "11px", marginBottom: "8px", lineHeight: "1.4" }}>
                <div>Attendance: <strong>{attT1.present ?? "—"}</strong> days out of <strong>{attT1.total ?? "—"}</strong></div>
                <div style={{ marginTop: "4px" }}>Remarks: <span style={{ color: "#7d0a0a", fontWeight: "bold" }}>{remT1}</span></div>
              </div>
              <div style={BS.sigGroup}>
                <span>Signature: Class Teacher</span>
                <span>Parent / Guardian</span>
                <span>Principal</span>
              </div>
            </div>

            {/* Term 2 Observations */}
            <div style={{ marginTop: "20px", borderTop: "1px dashed #b8956a", paddingTop: "15px" }}>
              <div style={BS.band}>General Observations Term - 2</div>
              <table style={BS.table}>
                <tbody>
                  {obsSubjects.map(sub => (
                    <tr key={sub}>
                      <td style={BS.td}>{sub}</td>
                      <td style={{ ...BS.td, width: 40, textAlign: "center" }}>
                        <div style={BS.gradeBox}>{getMAPreGrade(exams, "GENERAL OBSERVATIONS", "TERM - 2", sub)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: "11px", marginBottom: "8px", lineHeight: "1.4" }}>
                <div>Attendance: <strong>{attT2.present ?? "—"}</strong> days out of <strong>{attT2.total ?? "—"}</strong></div>
                <div style={{ marginTop: "4px" }}>Remarks: <span style={{ color: "#7d0a0a", fontWeight: "bold" }}>{remT2}</span></div>
                {promotion_status && (
                  <div style={{ marginTop: "4px" }}>Promoted to Class: <span style={{ color: "#15803d", fontWeight: "bold" }}>{promotion_status}</span></div>
                )}
              </div>
              <div style={BS.sigGroup}>
                <span>Signature: Class Teacher</span>
                <span>Parent / Guardian</span>
                <span>Principal</span>
              </div>

              <div style={BS.footerBand}>
                NOTE : Parents will return this card to the school duly signed and retained after Annual Examination.
              </div>
            </div>
          </div>

          <div style={BS.foldLine} />

          {/* Right Side: Cover Page */}
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", paddingLeft: "15px" }}>
            <div style={BS.header}>
              <div style={BS.schoolName}>{school.name}</div>
              <div style={BS.schoolDetails}>
                {school.address}<br />
                {school.affiliation}<br />
                Phone: {school.phone} | Fax: +91 361 2475977 Ext 333<br />
                Email: {school.email} | Web: {school.website}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "center", margin: "20px 0" }}>
              <img id="preview-school-logo" src={schoolLogo} alt="School Logo" style={{ width: "90px", height: "90px", objectFit: "contain" }} />
            </div>

            <div style={{ textAlign: "center", marginBottom: "15px" }}>
              <h2 style={{ fontFamily: "'Georgia', serif", color: "#7d0a0a", fontSize: "24px", fontWeight: "bold", margin: 0 }}>PROGRESS REPORT</h2>
              <p style={{ color: "#7d0a0a", fontSize: "11px", fontWeight: "bold", margin: "4px 0" }}>Montessori Section ( MA to Pre1 )</p>
              <p style={{ fontSize: "12px", fontWeight: "bold", color: "#333", marginTop: "15px" }}>ACADEMIC YEAR: {session_name || "—"}</p>
            </div>

            {/* Student Details Card */}
            <div style={BS.studentBox}>
              <div style={BS.studentGridRow}>
                <span style={BS.studentLabel}>Student Code :</span>
                <div style={{ flex: 1 }}>
                  {String(si.st_code || "").padEnd(6, " ").split("").map((ch, i) => (
                    <div key={i} style={BS.codeBox}>{ch.trim()}</div>
                  ))}
                </div>
              </div>
              <div style={BS.studentGridRow}>
                <span style={BS.studentLabel}>Student Name :</span>
                <div style={BS.studentValBox}>{fmt(si.st_name)}</div>
              </div>
              <div style={BS.studentGridRow}>
                <span style={BS.studentLabel}>Class :</span>
                <div style={BS.studentValBox}>{fmt(si.st_class).toUpperCase()}</div>
              </div>
              <div style={BS.studentGridRow}>
                <span style={BS.studentLabel}>Section :</span>
                <div style={BS.studentValBox}>{fmt(si.st_sec).toUpperCase()}</div>
              </div>
              <div style={BS.studentGridRow}>
                <span style={BS.studentLabel}>Roll :</span>
                <div style={BS.studentValBox}>{fmt(si.roll_no || report.roll_number)}</div>
              </div>
              <div style={BS.studentGridRow}>
                <span style={BS.studentLabel}>Specimen Signature of Parent/Guardian :</span>
                <div style={BS.studentValBox}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ════ PAGE 2 SPREAD: Inside Evaluation Records (T1 Left, T2 Right) ════ */}
      <div style={BS.page}>
        {/* Grade Chart Header */}
        <div style={BS.gradeChart}>
          <span>GRADE CHART :</span>
          <span>A+ : EXCELLENT</span>
          <span>A : VERY GOOD</span>
          <span>B+ : GOOD</span>
          <span>B : AVERAGE</span>
          <span>C : SCOPE FOR IMPROVEMENT</span>
        </div>

        <div style={BS.spread}>
          {/* Left Side: Term 1 Evaluation Record */}
          <div>
            <div style={BS.band}>Evaluation Record : Term - 1</div>
            <table style={BS.table}>
              <thead>
                <tr>
                  <th style={BS.th}>AREAS OF ASSESSMENT</th>
                  <th style={{ ...BS.th, width: 60, textAlign: "center" }}>GRADE</th>
                </tr>
              </thead>
              <tbody>
                {evalSubjects.map((sub, idx) => (
                  <tr key={sub}>
                    <td style={BS.td}>{String(idx + 1).padStart(2, "0")}. {sub}</td>
                    <td style={{ ...BS.td, textAlign: "center" }}>
                      <div style={BS.gradeBox}>{getMAPreGrade(exams, "EVALUATION RECORD", "TERM - 1", sub)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={BS.foldLine} />

          {/* Right Side: Term 2 Evaluation Record */}
          <div style={{ paddingLeft: "15px" }}>
            <div style={BS.band}>Evaluation Record : Term - 2</div>
            <table style={BS.table}>
              <thead>
                <tr>
                  <th style={BS.th}>AREAS OF ASSESSMENT</th>
                  <th style={{ ...BS.th, width: 60, textAlign: "center" }}>GRADE</th>
                </tr>
              </thead>
              <tbody>
                {evalSubjects.map((sub, idx) => (
                  <tr key={sub}>
                    <td style={BS.td}>{String(idx + 1).padStart(2, "0")}. {sub}</td>
                    <td style={{ ...BS.td, textAlign: "center" }}>
                      <div style={BS.gradeBox}>{getMAPreGrade(exams, "EVALUATION RECORD", "TERM - 2", sub)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

const ReportCard1_8 = ({ report, school }) => {
  return (
    <div style={{ padding: 20, textAlign: "center", background: "#fff", border: "1px solid #ccc", minHeight: "400px" }}>
      <h4>Classes 1-8 Grade Card Placeholder</h4>
      <p>Group: {report.group}</p>
      <p>Student Name: {report.student_info?.st_name || "—"}</p>
      <p>Class: {report.student_info?.st_class || "—"}</p>
    </div>
  )
}

const getSubjectMarks11_12 = (report) => {
  const { exams = [], group = "" } = report
  let scholasticExam = exams.find(e => e.exam_name === "Retest Marks Based")
  if (!scholasticExam || !scholasticExam.subjects || scholasticExam.subjects.length === 0) {
    scholasticExam = exams.find(e => e.exam_name === "11-12-Annual Exam-Scholastic")
  }
  if (!scholasticExam || !scholasticExam.subjects || scholasticExam.subjects.length === 0) {
    if (group && group.toLowerCase() === "retest" && exams.length > 0) {
      scholasticExam = exams[0]
    }
  }
  
  if (!scholasticExam || !scholasticExam.subjects) return []
  
  return scholasticExam.subjects.map((sub, idx) => {
    const pracAssess = sub.assessments?.find(a => a.assessment_name === "Practical")
    const theoAssess = sub.assessments?.find(a => a.assessment_name === "Theory")
    
    const fullPract = pracAssess ? (pracAssess.assessment_max ?? 0) : 0
    const fullTheory = theoAssess ? (theoAssess.assessment_max ?? 0) : 0
    
    const obtainPract = pracAssess ? (pracAssess.sum_obtained !== null && pracAssess.sum_obtained !== undefined ? pracAssess.sum_obtained : "") : ""
    const obtainTheory = theoAssess ? (theoAssess.sum_obtained !== null && theoAssess.sum_obtained !== undefined ? theoAssess.sum_obtained : "") : ""
    
    const passPract = pracAssess ? (pracAssess.pass_mark !== undefined && pracAssess.pass_mark !== null ? pracAssess.pass_mark : (fullPract ? Math.round(fullPract * 0.3) : "")) : ""
    const passTheory = theoAssess ? (theoAssess.pass_mark !== undefined && theoAssess.pass_mark !== null ? theoAssess.pass_mark : (fullTheory ? Math.round(fullTheory * 0.33) : "")) : ""
    
    const obtainTotal = sub.term_total !== null && sub.term_total !== undefined ? sub.term_total : ""
    
    return {
      slno: idx + 1,
      name: sub.subject_name,
      code: sub.subject_code,
      full_pract: fullPract,
      full_theory: fullTheory,
      pass_pract: passPract,
      pass_theory: passTheory,
      obtain_pract: obtainPract,
      obtain_theory: obtainTheory,
      obtain_total: obtainTotal,
      class_average: sub.class_average
    }
  })
}

const calculatePercentages11_12 = (subjects, report = {}) => {
  const omp = report.overall_marks_percentage || {}
  
  if (omp.practical_percentage !== undefined || omp.theory_percentage !== undefined || omp.overall_percentage !== undefined) {
    return {
      practical: omp.practical_percentage !== null ? String(omp.practical_percentage) : "—",
      theory: omp.theory_percentage !== null ? String(omp.theory_percentage) : "—",
      overall: omp.overall_percentage !== null ? String(omp.overall_percentage) : "—"
    }
  }

  let totalFullPract = 0, totalObtainPract = 0
  let totalFullTheory = 0, totalObtainTheory = 0
  
  subjects.forEach(s => {
    if (s.full_pract) {
      totalFullPract += s.full_pract
      totalObtainPract += parseFloat(s.obtain_pract) || 0
    }
    if (s.full_theory) {
      totalFullTheory += s.full_theory
      totalObtainTheory += parseFloat(s.obtain_theory) || 0
    }
  })
  
  const practPct = totalFullPract ? Math.round((totalObtainPract / totalFullPract) * 100) : null
  const theoryPct = totalFullTheory ? Math.round((totalObtainTheory / totalFullTheory) * 100) : null
  const overallPct = (totalFullPract + totalFullTheory) ? Math.round(((totalObtainPract + totalObtainTheory) / (totalFullPract + totalFullTheory)) * 100) : null
  
  return {
    practical: practPct !== null ? `${practPct}%` : "—",
    theory: theoryPct !== null ? `${theoryPct}%` : "—",
    overall: overallPct !== null ? `${overallPct}%` : "—"
  }
}

const ReportCard11_12Practical = ({ report, school = SCHOOL }) => {
  const {
    student_info     = {},
    exams            = [],
    co_scholastic    = [],
    attendance       = {},
    remark           = "",
    session_name     = "",
  } = report

  const si = student_info
  const photoUrl = getPhotoUrl(si.profile_pic)
  const subjects = getSubjectMarks11_12(report)
  const pct = calculatePercentages11_12(subjects, report)

  // Sum calculations
  const getSumVal = (field) => {
    let sum = 0
    let hasVal = false
    subjects.forEach(s => {
      const val = parseFloat(s[field])
      if (!isNaN(val)) {
        sum += val
        hasVal = true
      }
    })
    return hasVal ? sum : "—"
  }

  const totalFullPract = getSumVal("full_pract")
  const totalFullTheory = getSumVal("full_theory")
  const totalObtainPract = getSumVal("obtain_pract")
  const totalObtainTheory = getSumVal("obtain_theory")
  const totalObtainTotal = getSumVal("obtain_total")

  const attPct = attendance.percentage ? `${Math.round(attendance.percentage)}%` : "—"

  const PS = {
    page: {
      fontFamily: "Arial, sans-serif",
      fontSize: 10,
      color: "#000",
      background: "#fff",
      padding: "10px",
      boxSizing: "border-box",
      border: "4px solid #3a7d44",
    },
    topBar: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: "11px",
      marginBottom: "4px",
    },
    schoolHeader: {
      background: "#7d0a0a",
      color: "#fff",
      textAlign: "center",
      padding: "4px 2px",
    },
    schoolEst: { fontSize: "10px", letterSpacing: "0.5px" },
    schoolName: {
      fontSize: "24px",
      fontWeight: "bold",
      fontFamily: "Georgia, serif",
      margin: "2px 0 0 0",
    },
    infoStrip: {
      background: "#fff",
      display: "flex",
      alignItems: "center",
      padding: "6px 10px",
      borderBottom: "1px solid #ddd",
      marginBottom: "6px",
    },
    logoBox: {
      width: "55px",
      height: "55px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    logoImg: {
      maxHeight: "100%",
      maxWidth: "100%",
      objectFit: "contain",
    },
    centerInfo: {
      flex: 1,
      textAlign: "center",
      fontSize: "10px",
      padding: "0 10px",
      lineHeight: "1.3",
    },
    affiliation: { fontWeight: "bold", fontSize: "10px", marginBottom: "2px" },
    redHeader: { color: "#7d0a0a", fontWeight: "600" },
    
    studentSection: {
      background: "#efefe9",
      padding: "6px 8px",
      marginBottom: "8px",
    },
    studentLabel: {
      background: "#7d0a0a",
      color: "#fff",
      display: "inline-block",
      padding: "2px 8px",
      fontWeight: "bold",
      fontSize: "10px",
      marginBottom: "6px",
    },
    studentGrid: {
      display: "flex",
      gap: "15px",
      alignItems: "stretch",
    },
    studentLeft: { flex: 1.2 },
    studentRight: { flex: 1 },
    studentFieldRow: { 
      marginBottom: "4px",
      display: "flex",
    },
    studentFieldLabel: {
      color: "#505050",
      minWidth: "100px",
    },
    studentFieldValue: {
      fontWeight: "bold",
      color: "#000",
      flex: 1,
      wordBreak: "break-word",
    },
    studentPhoto: {
      width: "80px",
      height: "100px",
      border: "3px solid white",
      boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
      background: "#e5e7eb",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "9px",
      color: "#9ca3af",
      overflow: "hidden",
    },
    
    titleBand: { 
      display: "flex", 
      marginTop: "6px",
      marginBottom: "8px",
    },
    titleLeft: { width: "40px", background: "#dfc794" },
    titleCenter: {
      flex: 1,
      background: "#4a2e18",
      color: "#fff",
      textAlign: "center",
      fontSize: "16px",
      fontWeight: "bold",
      letterSpacing: "1.5px",
      padding: "4px 0",
    },
    titleRight: { width: "40px", background: "#dfc794" },
    
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "10px",
      marginTop: "4px",
      marginBottom: "8px",
    },
    th: {
      border: "1px solid #555",
      padding: "3px 4px",
      textAlign: "center",
      background: "#7d0a0a",
      color: "#fff",
      fontWeight: "bold",
      verticalAlign: "middle",
    },
    td: {
      border: "1px solid #555",
      padding: "3px 4px",
      textAlign: "center",
      verticalAlign: "middle",
      background: "#ffffff",
    },
    totalRow: {
      background: "#020255",
      color: "#fff",
      fontWeight: "bold",
    },
    
    lowerGrid: {
      display: "grid",
      gridTemplateColumns: "1.1fr 1.2fr 1.1fr",
      gap: "8px",
      marginTop: "6px",
    },
    
    boxTitle: {
      background: "#7d0a0a",
      color: "#fff",
      padding: "3px 6px",
      fontSize: "9.5px",
      fontWeight: "bold",
      textAlign: "center",
    },
    boxBody: {
      border: "1px solid #555",
      padding: "5px",
      minHeight: "110px",
      background: "#fff",
    },
    
    pctRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "4px 8px",
      borderBottom: "1px solid #eee",
      fontSize: "10px",
    },
    
    remarks: {
      border: "1px solid #aaa",
      marginTop: "8px",
      padding: "6px 8px",
    },
    remarksTitle: { fontWeight: "bold", fontSize: "11px", marginBottom: "4px" },
    
    signatures: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      marginTop: "20px",
      textAlign: "center",
      fontSize: "10px",
    },
    sigLine: {
      borderTop: "1px solid #333",
      width: "140px",
      margin: "0 auto 4px",
    }
  }

  return (
    <div style={PS.page}>
      {/* Top Bar */}
      <div style={PS.topBar}>
        <span>Session : <b>{session_name || "—"}</b></span>
        <span>Print Date : {printDate}</span>
      </div>

      {/* School Header */}
      <div style={PS.schoolHeader}>
        <div style={PS.schoolEst}>{school.established}</div>
        <div style={PS.schoolName}>{school.name}</div>
      </div>

      {/* Info Strip */}
      <div style={PS.infoStrip}>
        <div style={PS.logoBox}>
          <img id="preview-cbse-logo" src={cbseLogo} alt="CBSE" style={PS.logoImg} />
        </div>
        <div style={PS.centerInfo}>
          <div style={PS.affiliation}>{school.affiliation}</div>
          <div><span style={PS.redHeader}>Address : </span>{school.address}</div>
          <div><span style={PS.redHeader}>Phone : </span>{school.phone}</div>
          <div>
            <span style={PS.redHeader}>Email : </span>{school.email} &nbsp; 
            <span style={PS.redHeader}>Website : </span>{school.website}
          </div>
        </div>
        <div style={PS.logoBox}>
          <img id="preview-school-logo" src={schoolLogo} alt="School Logo" style={PS.logoImg} />
        </div>
      </div>

      {/* Student Details */}
      <div style={PS.studentSection}>
        <div style={PS.studentLabel}>Student Details</div>
        <div style={PS.studentGrid}>
          <div style={PS.studentLeft}>
            <div style={PS.studentFieldRow}>
              <span style={PS.studentFieldLabel}>Student's Name :</span>
              <span style={PS.studentFieldValue}>{fmt(si.st_name)}</span>
            </div>
            <div style={PS.studentFieldRow}>
              <span style={PS.studentFieldLabel}>Father's Name :</span>
              <span style={PS.studentFieldValue}>{si.father_name ? `MR. ${si.father_name.toUpperCase()}` : "—"}</span>
            </div>
            <div style={PS.studentFieldRow}>
              <span style={PS.studentFieldLabel}>Mother's Name :</span>
              <span style={PS.studentFieldValue}>{si.mother_name ? `MS. ${si.mother_name.toUpperCase()}` : "—"}</span>
            </div>
            <div style={PS.studentFieldRow}>
              <span style={PS.studentFieldLabel}>Address :</span>
              <span style={PS.studentFieldValue}>{fmt(si.address)}</span>
            </div>
          </div>
          <div style={PS.studentRight}>
            <div style={PS.studentFieldRow}>
              <span style={PS.studentFieldLabel}>Code :</span>
              <span style={PS.studentFieldValue}>{fmt(si.st_code)}</span>
            </div>
            <div style={PS.studentFieldRow}>
              <span style={PS.studentFieldLabel}>Class :</span>
              <span style={PS.studentFieldValue}>{fmt(si.st_class).toUpperCase()}</span>
            </div>
            <div style={PS.studentFieldRow}>
              <span style={PS.studentFieldLabel}>Section :</span>
              <span style={PS.studentFieldValue}>{fmt(si.st_sec).toUpperCase()}</span>
            </div>
            <div style={PS.studentFieldRow}>
              <span style={PS.studentFieldLabel}>Date of Birth :</span>
              <span style={PS.studentFieldValue}>{fmtD(si.dob)}</span>
            </div>
          </div>
          <div style={PS.studentPhoto}>
            {photoUrl ? (
              <img src={photoUrl} alt="Student" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              "Photo"
            )}
          </div>
        </div>
      </div>

      {/* Title Band */}
      <div style={PS.titleBand}>
        <div style={PS.titleLeft} />
        <div style={PS.titleCenter}>ANNUAL EXAMINATION REPORT CARD</div>
        <div style={PS.titleRight} />
      </div>

      {/* Part 1 Scholastic Area */}
      <div style={{ fontWeight: "bold", fontSize: "11px", marginBottom: "4px" }}>PART 1 : SCHOLASTIC</div>
      
      <table style={PS.table}>
        <thead>
          <tr>
            <th rowSpan={2} style={{ ...PS.th, width: "40px" }}>SLNO</th>
            <th rowSpan={2} style={{ ...PS.th, textAlign: "left" }}>SUBJECT NAME</th>
            <th rowSpan={2} style={{ ...PS.th, width: "90px" }}>SUBJECT CODE</th>
            <th colSpan={2} style={PS.th}>FULL MARK</th>
            <th colSpan={2} style={PS.th}>PASS MARK</th>
            <th colSpan={3} style={PS.th}>MARK OBTAIN</th>
          </tr>
          <tr>
            <th style={{ ...PS.th, width: "50px", background: "#8d1a1a" }}>Practical</th>
            <th style={{ ...PS.th, width: "50px", background: "#8d1a1a" }}>Theory</th>
            <th style={{ ...PS.th, width: "50px", background: "#8d1a1a" }}>Practical</th>
            <th style={{ ...PS.th, width: "50px", background: "#8d1a1a" }}>Theory</th>
            <th style={{ ...PS.th, width: "50px", background: "#8d1a1a" }}>Practical</th>
            <th style={{ ...PS.th, width: "50px", background: "#8d1a1a" }}>Theory</th>
            <th style={{ ...PS.th, width: "50px", background: "#8d1a1a" }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {subjects.map((s, idx) => (
            <tr key={s.code}>
              <td style={PS.td}>{idx + 1}</td>
              <td style={{ ...PS.td, textAlign: "left", fontWeight: "bold" }}>{s.name}</td>
              <td style={PS.td}>{s.code}</td>
              <td style={PS.td}>{s.full_pract || "—"}</td>
              <td style={PS.td}>{s.full_theory || "—"}</td>
              <td style={PS.td}>{s.pass_pract || "—"}</td>
              <td style={PS.td}>{s.pass_theory || "—"}</td>
              <td style={{ ...PS.td, fontWeight: "bold" }}>{s.obtain_pract}</td>
              <td style={{ ...PS.td, fontWeight: "bold" }}>{s.obtain_theory}</td>
              <td style={{ ...PS.td, fontWeight: "bold", background: "#fef9c3" }}>{s.obtain_total}</td>
            </tr>
          ))}
          <tr style={PS.totalRow}>
            <td colSpan={3} style={{ ...PS.td, ...PS.totalRow, textAlign: "center" }}>TOTAL MARKS</td>
            <td style={{ ...PS.td, ...PS.totalRow }}>{totalFullPract}</td>
            <td style={{ ...PS.td, ...PS.totalRow }}>{totalFullTheory}</td>
            <td style={{ ...PS.td, ...PS.totalRow }}>—</td>
            <td style={{ ...PS.td, ...PS.totalRow }}>—</td>
            <td style={{ ...PS.td, ...PS.totalRow }}>{totalObtainPract}</td>
            <td style={{ ...PS.td, ...PS.totalRow }}>{totalObtainTheory}</td>
            <td style={{ ...PS.td, ...PS.totalRow, background: "#1e3b8b" }}>{totalObtainTotal}</td>
          </tr>
        </tbody>
      </table>

      {report.is_retest && report.previous_attempt_marks && (
        <div style={{
          background: "#ebdcb9",
          border: "1px solid #7d0a0a",
          padding: "8px 12px",
          marginBottom: "12px",
          fontWeight: "bold",
          fontSize: "11px",
          color: "#000",
          textAlign: "left"
        }}>
          {report.previous_attempt_marks}
        </div>
      )}

      {/* Part 2 Section */}
      <div style={{ fontWeight: "bold", fontSize: "11px", marginBottom: "4px" }}>PART 2 : CO-SCHOLASTIC</div>

      <div style={PS.lowerGrid}>
        {/* Co-scholastic */}
        <div>
          <div style={PS.boxTitle}>CO-SCHOLASTIC AREAS</div>
          <div style={PS.boxBody}>
            <div style={{ fontSize: "8.5px", marginBottom: "4px", fontWeight: "bold", textAlign: "center" }}>Grading Scale</div>
            <div style={{ display: "flex", gap: "6px", justifyContent: "center", marginBottom: "6px" }}>
              {["A1", "A2", "B1", "B2", "C1", "C2", "D1", "D2", "E"].map(g => (
                <div key={g} style={{
                  fontSize: "8px", border: "1px solid #7d0a0a", padding: "1px 2px", fontWeight: "bold", borderRadius: "1px", background: "#fef3c7"
                }}>{g}</div>
              ))}
            </div>
            <table style={{ width: "100%", fontSize: "9px" }}>
              <tbody>
                {co_scholastic.map(c => (
                  <tr key={c.subject_code}>
                    <td style={{ padding: "3px 0", borderBottom: "1px solid #eee" }}>{c.subject_name}</td>
                    <td style={{ padding: "3px 0", borderBottom: "1px solid #eee", textAlign: "right", fontWeight: "bold", color: "#7d0a0a" }}>{c.grade || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Subject wise average */}
        <div>
          <div style={PS.boxTitle}>Subject wise average score in Class {fmt(si.st_class).toUpperCase()}, Section {fmt(si.st_sec).toUpperCase()}</div>
          <div style={PS.boxBody}>
            <table style={{ width: "100%", fontSize: "9px" }}>
              <thead>
                <tr style={{ fontWeight: "bold", borderBottom: "1px solid #999" }}>
                  <th style={{ textAlign: "left", paddingBottom: "2px" }}>SUBJECTS</th>
                  <th style={{ textAlign: "right", paddingBottom: "2px" }}>Average</th>
                </tr>
              </thead>
              <tbody>
                {subjects.map(s => (
                  <tr key={s.code}>
                    <td style={{ padding: "3px 0", borderBottom: "1px solid #eee" }}>{s.name}</td>
                    <td style={{ padding: "3px 0", borderBottom: "1px solid #eee", textAlign: "right", fontWeight: "bold" }}>{s.class_average ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Marks & Attendance Percentages */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <div>
            <div style={PS.boxTitle}>MARKS PERCENTAGE</div>
            <div style={{ border: "1px solid #555", background: "#fff", fontSize: "9px" }}>
              <div style={PS.pctRow}><span>Practical:</span> <b>{pct.practical}</b></div>
              <div style={PS.pctRow}><span>Theory:</span> <b>{pct.theory}</b></div>
              <div style={{ ...PS.pctRow, background: "#f3f4f6", fontWeight: "bold" }}><span>Overall Percentage:</span> <b>{pct.overall}</b></div>
            </div>
          </div>
          <div>
            <div style={PS.boxTitle}>ATTENDANCE PERCENTAGE</div>
            <div style={{ border: "1px solid #555", background: "#fff", fontSize: "9px" }}>
              <div style={{ ...PS.pctRow, textAlign: "center", display: "block", fontSize: "12px", fontWeight: "bold", color: "#15803d", padding: "6px 0" }}>{attPct}</div>
              <div style={{ fontSize: "8.5px", background: "#fef3c7", padding: "3px", textAlign: "center", borderTop: "1px solid #eee", fontWeight: "bold" }}>Min Attendance Required : {attendance?.min_required || "75%"}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Remarks */}
      <div style={PS.remarks}>
        <div style={PS.remarksTitle}>CLASS TEACHER'S REMARKS</div>
        <div style={{ minHeight: "35px", fontSize: "10.5px", fontStyle: "italic", color: "#555" }}>
          {remark || "No remarks entered."}
        </div>
      </div>

      {/* Signatures */}
      <div style={PS.signatures}>
        <div>
          <div style={{ height: "20px" }} />
          <div style={PS.sigLine} />
          <div><b>Signature of Guardian</b></div>
        </div>
        <div>
          <div style={{ height: "20px" }} />
          <div style={PS.sigLine} />
          <div><b>Signature of Class Teacher</b></div>
        </div>
        <div>
          <div style={{ height: "20px" }} />
          <div style={PS.sigLine} />
          <div><b>Signature of Principal</b></div>
        </div>
      </div>
    </div>
  )
}

const getSubjectMarks11_12WithoutPrac = (report) => {
  const { exams = [] } = report
  let scholasticExam = exams.find(e => e.exam_name === "Retest Marks Based")
  if (!scholasticExam || !scholasticExam.subjects || scholasticExam.subjects.length === 0) {
    scholasticExam = exams.find(e => e.exam_name.includes("without_prac") || e.exam_name.includes("Scholastic")) || exams[0]
  }
  
  if (!scholasticExam || !scholasticExam.subjects) return []
  
  return scholasticExam.subjects.map((sub, idx) => {
    const theoAssess = sub.assessments?.find(a => a.assessment_name === "Theory") || sub.assessments?.[0]
    const fullMark = sub.term_max ?? (theoAssess ? (theoAssess.assessment_max ?? 0) : 0)
    
    const isAbs = sub.assessments?.some(a => a.sum_obtained === "AB" || a.normalized_score === "AB")
    const obtainMark = isAbs ? "AB" : (sub.term_total !== null && sub.term_total !== undefined ? sub.term_total : "—")
    
    const passMark = theoAssess ? (theoAssess.pass_mark !== undefined && theoAssess.pass_mark !== null ? theoAssess.pass_mark : (fullMark ? Math.round(fullMark * 0.33) : "")) : ""
    const percentage = typeof obtainMark === "number" && fullMark ? `${Math.round((obtainMark / fullMark) * 100)}%` : "—"
    
    return {
      slno: idx + 1,
      name: sub.subject_name,
      code: sub.subject_code,
      full_mark: fullMark,
      pass_mark: passMark,
      obtain_mark: obtainMark,
      percentage: percentage
    }
  })
}

const ReportCard11_12 = ({ report, school = SCHOOL }) => {
  const {
    student_info     = {},
    exams            = [],
    attendance       = {},
    remark           = "",
    session_name     = "",
  } = report

  const si = student_info
  const photoUrl = getPhotoUrl(si.profile_pic)
  const subjects = getSubjectMarks11_12WithoutPrac(report)
  const examName = exams[0]?.exam_name || "HALF YEARLY REPORT CARD (SCIENCE STREAM)"

  // Sum calculations
  let totalFull = 0
  let totalObtain = 0
  let hasObtainVal = false

  subjects.forEach(s => {
    totalFull += s.full_mark || 0
    const val = parseFloat(s.obtain_mark)
    if (!isNaN(val)) {
      totalObtain += val
      hasObtainVal = true
    }
  })

  const totalObtainDisplay = hasObtainVal ? totalObtain : "—"
  const overallPct = totalFull && hasObtainVal ? `${Math.round((totalObtain / totalFull) * 100)}%` : "—"
  const attPct = attendance.percentage ? `${Math.round(attendance.percentage)}%` : "—"

  const US = {
    page: {
      fontFamily: "Arial, sans-serif",
      fontSize: 10.5,
      color: "#000",
      background: "#fff",
      padding: "12px",
      boxSizing: "border-box",
      border: "4px solid #3a7d44",
    },
    topBar: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: "11px",
      marginBottom: "4px",
    },
    schoolHeader: {
      background: "#7d0a0a",
      color: "#fff",
      textAlign: "center",
      padding: "4px 2px",
    },
    schoolEst: { fontSize: "10px", letterSpacing: "0.5px" },
    schoolName: {
      fontSize: "24px",
      fontWeight: "bold",
      fontFamily: "Georgia, serif",
      margin: "2px 0 0 0",
    },
    infoStrip: {
      background: "#fff",
      display: "flex",
      alignItems: "center",
      padding: "6px 10px",
      borderBottom: "1px solid #ddd",
      marginBottom: "6px",
    },
    logoBox: {
      width: "55px",
      height: "55px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    logoImg: {
      maxHeight: "100%",
      maxWidth: "100%",
      objectFit: "contain",
    },
    centerInfo: {
      flex: 1,
      textAlign: "center",
      fontSize: "10px",
      padding: "0 10px",
      lineHeight: "1.3",
    },
    affiliation: { fontWeight: "bold", fontSize: "10px", marginBottom: "2px" },
    redHeader: { color: "#7d0a0a", fontWeight: "600" },
    
    studentSection: {
      background: "#efefe9",
      padding: "6px 8px",
      marginBottom: "8px",
    },
    studentLabel: {
      background: "#7d0a0a",
      color: "#fff",
      display: "inline-block",
      padding: "2px 8px",
      fontWeight: "bold",
      fontSize: "10px",
      marginBottom: "6px",
    },
    studentGrid: {
      display: "flex",
      gap: "15px",
      alignItems: "stretch",
    },
    studentLeft: { flex: 1.2 },
    studentRight: { flex: 1 },
    studentFieldRow: { 
      marginBottom: "4px",
      display: "flex",
    },
    studentFieldLabel: {
      color: "#505050",
      minWidth: "100px",
    },
    studentFieldValue: {
      fontWeight: "bold",
      color: "#000",
      flex: 1,
      wordBreak: "break-word",
    },
    studentPhoto: {
      width: "80px",
      height: "100px",
      border: "3px solid white",
      boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
      background: "#e5e7eb",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "9px",
      color: "#9ca3af",
      overflow: "hidden",
    },
    
    examTitle: {
      textAlign: "center",
      fontFamily: "Georgia, serif",
      color: "#003366",
      fontSize: "20px",
      fontWeight: "bold",
      margin: "18px 0 14px 0",
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      lineHeight: "1.3",
    },
    
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "11px",
      marginTop: "4px",
      marginBottom: "12px",
    },
    th: {
      border: "1px solid #555",
      padding: "5px 6px",
      textAlign: "center",
      background: "#7d0a0a",
      color: "#fff",
      fontWeight: "bold",
      fontSize: "11.5px",
    },
    td: {
      border: "1px solid #555",
      padding: "5px 6px",
      textAlign: "center",
      verticalAlign: "middle",
      background: "#ffffff",
    },
    totalRow: {
      background: "#020255",
      color: "#fff",
      fontWeight: "bold",
    },
    
    lowerGrid: {
      display: "grid",
      gridTemplateColumns: "2.2fr 1fr",
      gap: "10px",
      marginTop: "8px",
    },
    boxTitle: {
      background: "#7d0a0a",
      color: "#fff",
      padding: "4px 6px",
      fontSize: "10px",
      fontWeight: "bold",
      textAlign: "center",
    },
    boxBody: {
      border: "1px solid #555",
      padding: "8px",
      minHeight: "75px",
      background: "#fff",
    },
    
    signatures: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      marginTop: "30px",
      textAlign: "center",
      fontSize: "10.5px",
    },
    sigLine: {
      borderTop: "1px solid #333",
      width: "140px",
      margin: "0 auto 4px",
    }
  }

  return (
    <div style={US.page}>
      {/* Top Bar */}
      <div style={US.topBar}>
        <span>Session : <b>{session_name || "—"}</b></span>
        <span>SENIOR SECONDARY</span>
        <span>Print Date : {printDate}</span>
      </div>

      {/* School Header */}
      <div style={US.schoolHeader}>
        <div style={US.schoolEst}>{school.established}</div>
        <div style={US.schoolName}>{school.name}</div>
      </div>

      {/* Info Strip */}
      <div style={US.infoStrip}>
        <div style={US.logoBox}>
          <img id="preview-cbse-logo" src={cbseLogo} alt="CBSE" style={US.logoImg} />
        </div>
        <div style={US.centerInfo}>
          <div style={US.affiliation}>{school.affiliation}</div>
          <div><span style={US.redHeader}>Address : </span>{school.address}</div>
          <div><span style={US.redHeader}>Phone : </span>{school.phone}</div>
          <div>
            <span style={US.redHeader}>Email : </span>{school.email} &nbsp; 
            <span style={US.redHeader}>Website : </span>{school.website}
          </div>
        </div>
        <div style={US.logoBox}>
          <img id="preview-school-logo" src={schoolLogo} alt="School Logo" style={US.logoImg} />
        </div>
      </div>

      {/* Student Details */}
      <div style={US.studentSection}>
        <div style={US.studentLabel}>Student Details</div>
        <div style={US.studentGrid}>
          <div style={US.studentLeft}>
            <div style={US.studentFieldRow}>
              <span style={US.studentFieldLabel}>Student's Name :</span>
              <span style={US.studentFieldValue}>{fmt(si.st_name)}</span>
            </div>
            <div style={US.studentFieldRow}>
              <span style={US.studentFieldLabel}>Father's Name :</span>
              <span style={US.studentFieldValue}>{si.father_name ? `MR. ${si.father_name.toUpperCase()}` : "—"}</span>
            </div>
            <div style={US.studentFieldRow}>
              <span style={US.studentFieldLabel}>Mother's Name :</span>
              <span style={US.studentFieldValue}>{si.mother_name ? `MS. ${si.mother_name.toUpperCase()}` : "—"}</span>
            </div>
            <div style={US.studentFieldRow}>
              <span style={US.studentFieldLabel}>Address :</span>
              <span style={US.studentFieldValue}>{fmt(si.address)}</span>
            </div>
          </div>
          <div style={US.studentRight}>
            <div style={US.studentFieldRow}>
              <span style={US.studentFieldLabel}>Code :</span>
              <span style={US.studentFieldValue}>{fmt(si.st_code)}</span>
            </div>
            <div style={US.studentFieldRow}>
              <span style={US.studentFieldLabel}>Class :</span>
              <span style={US.studentFieldValue}>{fmt(si.st_class).toUpperCase()}</span>
            </div>
            <div style={US.studentFieldRow}>
              <span style={US.studentFieldLabel}>Section :</span>
              <span style={US.studentFieldValue}>{fmt(si.st_sec).toUpperCase()}</span>
            </div>
            <div style={US.studentFieldRow}>
              <span style={US.studentFieldLabel}>Date of Birth :</span>
              <span style={US.studentFieldValue}>{fmtD(si.dob)}</span>
            </div>
          </div>
          <div style={US.studentPhoto}>
            {photoUrl ? (
              <img src={photoUrl} alt="Student" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              "Photo"
            )}
          </div>
        </div>
      </div>

      {/* Exam Title */}
      <div style={US.examTitle}>{examName}</div>

      {/* Table */}
      <table style={US.table}>
        <thead>
          <tr>
            <th style={{ ...US.th, textAlign: "left" }}>SUBJECTS</th>
            <th style={{ ...US.th, width: "100px" }}>FULL MARKS</th>
            <th style={{ ...US.th, width: "100px" }}>PASS MARKS</th>
            <th style={{ ...US.th, width: "120px" }}>MARKS OBTAINED</th>
            <th style={{ ...US.th, width: "120px" }}>PERCENTAGE</th>
          </tr>
        </thead>
        <tbody>
          {subjects.map((s) => (
            <tr key={s.code}>
              <td style={{ ...US.td, textAlign: "left", fontWeight: "bold" }}>
                {s.slno}) {s.name} &nbsp;[{s.code}]
              </td>
              <td style={US.td}>{s.full_mark}</td>
              <td style={US.td}>{s.pass_mark}</td>
              <td style={{ ...US.td, fontWeight: "bold" }}>{s.obtain_mark}</td>
              <td style={{ ...US.td, fontWeight: "bold", background: "#f5f5f5" }}>{s.percentage}</td>
            </tr>
          ))}
          <tr style={US.totalRow}>
            <td style={{ ...US.td, ...US.totalRow, textAlign: "left" }}>TOTAL MARKS</td>
            <td style={{ ...US.td, ...US.totalRow }}>{totalFull}</td>
            <td style={{ ...US.td, ...US.totalRow }}>—</td>
            <td style={{ ...US.td, ...US.totalRow }}>{totalObtainDisplay}</td>
            <td style={{ ...US.td, ...US.totalRow, background: "#1e3b8b" }}>{overallPct}</td>
          </tr>
        </tbody>
      </table>

      {report.is_retest && report.previous_attempt_marks && (
        <div style={{
          background: "#ebdcb9",
          border: "1px solid #7d0a0a",
          padding: "8px 12px",
          marginBottom: "12px",
          fontWeight: "bold",
          fontSize: "11px",
          color: "#000",
          textAlign: "left"
        }}>
          {report.previous_attempt_marks}
        </div>
      )}

      {/* Lower Section */}
      <div style={US.lowerGrid}>
        <div>
          <div style={{ ...US.boxTitle, textAlign: "left" }}>CLASS TEACHER'S REMARK</div>
          <div style={US.boxBody}>
            <div style={{ fontSize: "11px", fontStyle: "italic", color: "#555" }}>
              {remark || "No remarks entered."}
            </div>
          </div>
        </div>
        <div>
          <div style={US.boxTitle}>ATTENDANCE PERCENTAGE</div>
          <div style={{ ...US.boxBody, display: "flex", flexDirection: "column", justifyContent: "space-between", padding: 0 }}>
            <div style={{ textAlign: "center", fontSize: "14px", fontWeight: "bold", color: "#15803d", padding: "10px 0" }}>
              {attPct}
            </div>
            <div style={{ fontSize: "9px", background: "#fef3c7", padding: "4px", textAlign: "center", fontWeight: "bold", borderTop: "1px solid #555" }}>
              Min Attendance Required : {attendance?.min_required || "75%"}
            </div>
          </div>
        </div>
      </div>

      {/* Signatures */}
      <div style={US.signatures}>
        <div>
          <div style={{ height: "20px" }} />
          <div style={US.sigLine} />
          <div><b>Signature of Parent</b></div>
        </div>
        <div>
          <div style={{ height: "20px" }} />
          <div style={US.sigLine} />
          <div><b>Signature of Class Teacher</b></div>
        </div>
        <div>
          <div style={{ height: "20px" }} />
          <div style={US.sigLine} />
          <div><b>Signature of Principal</b></div>
        </div>
      </div>
    </div>
  )
}

const ReportCardRetest = ({ report, school = SCHOOL }) => {
  const {
    student_info     = {},
    exams            = [],
    co_scholastic    = [],
    attendance       = {},
    remark           = "",
    session_name     = "",
  } = report

  const si = student_info
  const photoUrl = getPhotoUrl(si.profile_pic)
  const subjects = getSubjectMarks11_12(report)
  const pct = calculatePercentages11_12(subjects, report)

  // Sum calculations
  const getSumVal = (field) => {
    let sum = 0
    let hasVal = false
    subjects.forEach(s => {
      const val = parseFloat(s[field])
      if (!isNaN(val)) {
        sum += val
        hasVal = true
      }
    })
    return hasVal ? sum : "—"
  }

  const totalFullPract = getSumVal("full_pract")
  const totalFullTheory = getSumVal("full_theory")
  const totalObtainPract = getSumVal("obtain_pract")
  const totalObtainTheory = getSumVal("obtain_theory")
  const totalObtainTotal = getSumVal("obtain_total")

  const attPct = attendance.percentage ? `${Math.round(attendance.percentage)}%` : "—"

  const PS = {
    page: {
      fontFamily: "Arial, sans-serif",
      fontSize: 10,
      color: "#000",
      background: "#fff",
      padding: "10px",
      border: "3px double #3a5f43",
      maxWidth: "800px",
      margin: "0 auto",
      position: "relative",
    },
    topBar: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: "11px",
      fontWeight: "bold",
      marginBottom: "6px",
    },
    schoolHeader: {
      background: "#7d0a0a",
      color: "#fff",
      padding: "6px 0",
      textAlign: "center",
      marginBottom: "6px",
      position: "relative",
    },
    schoolEst: {
      fontSize: "9px",
      fontWeight: "normal",
      marginBottom: "2px",
    },
    schoolName: {
      fontSize: "20px",
      fontWeight: "bold",
      letterSpacing: "1px",
    },
    infoStrip: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      borderBottom: "1px solid #ccc",
      paddingBottom: "6px",
      marginBottom: "6px",
    },
    logoBox: {
      width: "50px",
      height: "50px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    logoImg: {
      maxWidth: "100%",
      maxHeight: "100%",
      objectFit: "contain",
    },
    centerInfo: {
      textAlign: "center",
      fontSize: "9.5px",
      lineHeight: "1.3",
      flex: 1,
    },
    affiliation: {
      fontWeight: "bold",
      fontSize: "10px",
      marginBottom: "2px",
    },
    redHeader: {
      color: "#7d0a0a",
      fontWeight: "bold",
    },
    studentSection: {
      background: "#f7f7f1",
      padding: "6px 8px",
      marginBottom: "8px",
    },
    studentLabel: {
      background: "#7d0a0a",
      color: "#fff",
      padding: "2px 6px",
      fontSize: "9.5px",
      fontWeight: "bold",
      display: "inline-block",
      marginBottom: "6px",
    },
    studentGrid: {
      display: "flex",
      gap: "10px",
    },
    studentLeft: {
      flex: 1.2,
      display: "flex",
      flexDirection: "column",
      gap: "3px",
    },
    studentRight: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: "3px",
    },
    studentFieldRow: {
      display: "flex",
      fontSize: "10px",
    },
    studentFieldLabel: {
      width: "95px",
      color: "#555",
    },
    studentFieldValue: {
      fontWeight: "bold",
      flex: 1,
    },
    studentPhoto: {
      width: "80px",
      height: "100px",
      border: "3px solid white",
      boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
      background: "#e5e7eb",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "9px",
      color: "#9ca3af",
      overflow: "hidden",
    },
    titleBand: { 
      display: "flex", 
      marginTop: "6px",
      marginBottom: "8px",
    },
    titleLeft: { width: "40px", background: "#dfc794" },
    titleCenter: {
      flex: 1,
      background: "#4a2e18",
      color: "#fff",
      textAlign: "center",
      fontSize: "16px",
      fontWeight: "bold",
      letterSpacing: "1.5px",
      padding: "4px 0",
    },
    titleRight: { width: "40px", background: "#dfc794" },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "10px",
      marginTop: "4px",
      marginBottom: "8px",
    },
    th: {
      border: "1px solid #555",
      padding: "3px 4px",
      textAlign: "center",
      background: "#7d0a0a",
      color: "#fff",
      fontWeight: "bold",
      verticalAlign: "middle",
    },
    td: {
      border: "1px solid #555",
      padding: "3px 4px",
      textAlign: "center",
      verticalAlign: "middle",
      background: "#ffffff",
    },
    totalRow: {
      background: "#020255",
      color: "#fff",
      fontWeight: "bold",
    },
    lowerGrid: {
      display: "grid",
      gridTemplateColumns: "1.1fr 1.2fr 1.1fr",
      gap: "8px",
      marginTop: "6px",
    },
    boxTitle: {
      background: "#7d0a0a",
      color: "#fff",
      padding: "3px 6px",
      fontSize: "9.5px",
      fontWeight: "bold",
      textAlign: "center",
    },
    boxBody: {
      border: "1px solid #555",
      padding: "5px",
      minHeight: "110px",
      background: "#fff",
    },
    pctRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "4px 8px",
      borderBottom: "1px solid #eee",
      fontSize: "10px",
    },
    remarks: {
      border: "1px solid #aaa",
      marginTop: "8px",
      padding: "6px 8px",
    },
    remarksTitle: { fontWeight: "bold", fontSize: "11px", marginBottom: "4px" },
    signatures: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      marginTop: "20px",
      textAlign: "center",
      fontSize: "10px",
    },
    sigLine: {
      borderTop: "1px solid #333",
      width: "140px",
      margin: "0 auto 4px",
    }
  }

  return (
    <div style={PS.page}>
      {/* Top Bar */}
      <div style={PS.topBar}>
        <span>Session : <b>{session_name || "—"}</b></span>
        <span>Print Date : {printDate}</span>
      </div>

      {/* School Header */}
      <div style={PS.schoolHeader}>
        <div style={PS.schoolEst}>{school.established}</div>
        <div style={PS.schoolName}>{school.name}</div>
      </div>

      {/* Info Strip */}
      <div style={PS.infoStrip}>
        <div style={PS.logoBox}>
          <img id="preview-cbse-logo" src={cbseLogo} alt="CBSE" style={PS.logoImg} />
        </div>
        <div style={PS.centerInfo}>
          <div style={PS.affiliation}>{school.affiliation}</div>
          <div><span style={PS.redHeader}>Address : </span>{school.address}</div>
          <div><span style={PS.redHeader}>Phone : </span>{school.phone}</div>
          <div>
            <span style={PS.redHeader}>Email : </span>{school.email} &nbsp; 
            <span style={PS.redHeader}>Website : </span>{school.website}
          </div>
        </div>
        <div style={PS.logoBox}>
          <img id="preview-school-logo" src={schoolLogo} alt="School Logo" style={PS.logoImg} />
        </div>
      </div>

      {/* Student Details */}
      <div style={PS.studentSection}>
        <div style={PS.studentLabel}>Student Details</div>
        <div style={PS.studentGrid}>
          <div style={PS.studentLeft}>
            <div style={PS.studentFieldRow}>
              <span style={PS.studentFieldLabel}>Student's Name :</span>
              <span style={PS.studentFieldValue}>{fmt(si.st_name)}</span>
            </div>
            <div style={PS.studentFieldRow}>
              <span style={PS.studentFieldLabel}>Father's Name :</span>
              <span style={PS.studentFieldValue}>{si.father_name ? `MR. ${si.father_name.toUpperCase()}` : "—"}</span>
            </div>
            <div style={PS.studentFieldRow}>
              <span style={PS.studentFieldLabel}>Mother's Name :</span>
              <span style={PS.studentFieldValue}>{si.mother_name ? `MS. ${si.mother_name.toUpperCase()}` : "—"}</span>
            </div>
            <div style={PS.studentFieldRow}>
              <span style={PS.studentFieldLabel}>Address :</span>
              <span style={PS.studentFieldValue}>{fmt(si.address)}</span>
            </div>
          </div>
          <div style={PS.studentRight}>
            <div style={PS.studentFieldRow}>
              <span style={PS.studentFieldLabel}>Code :</span>
              <span style={PS.studentFieldValue}>{fmt(si.st_code)}</span>
            </div>
            <div style={PS.studentFieldRow}>
              <span style={PS.studentFieldLabel}>Class :</span>
              <span style={PS.studentFieldValue}>{fmt(si.st_class).toUpperCase()}</span>
            </div>
            <div style={PS.studentFieldRow}>
              <span style={PS.studentFieldLabel}>Section :</span>
              <span style={PS.studentFieldValue}>{fmt(si.st_sec).toUpperCase()}</span>
            </div>
            <div style={PS.studentFieldRow}>
              <span style={PS.studentFieldLabel}>Date of Birth :</span>
              <span style={PS.studentFieldValue}>{fmtD(si.dob)}</span>
            </div>
          </div>
          <div style={PS.studentPhoto}>
            {photoUrl ? (
              <img src={photoUrl} alt="Student" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              "Photo"
            )}
          </div>
        </div>
      </div>

      {/* Title Band */}
      <div style={PS.titleBand}>
        <div style={PS.titleLeft} />
        <div style={PS.titleCenter}>RETEST EXAMINATION REPORT CARD</div>
        <div style={PS.titleRight} />
      </div>

      {/* Part 1 Scholastic Area */}
      <div style={{ fontWeight: "bold", fontSize: "11px", marginBottom: "4px" }}>PART 1 : SCHOLASTIC</div>
      
      <table style={PS.table}>
        <thead>
          <tr>
            <th rowSpan={2} style={{ ...PS.th, width: "40px" }}>SLNO</th>
            <th rowSpan={2} style={{ ...PS.th, textAlign: "left" }}>SUBJECT NAME</th>
            <th rowSpan={2} style={{ ...PS.th, width: "90px" }}>SUBJECT CODE</th>
            <th colSpan={2} style={PS.th}>FULL MARK</th>
            <th colSpan={2} style={PS.th}>PASS MARK</th>
            <th colSpan={3} style={PS.th}>MARK OBTAIN</th>
          </tr>
          <tr>
            <th style={{ ...PS.th, width: "50px", background: "#8d1a1a" }}>Practical</th>
            <th style={{ ...PS.th, width: "50px", background: "#8d1a1a" }}>Theory</th>
            <th style={{ ...PS.th, width: "50px", background: "#8d1a1a" }}>Practical</th>
            <th style={{ ...PS.th, width: "50px", background: "#8d1a1a" }}>Theory</th>
            <th style={{ ...PS.th, width: "50px", background: "#8d1a1a" }}>Practical</th>
            <th style={{ ...PS.th, width: "50px", background: "#8d1a1a" }}>Theory</th>
            <th style={{ ...PS.th, width: "50px", background: "#8d1a1a" }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {subjects.map((s, idx) => (
            <tr key={s.code}>
              <td style={PS.td}>{idx + 1}</td>
              <td style={{ ...PS.td, textAlign: "left", fontWeight: "bold" }}>{s.name}</td>
              <td style={PS.td}>{s.code}</td>
              <td style={PS.td}>{s.full_pract || "—"}</td>
              <td style={PS.td}>{s.full_theory || "—"}</td>
              <td style={PS.td}>{s.pass_pract || "—"}</td>
              <td style={PS.td}>{s.pass_theory || "—"}</td>
              <td style={{ ...PS.td, fontWeight: "bold" }}>{s.obtain_pract}</td>
              <td style={{ ...PS.td, fontWeight: "bold" }}>{s.obtain_theory}</td>
              <td style={{ ...PS.td, fontWeight: "bold", background: "#fef9c3" }}>{s.obtain_total}</td>
            </tr>
          ))}
          <tr style={PS.totalRow}>
            <td colSpan={3} style={{ ...PS.td, ...PS.totalRow, textAlign: "center" }}>TOTAL MARKS</td>
            <td style={{ ...PS.td, ...PS.totalRow }}>{totalFullPract}</td>
            <td style={{ ...PS.td, ...PS.totalRow }}>{totalFullTheory}</td>
            <td style={{ ...PS.td, ...PS.totalRow }}>—</td>
            <td style={{ ...PS.td, ...PS.totalRow }}>—</td>
            <td style={{ ...PS.td, ...PS.totalRow }}>{totalObtainPract}</td>
            <td style={{ ...PS.td, ...PS.totalRow }}>{totalObtainTheory}</td>
            <td style={{ ...PS.td, ...PS.totalRow, background: "#1e3b8b" }}>{totalObtainTotal}</td>
          </tr>
        </tbody>
      </table>

      {/* Previous Attempt Marks of Failed */}
      {report.previous_attempt_marks && (
        <div style={{
          background: "#ebdcb9",
          border: "1px solid #7d0a0a",
          padding: "8px 12px",
          marginBottom: "12px",
          fontWeight: "bold",
          fontSize: "11px",
          color: "#000",
          textAlign: "left"
        }}>
          {report.previous_attempt_marks}
        </div>
      )}

      {/* Part 2 Section */}
      <div style={{ fontWeight: "bold", fontSize: "11px", marginBottom: "4px" }}>PART 2 : CO-SCHOLASTIC</div>

      <div style={PS.lowerGrid}>
        {/* Co-scholastic */}
        <div>
          <div style={PS.boxTitle}>CO-SCHOLASTIC AREAS</div>
          <div style={PS.boxBody}>
            <div style={{ fontSize: "8.5px", marginBottom: "4px", fontWeight: "bold", textAlign: "center" }}>Grading Scale</div>
            <div style={{ display: "flex", gap: "6px", justifyContent: "center", marginBottom: "6px" }}>
              {["A1", "A2", "B1", "B2", "C1", "C2", "D1", "D2", "E"].map(g => (
                <div key={g} style={{
                  fontSize: "8px", border: "1px solid #7d0a0a", padding: "1px 2px", fontWeight: "bold", borderRadius: "1px", background: "#fef3c7"
                }}>{g}</div>
              ))}
            </div>
            <table style={{ width: "100%", fontSize: "9px" }}>
              <tbody>
                {co_scholastic.map(c => (
                  <tr key={c.subject_code}>
                    <td style={{ padding: "3px 0", borderBottom: "1px solid #eee" }}>{c.subject_name}</td>
                    <td style={{ padding: "3px 0", borderBottom: "1px solid #eee", textAlign: "right", fontWeight: "bold", color: "#7d0a0a" }}>{c.grade || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Subject wise average */}
        <div>
          <div style={PS.boxTitle}>Subject wise average score in Class {fmt(si.st_class).toUpperCase()}, Section {fmt(si.st_sec).toUpperCase()}</div>
          <div style={PS.boxBody}>
            <table style={{ width: "100%", fontSize: "9px" }}>
              <thead>
                <tr style={{ fontWeight: "bold", borderBottom: "1px solid #999" }}>
                  <th style={{ textAlign: "left", paddingBottom: "2px" }}>SUBJECTS</th>
                  <th style={{ textAlign: "right", paddingBottom: "2px" }}>Average</th>
                </tr>
              </thead>
              <tbody>
                {subjects.map(s => (
                  <tr key={s.code}>
                    <td style={{ padding: "3px 0", borderBottom: "1px solid #eee" }}>{s.name}</td>
                    <td style={{ padding: "3px 0", borderBottom: "1px solid #eee", textAlign: "right", fontWeight: "bold" }}>{s.class_average ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Marks & Attendance Percentages */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <div>
            <div style={PS.boxTitle}>MARKS PERCENTAGE</div>
            <div style={{ border: "1px solid #555", background: "#fff", fontSize: "9px" }}>
              <div style={PS.pctRow}><span>Practical:</span> <b>{pct.practical}</b></div>
              <div style={PS.pctRow}><span>Theory:</span> <b>{pct.theory}</b></div>
              <div style={{ ...PS.pctRow, background: "#f3f4f6", fontWeight: "bold" }}><span>Overall Percentage:</span> <b>{pct.overall}</b></div>
            </div>
          </div>
          <div>
            <div style={PS.boxTitle}>ATTENDANCE PERCENTAGE</div>
            <div style={{ border: "1px solid #555", background: "#fff", fontSize: "9px" }}>
              <div style={{ ...PS.pctRow, textAlign: "center", display: "block", fontSize: "12px", fontWeight: "bold", color: "#15803d", padding: "6px 0" }}>{attPct}</div>
              <div style={{ fontSize: "8.5px", background: "#fef3c7", padding: "3px", textAlign: "center", borderTop: "1px solid #eee", fontWeight: "bold" }}>Min Attendance Required : {attendance?.min_required || "75%"}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Remarks */}
      <div style={PS.remarks}>
        <div style={PS.remarksTitle}>CLASS TEACHER'S REMARKS</div>
        <div style={{ minHeight: "35px", fontSize: "10.5px", fontStyle: "italic", color: "#555" }}>
          {remark || "No remarks entered."}
        </div>
      </div>

      {/* Signatures */}
      <div style={PS.signatures}>
        <div>
          <div style={{ height: "20px" }} />
          <div style={PS.sigLine} />
          <div><b>Signature of Guardian</b></div>
        </div>
        <div>
          <div style={{ height: "20px" }} />
          <div style={PS.sigLine} />
          <div><b>Signature of Class Teacher</b></div>
        </div>
        <div>
          <div style={{ height: "20px" }} />
          <div style={PS.sigLine} />
          <div><b>Signature of Principal</b></div>
        </div>
      </div>
    </div>
  )
}

// ── Group-Specific PDF Draw Page Functions (Placeholders) ──

async function pDrawPageGroup9(doc, report, school, cbseImg, schoolImg, studentImg) {
  const {
    student_info     = {},
    exams            = [],
    grand_total      = [],
    co_scholastic    = [],
    subject_avg      = [],
    attendance       = {},
    remark           = "",
    session_name     = "",
    promotion_status = "",
  } = report

  let y = M

  y = pTopBar(doc, session_name, y)
  y = pSchoolHeader(doc, school, y)
  y = pInfoStrip(doc, school, cbseImg, schoolImg, y)
  y = pStudentDetails(doc, student_info, studentImg, y)
  y = pTitleBand(doc, y)
  y = pScholasticHeader(doc, y)
  y = pGradeStrip(doc, y)

  const scholasticExams = exams.filter(e => 
    !e.exam_name.toLowerCase().includes("co-sco") && 
    !e.exam_name.toLowerCase().includes("co-scholastic")
  )
  const coScholasticExams = exams.filter(e => 
    e.exam_name.toLowerCase().includes("co-sco") || 
    e.exam_name.toLowerCase().includes("co-scholastic")
  )

  const exam1 = scholasticExams.find(e => e.exam_name.includes("Term 1")) || scholasticExams[0]
  const exam2 = scholasticExams.find(e => e.exam_name.includes("Term 2")) || scholasticExams[1] || scholasticExams[0]

  const t1Subjects = exam1?.subjects || []
  const t2Subjects = exam2?.subjects || []

  const subjectMap = {}
  t1Subjects.forEach(s => { subjectMap[s.subject_code] = s.subject_name })
  t2Subjects.forEach(s => { subjectMap[s.subject_code] = s.subject_name })

  const subjectCodes = Object.keys(subjectMap)

  const getMarkForClass9 = (exam, subCode, assessName) => {
    if (!exam) return "—"
    const subjectsList = exam.subjects || []
    const sub = subjectsList.find(s => s.subject_code === subCode)
    if (!sub) return "—"
    
    let targetNames = [assessName]
    if (assessName === "P.T") targetNames.push("PT", "Periodic Test")
    if (assessName === "N.B") targetNames.push("NB", "Note Book")
    if (assessName === "S.E") targetNames.push("SE", "Subject Enrichment")
    if (assessName === "A.E") targetNames.push("AE", "Annual Exam", "Annual")

    const assess = (sub.assessments || []).find(a => 
      targetNames.some(name => name.toLowerCase() === a.assessment_name.toLowerCase())
    )
    if (!assess) return "—"
    const score = assess.normalized_score !== null && assess.normalized_score !== undefined ? assess.normalized_score : assess.sum_obtained
    return score !== null && score !== undefined && score !== "" ? String(score) : "—"
  }

  const getTermTotalForClass9 = (subCode) => {
    const gt = grand_total.find(g => g.subject_code === subCode)
    if (gt && gt.average !== null && gt.average !== undefined) {
      return gt.average
    }
    if (exam2) {
      const sub = (exam2.subjects || []).find(s => s.subject_code === subCode)
      if (sub && sub.term_total !== null && sub.term_total !== undefined && sub.term_total !== "") return sub.term_total
    }
    return "—"
  }

  const getTermGradeForClass9 = (subCode) => {
    const gt = grand_total.find(g => g.subject_code === subCode)
    if (gt && gt.final_grade) return gt.final_grade
    if (exam2) {
      const sub = (exam2.subjects || []).find(s => s.subject_code === subCode)
      if (sub && sub.positional_grade !== null && sub.positional_grade !== undefined && sub.positional_grade !== "") return sub.positional_grade
    }
    return "—"
  }

  const subjectsData = subjectCodes.map((code, idx) => {
    const periodic = getMarkForClass9(exam2, code, "P.T") === "—" ? getMarkForClass9(exam1, code, "P.T") : getMarkForClass9(exam2, code, "P.T")
    return [
      idx + 1,
      subjectMap[code],
      code,
      periodic,
      getMarkForClass9(exam2, code, "N.B"),
      getMarkForClass9(exam2, code, "S.E"),
      getMarkForClass9(exam2, code, "A.E"),
      getTermTotalForClass9(code),
      getTermGradeForClass9(code)
    ]
  })

  const getSum = (colIdx) => {
    let sum = 0
    let hasVal = false
    subjectsData.forEach(row => {
      const val = parseFloat(row[colIdx])
      if (!isNaN(val)) {
        sum += val
        hasVal = true
      }
    })
    return hasVal ? sum : "—"
  }

  subjectsData.push([
    { content: "TOTAL MARKS", colSpan: 3, styles: { fontStyle: "bold", halign: "center" } },
    getSum(3),
    getSum(4),
    getSum(5),
    getSum(6),
    getSum(7),
    "-"
  ])

  const coScholasticList = []
  coScholasticExams.forEach(e => {
    (e.subjects || []).forEach(s => {
      coScholasticList.push({
        subject_name: s.subject_name,
        grade: s.grade || s.positional_grade || s.term_total || null
      })
    })
  })
  const finalCoScholastic = co_scholastic.length > 0 ? co_scholastic : coScholasticList

  autoTable(doc, {
    head: [
      [
        "SLNO",
        "SUBJECTS",
        "SUBJECT CODE",
        "PERIODIC TEST\n(A=10)",
        "NOTE BOOK\n(B=5)",
        "SUBJECT ENRICHMENT\n(C=5)",
        "ANNUAL EXAM\n(D=80)",
        "GRAND TOTAL\n(A+B+C+D=100)",
        "POSITIONAL GRADE"
      ]
    ],
    body: subjectsData,
    startY: y,
    margin: { left: M, right: M },
    styles: {
      fontSize: 6.5,
      cellPadding: 1.2,
      lineColor: [170, 170, 170],
      lineWidth: 0.15,
      textColor: C.black,
      halign: "center"
    },
    headStyles: {
      fillColor: C.maroon,
      textColor: C.white,
      fontStyle: "bold",
      fontSize: 6
    },
    columnStyles: {
      1: { halign: "left", cellWidth: 50 },
      0: { cellWidth: 10 },
      2: { cellWidth: 20 }
    },
    theme: "grid"
  })

  y = doc.lastAutoTable.finalY + 3
  if (report.is_retest && report.previous_attempt_marks) {
    y = pPreviousAttemptMarks(doc, report.previous_attempt_marks, y)
  }

  // Notes Row
  pSetFont(doc, C.darkGrey); doc.setFontSize(6.5); doc.setFont("helvetica", "bold")
  doc.text("P.T. = Periodic Test, N.B. : Note Book, S.E. = Subject Enrichment, A.E. = Annual Examination", M + 2, y)
  y += 4

  // Lower 3-column area
  const col1X = M, col1W = 62
  const col2X = col1X + col1W + 4, col2W = 68
  const col3X = col2X + col2W + 4, col3W = 54
  const coY = y

  pSetFill(doc, C.navyBlue)
  doc.rect(col1X, coY, col1W, 6, "F")
  pSetFont(doc, C.white); doc.setFontSize(7); doc.setFont("helvetica", "bold")
  doc.text("PART 2 : CO-SCHOLASTIC AREA", col1X + 2, coY + 4.2)

  pSetFill(doc, C.white)
  doc.rect(col1X, coY + 6, col1W, 12, "F")
  pSetDraw(doc, C.lightGrey); doc.setLineWidth(0.2)
  doc.rect(col1X, coY + 6, col1W, 12)
  pSetFont(doc, C.black); doc.setFontSize(6.5); doc.setFont("helvetica", "bold")
  doc.text("Grading Scale", col1X + 2, coY + 9.5)
  const circles = [["A", "Outstanding"], ["B", "Very Good"], ["C", "Fair"]]
  circles.forEach(([label, desc], i) => {
    const cx = col1X + 16 + i * 14, cy = coY + 13
    pSetDraw(doc, C.black); doc.setLineWidth(0.4)
    doc.circle(cx, cy, 2.5)
    pSetFont(doc, C.black); doc.setFontSize(5.5); doc.setFont("helvetica", "bold")
    doc.text(label, cx, cy + 1.5, { align: "center" })
    pSetFont(doc, C.darkGrey); doc.setFontSize(4); doc.setFont("helvetica", "normal")
    doc.text(desc, cx, cy + 4, { align: "center" })
  })

  autoTable(doc, {
    head : [["SUBJECTS", "GRADE"]],
    body : finalCoScholastic.length > 0
      ? finalCoScholastic.map(c => [fmt(c.subject_name), { content: fmt(c.grade ?? null), styles: { halign: "center", fontStyle: "bold" } }])
      : [["No co-scholastic records", ""]],
    startY  : coY + 18.5,
    margin  : { left: col1X, right: mmW - col1X - col1W },
    styles: { fontSize: 6, cellPadding: { top: 1, bottom: 1, left: 2, right: 2 }, lineColor: [136, 136, 136], lineWidth: 0.2, textColor: C.black },
    headStyles    : { fillColor: C.tableHead, textColor: C.white, fontStyle: "bold", fontSize: 6 },
    columnStyles  : { 0: { halign: "left" }, 1: { halign: "center", cellWidth: 12 } },
    theme         : "grid",
  })

  pSetFill(doc, C.navyBlue)
  doc.rect(col2X, y, col2W, 6, "F")
  pSetFont(doc, C.white); doc.setFontSize(7); doc.setFont("helvetica", "bold")
  const si = student_info
  doc.text(`Subject wise average score in Class ${(si.st_class || "").toUpperCase()}, Section ${(si.st_sec || "").toUpperCase()}`, col2X + 2, y + 4.2)

  autoTable(doc, {
    head : [["SUBJECTS", "Average"]],
    body : subject_avg.length > 0
      ? subject_avg.map((s, i) => [
          s.subject_name,
          { content: fmt(s.average ?? s.term2_avg ?? s.term1_avg), styles: { halign: "right", fontStyle: "bold" } },
        ])
      : [["No data available", ""]],
    startY  : y + 7,
    margin  : { left: col2X, right: mmW - col2X - col2W },
    styles: { fontSize: 6, cellPadding: { top: 1, bottom: 1, left: 2, right: 2 }, lineColor: [170, 170, 170], lineWidth: 0.2, textColor: C.black },
    headStyles   : { fillColor: [221, 221, 221], textColor: C.black, fontStyle: "bold", fontSize: 6, halign: "center" },
    columnStyles : { 0: { halign: "left" }, 1: { halign: "right", cellWidth: 16 } },
    theme        : "grid",
  })

  pSetFill(doc, C.maroon)
  doc.rect(col3X, y, col3W, 5.5, "F")
  pSetFont(doc, C.white); doc.setFontSize(7); doc.setFont("helvetica", "bold")
  doc.text("PROMOTION STATUS", col3X + col3W / 2, y + 3.8, { align: "center" })

  pSetFill(doc, C.white)
  doc.rect(col3X, y + 5.5, col3W, 20, "F")
  pSetDraw(doc, C.lightGrey); doc.setLineWidth(0.2)
  doc.rect(col3X, y + 5.5, col3W, 20)
  if (promotion_status) {
    pSetFont(doc, [21, 128, 61]); doc.setFontSize(7.5); doc.setFont("helvetica", "bold")
    doc.text(doc.splitTextToSize(promotion_status, col3W - 4), col3X + 2, y + 10, { align: "left" })
  }

  pSetFill(doc, C.maroon)
  doc.rect(col3X, y + 26.5, col3W, 5.5, "F")
  pSetFont(doc, C.white); doc.setFontSize(7); doc.setFont("helvetica", "bold")
  doc.text("ATTENDANCE", col3X + col3W / 2, y + 30.3, { align: "center" })

  const attY = y + 32
  pSetFill(doc, C.yellowAtt)
  doc.rect(col3X, attY, col3W, 16, "F")
  pSetDraw(doc, C.lightGrey); doc.setLineWidth(0.2)
  doc.rect(col3X, attY, col3W, 16)

  pSetFont(doc, C.black); doc.setFontSize(6.5); doc.setFont("helvetica", "bold")
  doc.text(`Total Days`, col3X + 2, attY + 5)
  doc.setFont("helvetica", "normal")
  doc.text(`${fmt(attendance?.total_days)} days`, col3X + 22, attY + 5)
  doc.setFont("helvetica", "bold")
  doc.text(`Days Present`, col3X + 2, attY + 10)
  doc.setFont("helvetica", "normal")
  doc.text(`${fmt(attendance?.days_present)} days`, col3X + 22, attY + 10)

  pSetFill(doc, C.orange)
  doc.rect(col3X + col3W - 14, attY, 14, 16, "F")
  pSetFont(doc, C.white); doc.setFontSize(8.5); doc.setFont("helvetica", "bold")
  doc.text(`${attendance?.percentage ?? "—"}%`, col3X + col3W - 7, attY + 9.5, { align: "center" })

  const afterLeft  = doc.lastAutoTable ? doc.lastAutoTable.finalY : y + 49
  const afterRight = attY + 17
  y = Math.max(afterLeft, afterRight) + 3

  y = pRemarks(doc, remark, y)
  pSignatures(doc)

  // Outer green border drawn on top
  pSetDraw(doc, [58, 125, 68]); doc.setLineWidth(1.2)
  doc.rect(M, M, mmW - M * 2, mmH - M * 2)
  doc.setLineWidth(0.3)
}

function pSignaturesGroup9(doc) {
  const y      = mmH - M - 14
  const labels = ["Signature of Guardian", "Signature of Class Teacher", "Signature of Principal"]
  const xs     = [M + 8, mmW / 2 - 22, mmW - M - 52]
  pSetDraw(doc, C.black); doc.setLineWidth(0.3)
  labels.forEach((label, i) => {
    if (label === "Signature of Principal") {
      pSetFont(doc, C.black); doc.setFontSize(10); doc.setFont("courier", "bolditalic")
      doc.text("V. Lakhar", xs[i] + 22, y - 3, { align: "center" })
    }
    doc.line(xs[i], y, xs[i] + 44, y)
    pSetFont(doc, C.darkGrey); doc.setFontSize(7); doc.setFont("helvetica", "bold")
    doc.text(label, xs[i] + 22, y + 4.5, { align: "center" })
  })
}

async function pDrawPageMAPre(doc, report, school, cbseImg, schoolImg, studentImg) {
  const w = 297
  const h = 210
  const M = 6
  const colW = (w - M * 2) / 2
  const lx = M + 4
  const rx = w / 2 + 4

  const {
    student_info     = {},
    exams            = [],
    attendance       = {},
    remark           = "",
    session_name     = "",
    promotion_status = "",
  } = report

  const si = student_info

  const obsSubjects = [
    "Activities taken part",
    "General Appearance and Grooming",
    "Behaviour and Conduct",
    "Personality",
    "Discipline",
    "Co-Operation"
  ]

  const evalSubjects = [
    "English (Oral)",
    "English (Written)",
    "Number (Oral)",
    "Number (Written)",
    "2nd Language (Oral)",
    "2nd Language (Written)",
    "General Knowledge (Oral)",
    "Recitation",
    "Observation of surroundings",
    "Recognition of sound",
    "Sensorial Presentation",
    "Drawing and Colouring",
    "Apparatus Presentation",
    "Exercises of Practical Life",
    "Picture Conversation"
  ]

  const formatAttVal = val => (val === 0 || val === null || val === undefined || val === "" ? "       " : String(val))
  const getAtt = (termKey, field) => {
    if (report[termKey]) {
      const tAtt = report[termKey]
      if (field === "days_present" && tAtt.present !== undefined) return formatAttVal(tAtt.present)
      if (field === "total_days" && tAtt.total !== undefined) return formatAttVal(tAtt.total)
      if (tAtt[field] !== undefined) return formatAttVal(tAtt[field])
    }
    return formatAttVal(attendance[field])
  }

  const attT1 = { present: getAtt("attendance_t1", "days_present"), total: getAtt("attendance_t1", "total_days") }
  const attT2 = { present: getAtt("attendance_t2", "days_present"), total: getAtt("attendance_t2", "total_days") }
  const remT1 = report.remarks_t1 || ""
  const remT2 = report.remarks_t2 || remark || ""

  // ═══════════════════════════════════════════════════════════════════════════
  //  PAGE 1: booklet outer (Left: General Observations, Right: Cover Page)
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Warm cream/yellowish page background
  pSetFill(doc, [255, 253, 230])
  doc.rect(M, M, w - M*2, h - M*2, "F")

  // Outer gold/tan border
  pSetDraw(doc, [196, 154, 108])
  doc.setLineWidth(1.0)
  doc.rect(M, M, w - M*2, h - M*2)

  // Middle fold line
  pSetDraw(doc, [210, 190, 140])
  doc.line(w/2, M, w/2, h - M)

  // ── LEFT SIDE: General Observations ──
  // Term 1 header band
  let ly = M + 6
  pSetFill(doc, [196, 154, 108])
  doc.rect(lx, ly, colW - 8, 8, "F")
  pSetFont(doc, C.white)
  doc.setFontSize(10)
  doc.setFont("helvetica", "bold")
  doc.text("GENERAL OBSERVATIONS TERM - 1", lx + 4, ly + 5.5)

  // Term 1 table
  let tableY = ly + 10
  doc.setFontSize(8)
  pSetFont(doc, [40, 40, 40])
  obsSubjects.forEach((sub, i) => {
    let ry = tableY + i * 7
    doc.setFont("helvetica", "normal")
    doc.text(sub, lx + 2, ry + 5)
    // Grade box
    pSetDraw(doc, [210, 190, 140])
    pSetFill(doc, C.white)
    doc.rect(lx + colW - 18, ry + 1, 8, 5.5, "FD")
    pSetFont(doc, C.maroon)
    doc.setFont("helvetica", "bold")
    const grade = getMAPreGrade(exams, "GENERAL OBSERVATIONS", "TERM - 1", sub)
    doc.text(grade, lx + colW - 14, ry + 5, { align: "center" })
    pSetFont(doc, [40, 40, 40])
  })

  // Term 1 Attendance, Remarks, Signatures
  let attY = tableY + obsSubjects.length * 7 + 4
  doc.setFont("helvetica", "normal")
  doc.text(`Attendance...........................................${attT1.present ?? "—"} days out of ${attT1.total ?? "—"}...........................................`, lx + 2, attY)
  doc.text(`Remarks........................................................................................................................................`, lx + 2, attY + 6)
  if (remT1 && remT1 !== "—") {
    pSetFont(doc, C.maroon)
    doc.setFont("helvetica", "bold")
    doc.text(remT1, lx + 20, attY + 6)
  }
  pSetFont(doc, [60, 60, 60])
  doc.setFontSize(7.5)
  let sigY = attY + 18
  doc.text("Signature : Class Teacher", lx + 2, sigY)
  doc.text("Parent / Guardian", lx + colW / 2 - 12, sigY)
  doc.text("Principal", lx + colW - 20, sigY)

  // Term 2 header band
  ly = M + 104
  pSetFill(doc, [196, 154, 108])
  doc.rect(lx, ly, colW - 8, 8, "F")
  pSetFont(doc, C.white)
  doc.setFontSize(10)
  doc.setFont("helvetica", "bold")
  doc.text("GENERAL OBSERVATIONS TERM - 2", lx + 4, ly + 5.5)

  // Term 2 table
  tableY = ly + 10
  pSetFont(doc, [40, 40, 40])
  doc.setFontSize(8)
  obsSubjects.forEach((sub, i) => {
    let ry = tableY + i * 7
    doc.setFont("helvetica", "normal")
    doc.text(sub, lx + 2, ry + 5)
    // Grade box
    pSetDraw(doc, [210, 190, 140])
    pSetFill(doc, C.white)
    doc.rect(lx + colW - 18, ry + 1, 8, 5.5, "FD")
    pSetFont(doc, C.maroon)
    doc.setFont("helvetica", "bold")
    const grade = getMAPreGrade(exams, "GENERAL OBSERVATIONS", "TERM - 2", sub)
    doc.text(grade, lx + colW - 14, ry + 5, { align: "center" })
    pSetFont(doc, [40, 40, 40])
  })

  // Term 2 Attendance, Remarks, Promotion, Signatures
  attY = tableY + obsSubjects.length * 7 + 4
  doc.setFont("helvetica", "normal")
  doc.text(`Attendance...........................................${attT2.present ?? "—"} days out of ${attT2.total ?? "—"}...........................................`, lx + 2, attY)
  doc.text(`Remarks........................................................................................................................................`, lx + 2, attY + 6)
  if (remT2 && remT2 !== "—") {
    pSetFont(doc, C.maroon)
    doc.setFont("helvetica", "bold")
    doc.text(remT2, lx + 20, attY + 6)
  }
  pSetFont(doc, [40, 40, 40])
  doc.setFont("helvetica", "normal")
  doc.text(`Promoted to Class..................................................${promotion_status || "—"}..................................................`, lx + 2, attY + 12)
  pSetFont(doc, [60, 60, 60])
  doc.setFontSize(7.5)
  sigY = attY + 22
  doc.text("Signature : Class Teacher", lx + 2, sigY)
  doc.text("Parent / Guardian", lx + colW / 2 - 12, sigY)
  doc.text("Principal", lx + colW - 20, sigY)

  // Page 1 Footer Note
  pSetFill(doc, [196, 154, 108])
  doc.rect(lx, h - M - 6, colW - 8, 6, "F")
  pSetFont(doc, C.white)
  doc.setFontSize(7)
  doc.setFont("helvetica", "bold")
  doc.text("NOTE : Parents will return this card to the school duly signed and retained after Annual Examination.", lx + 4, h - M - 2)

  // ── RIGHT SIDE: Cover Page ──
  ly = M + 6
  // School details header
  pSetFont(doc, C.maroon)
  doc.setFontSize(16)
  doc.setFont("helvetica", "bold")
  doc.text(school.name, rx + colW / 2 - 4, ly + 6, { align: "center" })

  pSetFont(doc, [60, 60, 60])
  doc.setFontSize(7)
  doc.setFont("helvetica", "normal")
  doc.text(school.address, rx + colW / 2 - 4, ly + 10, { align: "center" })
  doc.text(school.affiliation, rx + colW / 2 - 4, ly + 13.5, { align: "center" })
  doc.text(`Phone: ${school.phone} | Fax: +91 361 2475977 Ext 333`, rx + colW / 2 - 4, ly + 17, { align: "center" })
  doc.text(`email: ${school.email} | Web site: ${school.website}`, rx + colW / 2 - 4, ly + 20.5, { align: "center" })

  // Thick maroon band
  pSetFill(doc, C.maroon)
  doc.rect(rx, ly + 23, colW - 8, 2.5, "F")

  // Logo in center
  const logoX = rx + colW / 2 - 4
  const logoY = ly + 35
  if (schoolImg) {
    try {
      doc.addImage(schoolImg, "PNG", logoX - 11, logoY, 22, 22)
    } catch (e) {
      console.warn("Failed to add School logo to pre-primary cover:", e)
    }
  }

  // Progress Report Title
  pSetFont(doc, C.maroon)
  doc.setFontSize(20)
  doc.setFont("helvetica", "bold")
  doc.text("PROGRESS REPORT", rx + colW / 2 - 4, ly + 68, { align: "center" })
  doc.setFontSize(11)
  doc.text("Montessori Section ( MA to Pre1 )", rx + colW / 2 - 4, ly + 75, { align: "center" })

  pSetFont(doc, C.black)
  doc.setFontSize(12)
  doc.text(`ACADEMIC YEAR :  ${session_name || "—"}`, rx + 6, ly + 100)

  // Student Details box
  const boxY = ly + 112
  const boxW = colW - 8
  const boxH = 58
  pSetFill(doc, [255, 253, 230])
  pSetDraw(doc, C.maroon)
  doc.setLineWidth(0.8)
  doc.rect(rx, boxY, boxW, boxH, "FD")
  doc.setLineWidth(0.3)

  const bx = rx + 4
  const by = boxY + 6
  pSetFont(doc, C.black)
  doc.setFontSize(9)
  doc.setFont("helvetica", "bold")
  doc.text("Student Code", bx, by)
  doc.text(":", bx + 65, by)
  
  // Student Code boxes
  const codeStr = si.st_code || ""
  for (let i = 0; i < 6; i++) {
    doc.rect(bx + 69 + i * 6, by - 3.8, 5.5, 5, "D")
    if (codeStr[i]) {
      doc.text(codeStr[i], bx + 71.7 + i * 6, by, { align: "center" })
    }
  }

  const drawInfoRow = (lbl, val, rowY) => {
    doc.text(lbl, bx, rowY)
    doc.text(":", bx + 65, rowY)
    doc.rect(bx + 69, rowY - 3.8, boxW - 77, 5, "D")
    pSetFont(doc, [60, 60, 60])
    doc.text(val || "", bx + 71, rowY)
    pSetFont(doc, C.black)
  }

  drawInfoRow("Student Name", si.st_name, by + 8)
  drawInfoRow("Class", (si.st_class || "").toUpperCase(), by + 16)
  drawInfoRow("Section", (si.st_sec || "").toUpperCase(), by + 24)
  drawInfoRow("Roll", (si.roll_no || report.roll_number || "").toString(), by + 32)
  drawInfoRow("Specimen Signature of Parent/Guardian", "", by + 42)


  // ═══════════════════════════════════════════════════════════════════════════
  //  PAGE 2: Inside Spread (Evaluation Records Term 1 & Term 2 side-by-side)
  // ═══════════════════════════════════════════════════════════════════════════
  doc.addPage("a4", "landscape")

  // Warm cream/yellowish page background
  pSetFill(doc, [255, 253, 230])
  doc.rect(M, M, w - M*2, h - M*2, "F")

  // Outer gold/tan border
  pSetDraw(doc, [196, 154, 108])
  doc.setLineWidth(1.0)
  doc.rect(M, M, w - M*2, h - M*2)

  // Middle fold line
  pSetDraw(doc, [210, 190, 140])
  doc.line(w/2, M, w/2, h - M)

  // Grade chart header
  pSetFill(doc, [255, 253, 240])
  doc.rect(M + 4, M + 4, w - M*2 - 8, 9, "FD")
  pSetFont(doc, C.maroon)
  doc.setFontSize(8)
  doc.setFont("helvetica", "bold")
  doc.text("GRADE CHART :", M + 6, M + 10)
  pSetFont(doc, C.black)
  doc.text("A+ : EXCELLENT       A : VERY GOOD       B+ : GOOD       B : AVERAGE       C : SCOPE FOR IMPROVEMENT", M + 34, M + 10)

  // Evaluation Record Term 1 Header
  let contentY = M + 20
  pSetFill(doc, [196, 154, 108])
  doc.rect(lx, contentY, colW - 8, 6, "F")
  pSetFont(doc, C.white)
  doc.setFontSize(8.5)
  doc.setFont("helvetica", "bold")
  doc.text("EVALUATION RECORD : TERM - 1", lx + 4, contentY + 4)

  // Evaluation Record Term 2 Header
  pSetFill(doc, [196, 154, 108])
  doc.rect(rx, contentY, colW - 8, 6, "F")
  pSetFont(doc, C.white)
  doc.text("EVALUATION RECORD : TERM - 2", rx + 4, contentY + 4)

  // Prepare autoTable data
  const t1EvalData = evalSubjects.map((sub, idx) => [
    `${String(idx + 1).padStart(2, "0")}. ${sub}`,
    getMAPreGrade(exams, "EVALUATION RECORD", "TERM - 1", sub)
  ])

  const t2EvalData = evalSubjects.map((sub, idx) => [
    `${String(idx + 1).padStart(2, "0")}. ${sub}`,
    getMAPreGrade(exams, "EVALUATION RECORD", "TERM - 2", sub)
  ])

  // Term 1 table
  autoTable(doc, {
    head: [["AREAS OF ASSESSMENT", "GRADE"]],
    body: t1EvalData,
    startY: contentY + 6,
    margin: { left: lx, right: w / 2 + 4 },
    theme: "grid",
    styles: {
      fontSize: 8.5,
      cellPadding: 2.4,
      lineColor: [210, 190, 140],
      lineWidth: 0.15,
      textColor: C.black,
      valign: "middle"
    },
    headStyles: {
      fillColor: [196, 154, 108],
      textColor: C.white,
      fontStyle: "bold",
      fontSize: 9,
      valign: "middle"
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center", cellWidth: 22, fontStyle: "bold", textColor: C.maroon }
    }
  })

  // Term 2 table
  autoTable(doc, {
    head: [["AREAS OF ASSESSMENT", "GRADE"]],
    body: t2EvalData,
    startY: contentY + 6,
    margin: { left: rx, right: M + 4 },
    theme: "grid",
    styles: {
      fontSize: 8.5,
      cellPadding: 2.4,
      lineColor: [210, 190, 140],
      lineWidth: 0.15,
      textColor: C.black,
      valign: "middle"
    },
    headStyles: {
      fillColor: [196, 154, 108],
      textColor: C.white,
      fontStyle: "bold",
      fontSize: 9,
      valign: "middle"
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center", cellWidth: 22, fontStyle: "bold", textColor: C.maroon }
    }
  })
}

async function pDrawPage1_8(doc, report, school, cbseImg, schoolImg, studentImg) {
  pSetDraw(doc, [58, 125, 68]); doc.setLineWidth(1.2)
  doc.rect(M, M, mmW - M * 2, mmH - M * 2)
  pSetFont(doc, C.black); doc.setFontSize(14); doc.setFont("helvetica", "bold")
  doc.text("CLASSES 1-8 GRADE CARD PLACEHOLDER", mmW / 2, 40, { align: "center" })
  doc.setFontSize(10); doc.setFont("helvetica", "normal")
  doc.text(`Student Name: ${report.student_info?.st_name || "—"}`, mmW / 2, 50, { align: "center" })
  doc.text(`Group: ${report.group || "—"}`, mmW / 2, 60, { align: "center" })
}

async function pDrawPage11_12Practical(doc, report, school, cbseImg, schoolImg, studentImg) {
  const {
    student_info     = {},
    exams            = [],
    co_scholastic    = [],
    attendance       = {},
    remark           = "",
    session_name     = "",
  } = report

  const si = student_info
  const subjects = getSubjectMarks11_12(report)
  const pct = calculatePercentages11_12(subjects, report)

  // Sum calculations
  const getSumVal = (field) => {
    let sum = 0
    let hasVal = false
    subjects.forEach(s => {
      const val = parseFloat(s[field])
      if (!isNaN(val)) {
        sum += val
        hasVal = true
      }
    })
    return hasVal ? sum : "—"
  }

  const totalFullPract = getSumVal("full_pract")
  const totalFullTheory = getSumVal("full_theory")
  const totalObtainPract = getSumVal("obtain_pract")
  const totalObtainTheory = getSumVal("obtain_theory")
  const totalObtainTotal = getSumVal("obtain_total")

  const attPct = attendance.percentage ? `${Math.round(attendance.percentage)}%` : "—"

  // Border
  pSetDraw(doc, [58, 125, 68]); doc.setLineWidth(1.2)
  doc.rect(M, M, mmW - M * 2, mmH - M * 2)

  // Session & Print Date
  pSetFont(doc, C.black); doc.setFontSize(8); doc.setFont("helvetica", "bold")
  doc.text(`Session : ${session_name || "—"}`, M + 4, M + 6)
  doc.text(`Print Date : ${printDate}`, mmW - M - 4, M + 6, { align: "right" })

  // School Header Band
  pSetFill(doc, [125, 10, 10])
  doc.rect(M + 2, M + 9, mmW - M*2 - 4, 13, "F")
  pSetFont(doc, C.white); doc.setFontSize(7.5); doc.setFont("helvetica", "normal")
  doc.text("Established Year 1960", mmW / 2, M + 12.5, { align: "center" })
  doc.setFontSize(14); doc.setFont("helvetica", "bold")
  doc.text(school.name, mmW / 2, M + 19.5, { align: "center" })

  // Info Strip
  if (cbseImg) {
    try {
      doc.addImage(cbseImg, "PNG", M + 4, M + 24, 12, 12)
    } catch (e) {
      console.warn("Failed to add CBSE logo to 11-12-practical:", e)
    }
  }
  if (schoolImg) {
    try {
      doc.addImage(schoolImg, "PNG", mmW - M - 16, M + 24, 12, 12)
    } catch (e) {
      console.warn("Failed to add School logo to 11-12-practical:", e)
    }
  }

  pSetFont(doc, C.black); doc.setFontSize(7.5); doc.setFont("helvetica", "bold")
  doc.text(school.affiliation, mmW / 2, M + 26, { align: "center" })
  doc.setFont("helvetica", "normal"); doc.setFontSize(6.5)
  doc.text(`Address : ${school.address}`, mmW / 2, M + 29.5, { align: "center" })
  doc.text(`Phone : ${school.phone}`, mmW / 2, M + 32.5, { align: "center" })
  doc.text(`Email : ${school.email} | Website : ${school.website}`, mmW / 2, M + 35.5, { align: "center" })

  // Divider
  doc.setLineWidth(0.2); pSetDraw(doc, [200, 200, 200])
  doc.line(M + 2, M + 38.5, mmW - M - 2, M + 38.5)

  // Student Details Box
  pSetFill(doc, [239, 239, 233])
  doc.rect(M + 2, M + 40, mmW - M*2 - 4, 34, "F")

  pSetFill(doc, [125, 10, 10])
  doc.rect(M + 4, M + 42, 22, 4.5, "F")
  pSetFont(doc, C.white); doc.setFontSize(7); doc.setFont("helvetica", "bold")
  doc.text("Student Details", M + 5, M + 45.2)

  let sY = M + 51
  pSetFont(doc, C.black); doc.setFontSize(7.5); doc.setFont("helvetica", "normal")
  
  // Left side info fields
  doc.text("Student's Name :", M + 4, sY)
  doc.text("Father's Name :", M + 4, sY + 5)
  doc.text("Mother's Name :", M + 4, sY + 10)
  doc.text("Address :", M + 4, sY + 15)

  doc.setFont("helvetica", "bold")
  doc.text(fmt(si.st_name), M + 30, sY)
  doc.text(si.father_name ? `MR. ${si.father_name.toUpperCase()}` : "—", M + 30, sY + 5)
  doc.text(si.mother_name ? `MS. ${si.mother_name.toUpperCase()}` : "—", M + 30, sY + 10)
  doc.text(fmt(si.address), M + 30, sY + 15)

  // Right side info fields
  doc.setFont("helvetica", "normal")
  doc.text("Code :", M + 115, sY)
  doc.text("Class :", M + 115, sY + 5)
  doc.text("Section :", M + 115, sY + 10)
  doc.text("Date of Birth :", M + 115, sY + 15)

  doc.setFont("helvetica", "bold")
  doc.text(fmt(si.st_code), M + 138, sY)
  doc.text((si.st_class || "").toUpperCase(), M + 138, sY + 5)
  doc.text((si.st_sec || "").toUpperCase(), M + 138, sY + 10)
  doc.text(fmtD(si.dob), M + 138, sY + 15)

  // Photo
  pSetFill(doc, C.white); pSetDraw(doc, [200, 200, 200])
  doc.rect(mmW - M - 24, M + 42, 20, 25, "FD")
  if (studentImg) {
    try {
      doc.addImage(studentImg, "JPEG", mmW - M - 24, M + 42, 20, 25)
    } catch (e) {
      console.warn("Failed to add student image:", e)
    }
  } else {
    pSetFont(doc, [150, 150, 150]); doc.setFontSize(7)
    doc.text("No Photo", mmW - M - 14, M + 55, { align: "center" })
  }

  // Title Band
  let tbY = M + 78
  pSetFill(doc, [223, 199, 148])
  doc.rect(M + 2, tbY, 15, 6, "F")
  pSetFill(doc, [74, 46, 24])
  doc.rect(M + 17, tbY, mmW - M*2 - 34, 6, "F")
  pSetFill(doc, [223, 199, 148])
  doc.rect(mmW - M - 17, tbY, 15, 6, "F")

  pSetFont(doc, C.white); doc.setFontSize(9); doc.setFont("helvetica", "bold")
  doc.text("ANNUAL EXAMINATION REPORT CARD", mmW / 2, tbY + 4.2, { align: "center" })

  // Part 1 Scholastic Area Table
  pSetFont(doc, C.black); doc.setFontSize(8); doc.setFont("helvetica", "bold")
  doc.text("PART 1 : SCHOLASTIC", M + 2, tbY + 11)

  const head = [
    [
      { content: "SLNO", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
      { content: "SUBJECT NAME", rowSpan: 2, styles: { valign: "middle", halign: "left" } },
      { content: "SUBJECT CODE", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
      { content: "FULL MARK", colSpan: 2, styles: { halign: "center" } },
      { content: "PASS MARK", colSpan: 2, styles: { halign: "center" } },
      { content: "MARK OBTAIN", colSpan: 3, styles: { halign: "center" } }
    ],
    [
      { content: "Practical", styles: { halign: "center", fillColor: [141, 26, 26] } },
      { content: "Theory", styles: { halign: "center", fillColor: [141, 26, 26] } },
      { content: "Practical", styles: { halign: "center", fillColor: [141, 26, 26] } },
      { content: "Theory", styles: { halign: "center", fillColor: [141, 26, 26] } },
      { content: "Practical", styles: { halign: "center", fillColor: [141, 26, 26] } },
      { content: "Theory", styles: { halign: "center", fillColor: [141, 26, 26] } },
      { content: "Total", styles: { halign: "center", fillColor: [141, 26, 26] } }
    ]
  ]

  const body = subjects.map((s, idx) => [
    idx + 1,
    s.name,
    s.code,
    s.full_pract || "—",
    s.full_theory || "—",
    s.pass_pract || "—",
    s.pass_theory || "—",
    s.obtain_pract,
    s.obtain_theory,
    s.obtain_total
  ])

  body.push([
    { content: "TOTAL MARKS", colSpan: 3, styles: { halign: "center", fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: totalFullPract, styles: { fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: totalFullTheory, styles: { fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: "—", styles: { fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: "—", styles: { fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: totalObtainPract, styles: { fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: totalObtainTheory, styles: { fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: totalObtainTotal, styles: { fontStyle: "bold", fillColor: [30, 59, 139], textColor: [255, 255, 255] } }
  ])

  autoTable(doc, {
    head: head,
    body: body,
    startY: tbY + 13,
    margin: { left: M + 2, right: M + 2 },
    theme: "grid",
    styles: {
      fontSize: 7,
      cellPadding: 1.5,
      lineColor: [80, 80, 80],
      lineWidth: 0.15,
      textColor: C.black,
      valign: "middle"
    },
    headStyles: {
      fillColor: [125, 10, 10],
      textColor: C.white,
      fontStyle: "bold",
      fontSize: 7
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 10 },
      1: { halign: "left", fontStyle: "bold" },
      2: { halign: "center", cellWidth: 20 },
      3: { halign: "center", cellWidth: 15 },
      4: { halign: "center", cellWidth: 15 },
      5: { halign: "center", cellWidth: 15 },
      6: { halign: "center", cellWidth: 15 },
      7: { halign: "center", cellWidth: 15 },
      8: { halign: "center", cellWidth: 15 },
      9: { halign: "center", cellWidth: 15, fontStyle: "bold" }
    }
  })

  // Lower layout grids
  let y = doc.lastAutoTable ? doc.lastAutoTable.finalY : 170
  if (report.is_retest && report.previous_attempt_marks) {
    y = pPreviousAttemptMarks(doc, report.previous_attempt_marks, y + 2)
  }
  y += 5

  pSetFont(doc, C.black); doc.setFontSize(8); doc.setFont("helvetica", "bold")
  doc.text("PART 2 : CO-SCHOLASTIC", M + 2, y)
  y += 2

  // Column 1: CO-SCHOLASTIC AREAS (Width 61)
  pSetFill(doc, [125, 10, 10])
  doc.rect(M + 2, y, 61, 5, "F")
  pSetFont(doc, C.white); doc.setFontSize(6.5); doc.setFont("helvetica", "bold")
  doc.text("CO-SCHOLASTIC AREAS", M + 32.5, y + 3.8, { align: "center" })

  pSetFill(doc, C.white); pSetDraw(doc, [80, 80, 80])
  doc.rect(M + 2, y + 5, 61, 35, "FD")
  pSetFont(doc, C.black); doc.setFontSize(6.5); doc.setFont("helvetica", "bold")
  doc.text("Grading Scale", M + 32.5, y + 9.5, { align: "center" })

  const scales = ["A1", "A2", "B1", "B2", "C1", "C2", "D1", "D2", "E"]
  scales.forEach((sc, i) => {
    let sx = M + 3.5 + i * 6.2
    pSetFill(doc, [254, 243, 199])
    doc.rect(sx, y + 11.5, 5.5, 4, "F")
    pSetFont(doc, [125, 10, 10]); doc.setFontSize(5)
    doc.text(sc, sx + 2.75, y + 14.5, { align: "center" })
  })

  pSetFont(doc, C.black); doc.setFontSize(6.5)
  co_scholastic.forEach((c, i) => {
    let cy = y + 21 + i * 5
    doc.setFont("helvetica", "normal")
    doc.text(c.subject_name || "", M + 4, cy)
    doc.setFont("helvetica", "bold")
    pSetFont(doc, [125, 10, 10])
    doc.text(c.grade || "—", M + 59, cy, { align: "right" })
    pSetFont(doc, C.black)
  })

  // Column 2: Subject wise average score (Width 64)
  pSetFill(doc, [125, 10, 10])
  doc.rect(M + 66, y, 64, 5, "F")
  pSetFont(doc, C.white); doc.setFontSize(6)
  doc.text(`Subject wise average score in Class ${fmt(si.st_class).toUpperCase()}, Sec ${fmt(si.st_sec).toUpperCase()}`, M + 98, y + 3.8, { align: "center" })

  pSetFill(doc, C.white); pSetDraw(doc, [80, 80, 80])
  doc.rect(M + 66, y + 5, 64, 35, "FD")
  pSetFont(doc, C.black); doc.setFontSize(6.5); doc.setFont("helvetica", "bold")
  doc.text("SUBJECTS", M + 68, y + 9.5)
  doc.text("Average", M + 128, y + 9.5, { align: "right" })
  doc.line(M + 68, y + 10.5, M + 128, y + 10.5)

  subjects.forEach((s, i) => {
    let sy = y + 14.5 + i * 5
    doc.setFont("helvetica", "normal")
    doc.text(s.name, M + 68, sy)
    doc.setFont("helvetica", "bold")
    doc.text(String(s.class_average ?? "—"), M + 128, sy, { align: "right" })
  })

  // Column 3: Marks & Attendance Percentage (Width 61)
  pSetFill(doc, [125, 10, 10])
  doc.rect(M + 133, y, 61, 5, "F")
  pSetFont(doc, C.white); doc.setFontSize(6.5); doc.setFont("helvetica", "bold")
  doc.text("MARKS PERCENTAGE", M + 163.5, y + 3.8, { align: "center" })

  pSetFill(doc, C.white); pSetDraw(doc, [80, 80, 80])
  doc.rect(M + 133, y + 5, 61, 15, "FD")
  pSetFont(doc, C.black); doc.setFontSize(6.5)
  doc.setFont("helvetica", "normal")
  doc.text("Practical:", M + 136, y + 9.5)
  doc.text("Theory:", M + 136, y + 13.5)
  doc.text("Overall Percentage:", M + 136, y + 17.5)
  doc.setFont("helvetica", "bold")
  doc.text(pct.practical, M + 191, y + 9.5, { align: "right" })
  doc.text(pct.theory, M + 191, y + 13.5, { align: "right" })
  doc.text(pct.overall, M + 191, y + 17.5, { align: "right" })

  // Attendance Box
  let ay = y + 21
  pSetFill(doc, [125, 10, 10])
  doc.rect(M + 133, ay, 61, 5, "F")
  pSetFont(doc, C.white); doc.setFontSize(6.5); doc.setFont("helvetica", "bold")
  doc.text("ATTENDANCE PERCENTAGE", M + 163.5, ay + 3.8, { align: "center" })

  pSetFill(doc, C.white); pSetDraw(doc, [80, 80, 80])
  doc.rect(M + 133, ay + 5, 61, 14, "FD")
  pSetFont(doc, [21, 128, 61]); doc.setFontSize(9.5); doc.setFont("helvetica", "bold")
  doc.text(attPct, M + 163.5, ay + 11, { align: "center" })

  pSetFill(doc, [254, 243, 199])
  doc.rect(M + 133, ay + 13, 61, 5, "F")
  pSetFont(doc, [125, 10, 10]); doc.setFontSize(5.5); doc.setFont("helvetica", "bold")
  doc.text(`Min Attendance Required : ${attendance?.min_required || "75%"}`, M + 163.5, ay + 16.5, { align: "center" })

  // Remarks
  let ry = y + 43
  pSetDraw(doc, [150, 150, 150])
  doc.rect(M + 2, ry, mmW - M*2 - 4, 18, "D")
  pSetFont(doc, C.black); doc.setFontSize(7.5); doc.setFont("helvetica", "bold")
  doc.text("REMARKS", M + 4, ry + 4.5)
  doc.setFont("helvetica", "normal"); doc.setFontSize(7)
  pSetFont(doc, [80, 80, 80])
  doc.text(remark || "No remarks entered.", M + 4, ry + 9.5)

  // Signatures
  let sigY = ry + 28
  const xs = [M + 15, mmW / 2 - 20, mmW - M - 45]
  const labels = ["Signature of Guardian", "Signature of Class Teacher", "Signature of Principal"]
  labels.forEach((label, i) => {
    pSetDraw(doc, C.black); doc.setLineWidth(0.3)
    doc.line(xs[i], sigY, xs[i] + 40, sigY)
    pSetFont(doc, [60, 60, 60]); doc.setFontSize(7.5); doc.setFont("helvetica", "bold")
    doc.text(label, xs[i] + 20, sigY + 4, { align: "center" })
  })
}

async function pDrawPage11_12(doc, report, school, cbseImg, schoolImg, studentImg) {
  const {
    student_info     = {},
    exams            = [],
    attendance       = {},
    remark           = "",
    session_name     = "",
  } = report

  const si = student_info
  const subjects = getSubjectMarks11_12WithoutPrac(report)
  const examName = exams[0]?.exam_name || "HALF YEARLY REPORT CARD (SCIENCE STREAM)"

  // Sum calculations
  let totalFull = 0
  let totalObtain = 0
  let hasObtainVal = false

  subjects.forEach(s => {
    totalFull += s.full_mark || 0
    const val = parseFloat(s.obtain_mark)
    if (!isNaN(val)) {
      totalObtain += val
      hasObtainVal = true
    }
  })

  const totalObtainDisplay = hasObtainVal ? totalObtain : "—"
  const overallPct = totalFull && hasObtainVal ? `${Math.round((totalObtain / totalFull) * 100)}%` : "—"
  const attPct = attendance.percentage ? `${Math.round(attendance.percentage)}%` : "—"

  // Border
  pSetDraw(doc, [58, 125, 68]); doc.setLineWidth(1.2)
  doc.rect(M, M, mmW - M * 2, mmH - M * 2)

  // Session & Print Date
  pSetFont(doc, C.black); doc.setFontSize(8); doc.setFont("helvetica", "bold")
  doc.text(`Session : ${session_name || "—"}`, M + 4, M + 6)
  doc.text("SENIOR SECONDARY", mmW / 2, M + 6, { align: "center" })
  doc.text(`Print Date : ${printDate}`, mmW - M - 4, M + 6, { align: "right" })

  // School Header Band
  pSetFill(doc, [125, 10, 10])
  doc.rect(M + 2, M + 9, mmW - M*2 - 4, 13, "F")
  pSetFont(doc, C.white); doc.setFontSize(7.5); doc.setFont("helvetica", "normal")
  doc.text("Established Year 1960", mmW / 2, M + 12.5, { align: "center" })
  doc.setFontSize(14); doc.setFont("helvetica", "bold")
  doc.text(school.name, mmW / 2, M + 19.5, { align: "center" })

  // Info Strip
  if (cbseImg) {
    try {
      doc.addImage(cbseImg, "PNG", M + 4, M + 24, 12, 12)
    } catch (e) {
      console.warn("Failed to add CBSE logo to 11-12:", e)
    }
  }
  if (schoolImg) {
    try {
      doc.addImage(schoolImg, "PNG", mmW - M - 16, M + 24, 12, 12)
    } catch (e) {
      console.warn("Failed to add School logo to 11-12:", e)
    }
  }

  pSetFont(doc, C.black); doc.setFontSize(7.5); doc.setFont("helvetica", "bold")
  doc.text(school.affiliation, mmW / 2, M + 26, { align: "center" })
  doc.setFont("helvetica", "normal"); doc.setFontSize(6.5)
  doc.text(`Address : ${school.address}`, mmW / 2, M + 29.5, { align: "center" })
  doc.text(`Phone : ${school.phone}`, mmW / 2, M + 32.5, { align: "center" })
  doc.text(`Email : ${school.email} | Website : ${school.website}`, mmW / 2, M + 35.5, { align: "center" })

  // Divider
  doc.setLineWidth(0.2); pSetDraw(doc, [200, 200, 200])
  doc.line(M + 2, M + 38.5, mmW - M - 2, M + 38.5)

  // Student Details Box
  pSetFill(doc, [239, 239, 233])
  doc.rect(M + 2, M + 40, mmW - M*2 - 4, 34, "F")

  pSetFill(doc, [125, 10, 10])
  doc.rect(M + 4, M + 42, 22, 4.5, "F")
  pSetFont(doc, C.white); doc.setFontSize(7); doc.setFont("helvetica", "bold")
  doc.text("Student Details", M + 5, M + 45.2)

  let sY = M + 51
  pSetFont(doc, C.black); doc.setFontSize(7.5); doc.setFont("helvetica", "normal")
  
  // Left side info fields
  doc.text("Student's Name :", M + 4, sY)
  doc.text("Father's Name :", M + 4, sY + 5)
  doc.text("Mother's Name :", M + 4, sY + 10)
  doc.text("Address :", M + 4, sY + 15)

  doc.setFont("helvetica", "bold")
  doc.text(fmt(si.st_name), M + 30, sY)
  doc.text(si.father_name ? `MR. ${si.father_name.toUpperCase()}` : "—", M + 30, sY + 5)
  doc.text(si.mother_name ? `MS. ${si.mother_name.toUpperCase()}` : "—", M + 30, sY + 10)
  doc.text(fmt(si.address), M + 30, sY + 15)

  // Right side info fields
  doc.setFont("helvetica", "normal")
  doc.text("Code :", M + 115, sY)
  doc.text("Class :", M + 115, sY + 5)
  doc.text("Section :", M + 115, sY + 10)
  doc.text("Date of Birth :", M + 115, sY + 15)

  doc.setFont("helvetica", "bold")
  doc.text(fmt(si.st_code), M + 138, sY)
  doc.text((si.st_class || "").toUpperCase(), M + 138, sY + 5)
  doc.text((si.st_sec || "").toUpperCase(), M + 138, sY + 10)
  doc.text(fmtD(si.dob), M + 138, sY + 15)

  // Photo
  pSetFill(doc, C.white); pSetDraw(doc, [200, 200, 200])
  doc.rect(mmW - M - 24, M + 42, 20, 25, "FD")
  if (studentImg) {
    try {
      doc.addImage(studentImg, "JPEG", mmW - M - 24, M + 42, 20, 25)
    } catch (e) {
      console.warn("Failed to add student image:", e)
    }
  } else {
    pSetFont(doc, [150, 150, 150]); doc.setFontSize(7)
    doc.text("No Photo", mmW - M - 14, M + 55, { align: "center" })
  }

  // Exam Title
  pSetFont(doc, [0, 51, 102]); doc.setFontSize(13); doc.setFont("helvetica", "bold")
  doc.text(examName.toUpperCase(), mmW / 2, M + 84, { align: "center" })

  // Table
  const head = [["SUBJECTS", "FULL MARKS", "PASS MARKS", "MARKS OBTAINED", "PERCENTAGE"]]
  const body = subjects.map(s => [
    `${s.slno}) ${s.name}  [${s.code}]`,
    s.full_mark,
    s.pass_mark,
    s.obtain_mark,
    s.percentage
  ])

  body.push([
    { content: "TOTAL MARKS", styles: { fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: totalFull, styles: { fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: "—", styles: { fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: totalObtainDisplay, styles: { fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: overallPct, styles: { fontStyle: "bold", fillColor: [30, 59, 139], textColor: [255, 255, 255] } }
  ])

  autoTable(doc, {
    head: head,
    body: body,
    startY: M + 88,
    margin: { left: M + 2, right: M + 2 },
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 2.2,
      lineColor: [80, 80, 80],
      lineWidth: 0.15,
      textColor: C.black,
      valign: "middle"
    },
    headStyles: {
      fillColor: [125, 10, 10],
      textColor: C.white,
      fontStyle: "bold",
      fontSize: 8.5
    },
    columnStyles: {
      0: { halign: "left", fontStyle: "bold" },
      1: { halign: "center", cellWidth: 24 },
      2: { halign: "center", cellWidth: 24 },
      3: { halign: "center", cellWidth: 28, fontStyle: "bold" },
      4: { halign: "center", cellWidth: 28, fontStyle: "bold" }
    }
  })

  // Lower Section
  let y = doc.lastAutoTable ? doc.lastAutoTable.finalY : 200
  if (report.is_retest && report.previous_attempt_marks) {
    y = pPreviousAttemptMarks(doc, report.previous_attempt_marks, y + 2)
  }
  y += 6

  // Box 1: Class Teacher's Remark (Width 125)
  pSetFill(doc, [125, 10, 10])
  doc.rect(M + 2, y, 125, 5, "F")
  pSetFont(doc, C.white); doc.setFontSize(7.5); doc.setFont("helvetica", "bold")
  doc.text("CLASS TEACHER'S REMARK", M + 4, y + 3.8)

  pSetFill(doc, C.white); pSetDraw(doc, [80, 80, 80])
  doc.rect(M + 2, y + 5, 125, 18, "FD")
  pSetFont(doc, C.black); doc.setFontSize(7.5); doc.setFont("helvetica", "italic")
  pSetFont(doc, [80, 80, 80])
  doc.text(remark || "No remarks entered.", M + 5, y + 10)

  // Box 2: Attendance Percentage (Width 61)
  pSetFill(doc, [125, 10, 10])
  doc.rect(M + 131, y, 61, 5, "F")
  pSetFont(doc, C.white); doc.setFontSize(7.5); doc.setFont("helvetica", "bold")
  doc.text("ATTENDANCE PERCENTAGE", M + 161.5, y + 3.8, { align: "center" })

  pSetFill(doc, C.white)
  doc.rect(M + 131, y + 5, 61, 18, "FD")
  pSetFont(doc, [21, 128, 61]); doc.setFontSize(11); doc.setFont("helvetica", "bold")
  doc.text(attPct, M + 161.5, y + 12, { align: "center" })

  pSetFill(doc, [254, 243, 199])
  doc.rect(M + 131, y + 17, 61, 6, "F")
  pSetFont(doc, [125, 10, 10]); doc.setFontSize(6); doc.setFont("helvetica", "bold")
  doc.text(`Min Attendance Required : ${attendance?.min_required || "75%"}`, M + 161.5, y + 21, { align: "center" })

  // Signatures
  let sigY = y + 36
  const xs = [M + 15, mmW / 2 - 20, mmW - M - 45]
  const labels = ["Signature of Parent", "Signature of Class Teacher", "Signature of Principal"]
  labels.forEach((label, i) => {
    pSetDraw(doc, C.black); doc.setLineWidth(0.3)
    doc.line(xs[i], sigY, xs[i] + 40, sigY)
    pSetFont(doc, [60, 60, 60]); doc.setFontSize(7.5); doc.setFont("helvetica", "bold")
    doc.text(label, xs[i] + 20, sigY + 4, { align: "center" })
  })
}

async function pDrawPageRetest(doc, report, school, cbseImg, schoolImg, studentImg) {
  const {
    student_info     = {},
    exams            = [],
    co_scholastic    = [],
    attendance       = {},
    remark           = "",
    session_name     = "",
  } = report

  const si = student_info
  const subjects = getSubjectMarks11_12(report)
  const pct = calculatePercentages11_12(subjects, report)

  // Sum calculations
  const getSumVal = (field) => {
    let sum = 0
    let hasVal = false
    subjects.forEach(s => {
      const val = parseFloat(s[field])
      if (!isNaN(val)) {
        sum += val
        hasVal = true
      }
    })
    return hasVal ? sum : "—"
  }

  const totalFullPract = getSumVal("full_pract")
  const totalFullTheory = getSumVal("full_theory")
  const totalObtainPract = getSumVal("obtain_pract")
  const totalObtainTheory = getSumVal("obtain_theory")
  const totalObtainTotal = getSumVal("obtain_total")

  const attPct = attendance.percentage ? `${Math.round(attendance.percentage)}%` : "—"

  // Border
  pSetDraw(doc, [58, 125, 68]); doc.setLineWidth(1.2)
  doc.rect(M, M, mmW - M * 2, mmH - M * 2)

  // Session & Print Date
  pSetFont(doc, C.black); doc.setFontSize(8); doc.setFont("helvetica", "bold")
  doc.text(`Session : ${session_name || "—"}`, M + 4, M + 6)
  doc.text(`Print Date : ${printDate}`, mmW - M - 4, M + 6, { align: "right" })

  // School Header Band
  pSetFill(doc, [125, 10, 10])
  doc.rect(M + 2, M + 9, mmW - M*2 - 4, 13, "F")
  pSetFont(doc, C.white); doc.setFontSize(7.5); doc.setFont("helvetica", "normal")
  doc.text("Established Year 1960", mmW / 2, M + 12.5, { align: "center" })
  doc.setFontSize(14); doc.setFont("helvetica", "bold")
  doc.text(school.name, mmW / 2, M + 19.5, { align: "center" })

  // Info Strip
  if (cbseImg) {
    try {
      doc.addImage(cbseImg, "PNG", M + 4, M + 24, 12, 12)
    } catch (e) {
      console.warn("Failed to add CBSE logo to Retest:", e)
    }
  }
  if (schoolImg) {
    try {
      doc.addImage(schoolImg, "PNG", mmW - M - 16, M + 24, 12, 12)
    } catch (e) {
      console.warn("Failed to add School logo to Retest:", e)
    }
  }

  pSetFont(doc, C.black); doc.setFontSize(7.5); doc.setFont("helvetica", "bold")
  doc.text(school.affiliation, mmW / 2, M + 26, { align: "center" })
  doc.setFont("helvetica", "normal"); doc.setFontSize(6.5)
  doc.text(`Address : ${school.address}`, mmW / 2, M + 29.5, { align: "center" })
  doc.text(`Phone : ${school.phone}`, mmW / 2, M + 32.5, { align: "center" })
  doc.text(`Email : ${school.email} | Website : ${school.website}`, mmW / 2, M + 35.5, { align: "center" })

  // Divider
  doc.setLineWidth(0.2); pSetDraw(doc, [200, 200, 200])
  doc.line(M + 2, M + 38.5, mmW - M - 2, M + 38.5)

  // Student Details Box
  pSetFill(doc, [239, 239, 233])
  doc.rect(M + 2, M + 40, mmW - M*2 - 4, 34, "F")

  pSetFill(doc, [125, 10, 10])
  doc.rect(M + 4, M + 42, 22, 4.5, "F")
  pSetFont(doc, C.white); doc.setFontSize(7); doc.setFont("helvetica", "bold")
  doc.text("Student Details", M + 5, M + 45.2)

  let sY = M + 51
  pSetFont(doc, C.black); doc.setFontSize(7.5); doc.setFont("helvetica", "normal")
  
  // Left side info fields
  doc.text("Student's Name :", M + 4, sY)
  doc.text("Father's Name :", M + 4, sY + 5)
  doc.text("Mother's Name :", M + 4, sY + 10)
  doc.text("Address :", M + 4, sY + 15)

  doc.setFont("helvetica", "bold")
  doc.text(fmt(si.st_name), M + 30, sY)
  doc.text(si.father_name ? `MR. ${si.father_name.toUpperCase()}` : "—", M + 30, sY + 5)
  doc.text(si.mother_name ? `MS. ${si.mother_name.toUpperCase()}` : "—", M + 30, sY + 10)
  doc.text(fmt(si.address), M + 30, sY + 15)

  // Right side info fields
  doc.setFont("helvetica", "normal")
  doc.text("Code :", M + 115, sY)
  doc.text("Class :", M + 115, sY + 5)
  doc.text("Section :", M + 115, sY + 10)
  doc.text("Date of Birth :", M + 115, sY + 15)

  doc.setFont("helvetica", "bold")
  doc.text(fmt(si.st_code), M + 138, sY)
  doc.text((si.st_class || "").toUpperCase(), M + 138, sY + 5)
  doc.text((si.st_sec || "").toUpperCase(), M + 138, sY + 10)
  doc.text(fmtD(si.dob), M + 138, sY + 15)

  // Photo
  pSetFill(doc, C.white); pSetDraw(doc, [200, 200, 200])
  doc.rect(mmW - M - 24, M + 42, 20, 25, "FD")
  if (studentImg) {
    try {
      doc.addImage(studentImg, "JPEG", mmW - M - 24, M + 42, 20, 25)
    } catch (e) {
      console.warn("Failed to add student image:", e)
    }
  } else {
    pSetFont(doc, [150, 150, 150]); doc.setFontSize(7)
    doc.text("No Photo", mmW - M - 14, M + 55, { align: "center" })
  }

  // Title Band
  let tbY = M + 78
  pSetFill(doc, [223, 199, 148])
  doc.rect(M + 2, tbY, 15, 6, "F")
  pSetFill(doc, [74, 46, 24])
  doc.rect(M + 17, tbY, mmW - M*2 - 34, 6, "F")
  pSetFill(doc, [223, 199, 148])
  doc.rect(mmW - M - 17, tbY, 15, 6, "F")

  pSetFont(doc, C.white); doc.setFontSize(9); doc.setFont("helvetica", "bold")
  doc.text("RETEST EXAMINATION REPORT CARD", mmW / 2, tbY + 4.2, { align: "center" })

  // Part 1 Scholastic Area Table
  pSetFont(doc, C.black); doc.setFontSize(8); doc.setFont("helvetica", "bold")
  doc.text("PART 1 : SCHOLASTIC", M + 2, tbY + 11)

  const head = [
    [
      { content: "SLNO", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
      { content: "SUBJECT NAME", rowSpan: 2, styles: { valign: "middle", halign: "left" } },
      { content: "SUBJECT CODE", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
      { content: "FULL MARK", colSpan: 2, styles: { halign: "center" } },
      { content: "PASS MARK", colSpan: 2, styles: { halign: "center" } },
      { content: "MARK OBTAIN", colSpan: 3, styles: { halign: "center" } }
    ],
    [
      { content: "Practical", styles: { halign: "center", fillColor: [141, 26, 26] } },
      { content: "Theory", styles: { halign: "center", fillColor: [141, 26, 26] } },
      { content: "Practical", styles: { halign: "center", fillColor: [141, 26, 26] } },
      { content: "Theory", styles: { halign: "center", fillColor: [141, 26, 26] } },
      { content: "Practical", styles: { halign: "center", fillColor: [141, 26, 26] } },
      { content: "Theory", styles: { halign: "center", fillColor: [141, 26, 26] } },
      { content: "Total", styles: { halign: "center", fillColor: [141, 26, 26] } }
    ]
  ]

  const body = subjects.map((s, idx) => [
    idx + 1,
    s.name,
    s.code,
    s.full_pract || "—",
    s.full_theory || "—",
    s.pass_pract || "—",
    s.pass_theory || "—",
    s.obtain_pract,
    s.obtain_theory,
    s.obtain_total
  ])

  body.push([
    { content: "TOTAL MARKS", colSpan: 3, styles: { halign: "center", fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: totalFullPract, styles: { fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: totalFullTheory, styles: { fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: "—", styles: { fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: "—", styles: { fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: totalObtainPract, styles: { fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: totalObtainTheory, styles: { fontStyle: "bold", fillColor: [2, 2, 85], textColor: [255, 255, 255] } },
    { content: totalObtainTotal, styles: { fontStyle: "bold", fillColor: [30, 59, 139], textColor: [255, 255, 255] } }
  ])

  autoTable(doc, {
    head: head,
    body: body,
    startY: tbY + 13,
    margin: { left: M + 2, right: M + 2 },
    theme: "grid",
    styles: {
      fontSize: 7,
      cellPadding: 1.5,
      lineColor: [80, 80, 80],
      lineWidth: 0.15,
      textColor: C.black,
      valign: "middle"
    },
    headStyles: {
      fillColor: [125, 10, 10],
      textColor: C.white,
      fontStyle: "bold",
      fontSize: 7
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 10 },
      1: { halign: "left", fontStyle: "bold" },
      2: { halign: "center", cellWidth: 20 },
      3: { halign: "center", cellWidth: 15 },
      4: { halign: "center", cellWidth: 15 },
      5: { halign: "center", cellWidth: 15 },
      6: { halign: "center", cellWidth: 15 },
      7: { halign: "center", cellWidth: 15 },
      8: { halign: "center", cellWidth: 15 },
      9: { halign: "center", cellWidth: 15, fontStyle: "bold" }
    }
  })

  // Lower layout grids
  let y = doc.lastAutoTable ? doc.lastAutoTable.finalY : 170
  y += 2

  // Previous Attempt Marks of Failed Banner
  if (report.previous_attempt_marks) {
    pSetFill(doc, [235, 220, 185])
    pSetDraw(doc, [125, 10, 10])
    doc.setLineWidth(0.3)
    doc.rect(M + 2, y, mmW - M*2 - 4, 8, "FD")
    pSetFont(doc, C.black); doc.setFontSize(8); doc.setFont("helvetica", "bold")
    doc.text(report.previous_attempt_marks, M + 5, y + 5.2)
    y += 11
  } else {
    y += 5
  }

  pSetFont(doc, C.black); doc.setFontSize(8); doc.setFont("helvetica", "bold")
  doc.text("PART 2 : CO-SCHOLASTIC", M + 2, y)
  y += 2

  // Column 1: CO-SCHOLASTIC AREAS (Width 61)
  pSetFill(doc, [125, 10, 10])
  doc.rect(M + 2, y, 61, 5, "F")
  pSetFont(doc, C.white); doc.setFontSize(6.5); doc.setFont("helvetica", "bold")
  doc.text("CO-SCHOLASTIC AREAS", M + 32.5, y + 3.8, { align: "center" })

  pSetFill(doc, C.white); pSetDraw(doc, [80, 80, 80])
  doc.rect(M + 2, y + 5, 61, 35, "FD")
  pSetFont(doc, C.black); doc.setFontSize(6.5); doc.setFont("helvetica", "bold")
  doc.text("Grading Scale", M + 32.5, y + 9.5, { align: "center" })

  const scales = ["A1", "A2", "B1", "B2", "C1", "C2", "D1", "D2", "E"]
  scales.forEach((sc, i) => {
    let sx = M + 3.5 + i * 6.2
    pSetFill(doc, [254, 243, 199])
    doc.rect(sx, y + 11.5, 5.5, 4, "F")
    pSetFont(doc, [125, 10, 10]); doc.setFontSize(5)
    doc.text(sc, sx + 2.75, y + 14.5, { align: "center" })
  })

  pSetFont(doc, C.black); doc.setFontSize(6.5)
  co_scholastic.forEach((c, i) => {
    let cy = y + 21 + i * 5
    doc.setFont("helvetica", "normal")
    doc.text(c.subject_name || "", M + 4, cy)
    doc.setFont("helvetica", "bold")
    pSetFont(doc, [125, 10, 10])
    doc.text(c.grade || "—", M + 59, cy, { align: "right" })
    pSetFont(doc, C.black)
  })

  // Column 2: Subject wise average score (Width 64)
  pSetFill(doc, [125, 10, 10])
  doc.rect(M + 66, y, 64, 5, "F")
  pSetFont(doc, C.white); doc.setFontSize(6)
  doc.text(`Subject wise average score in Class ${fmt(si.st_class).toUpperCase()}, Sec ${fmt(si.st_sec).toUpperCase()}`, M + 98, y + 3.8, { align: "center" })

  pSetFill(doc, C.white); pSetDraw(doc, [80, 80, 80])
  doc.rect(M + 66, y + 5, 64, 35, "FD")
  pSetFont(doc, C.black); doc.setFontSize(6.5); doc.setFont("helvetica", "bold")
  doc.text("SUBJECTS", M + 68, y + 9.5)
  doc.text("Average", M + 128, y + 9.5, { align: "right" })
  doc.line(M + 68, y + 10.5, M + 128, y + 10.5)

  subjects.forEach((s, i) => {
    let sy = y + 14.5 + i * 5
    doc.setFont("helvetica", "normal")
    doc.text(s.name, M + 68, sy)
    doc.setFont("helvetica", "bold")
    doc.text(String(s.class_average ?? "—"), M + 128, sy, { align: "right" })
  })

  // Column 3: Marks & Attendance Percentage (Width 61)
  pSetFill(doc, [125, 10, 10])
  doc.rect(M + 133, y, 61, 5, "F")
  pSetFont(doc, C.white); doc.setFontSize(6.5); doc.setFont("helvetica", "bold")
  doc.text("MARKS PERCENTAGE", M + 163.5, y + 3.8, { align: "center" })

  pSetFill(doc, C.white); pSetDraw(doc, [80, 80, 80])
  doc.rect(M + 133, y + 5, 61, 15, "FD")
  pSetFont(doc, C.black); doc.setFontSize(6.5)
  doc.setFont("helvetica", "normal")
  doc.text("Practical:", M + 136, y + 9.5)
  doc.text("Theory:", M + 136, y + 13.5)
  doc.text("Overall Percentage:", M + 136, y + 17.5)
  doc.setFont("helvetica", "bold")
  doc.text(pct.practical, M + 191, y + 9.5, { align: "right" })
  doc.text(pct.theory, M + 191, y + 13.5, { align: "right" })
  doc.text(pct.overall, M + 191, y + 17.5, { align: "right" })

  // Attendance Box
  let ay = y + 21
  pSetFill(doc, [125, 10, 10])
  doc.rect(M + 133, ay, 61, 5, "F")
  pSetFont(doc, C.white); doc.setFontSize(6.5); doc.setFont("helvetica", "bold")
  doc.text("ATTENDANCE PERCENTAGE", M + 163.5, ay + 3.8, { align: "center" })

  pSetFill(doc, C.white); pSetDraw(doc, [80, 80, 80])
  doc.rect(M + 133, ay + 5, 61, 14, "FD")
  pSetFont(doc, [21, 128, 61]); doc.setFontSize(9.5); doc.setFont("helvetica", "bold")
  doc.text(attPct, M + 163.5, ay + 11, { align: "center" })

  pSetFill(doc, [254, 243, 199])
  doc.rect(M + 133, ay + 13, 61, 5, "F")
  pSetFont(doc, [125, 10, 10]); doc.setFontSize(5.5); doc.setFont("helvetica", "bold")
  doc.text(`Min Attendance Required : ${attendance?.min_required || "75%"}`, M + 163.5, ay + 16.5, { align: "center" })

  // Remarks
  let ry = y + 43
  pSetDraw(doc, [150, 150, 150])
  doc.rect(M + 2, ry, mmW - M*2 - 4, 18, "D")
  pSetFont(doc, C.black); doc.setFontSize(7.5); doc.setFont("helvetica", "bold")
  doc.text("REMARKS", M + 4, ry + 4.5)
  doc.setFont("helvetica", "normal"); doc.setFontSize(7)
  pSetFont(doc, [80, 80, 80])
  doc.text(remark || "No remarks entered.", M + 4, ry + 9.5)

  // Signatures
  let sigY = ry + 28
  const xs = [M + 15, mmW / 2 - 20, mmW - M - 45]
  const labels = ["Signature of Guardian", "Signature of Class Teacher", "Signature of Principal"]
  labels.forEach((label, i) => {
    pSetDraw(doc, C.black); doc.setLineWidth(0.3)
    doc.line(xs[i], sigY, xs[i] + 40, sigY)
    pSetFont(doc, [60, 60, 60]); doc.setFontSize(7.5); doc.setFont("helvetica", "bold")
    doc.text(label, xs[i] + 20, sigY + 4, { align: "center" })
  })
}

